import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCompanyFromARES, validateIco, getIcoStatusCategory } from "../utils/ares";
import { getCompanyStatusNames, getCompanyStatuses } from "../utils/statuses";
import { API_URL } from "../config";
import UpsellManager from "./UpsellManager";
import { MiniCounterButtons } from "./UserStatsWidget";
import WhatToOfferDrawer from "./WhatToOfferDrawer";
import ActivityTemplatesModal from "./ActivityTemplatesModal";

// Detekce iOS PWA – true JEN na iPhonu/iPadu přidaném na plochu
const isIOSPWA = window.navigator.standalone === true;

const defaultCompany = {
  ico: "",
  icoStatus: "",
  name: "",
  address: "",
  personType: "", // Nové pole pro typ osoby
  ca: "", // Nové pole pro číslo zákazníka (max 10 čísel)
  contacts: [{ name: "", phone: "", email: "" }],
  operator: "",
  rsEndDate: "",
  opportunityDate: "", // Příležitost (datum)
  prm: "", // PRM Vodafone (písmena a čísla, max 15 znaků)
  myNotes: "", // Osobní poznámky ke společnosti
  coverageSnapshot: null, // Uložené pokrytí z ČTÚ VPortal
  status: "",
  forecast: "",
  bmsl: "",
  closedDate: "", // Nové pole pro zobchodováno
  contractLength: "", // Nové pole pro délku smlouvy
  doNotConnect: false,
  activities: [],
  createdBy: "",
  isLeadBa: false,
  leadBaTipar: "",
  leadBaComsFee: null,
};

export default function CompanyForm({
  company,
  users,
  onSave,
  onCancel,
  nextCompanyId, // <- pouze přijímáš prop
  isDarkMode,
  companies, // <- přidáváme prop pro výpočet dnešních aktivit
  userRole, // <- přidáváme userRole pro callcentrum
  fetchCompanies, // <- přidáváme fetchCompanies pro aktualizaci seznamu
  userId, // <- přidáváme userId pro fetchCompanies
  onOpenMeetingPlanner, // <- přidáváme callback pro otevření meeting planneru
}) {
  const navigate = useNavigate();

  // VoIP: zjisti, zda má přihlášený uživatel povolené volání (pro tlačítka u kontaktů)
  const [twilioEnabled, setTwilioEnabled] = useState(false);
  const [twilioNumber, setTwilioNumber] = useState('');
  const [voipProvider, setVoipProvider] = useState('twilio');
  useEffect(() => {
    const uid = userId || localStorage.getItem('lastUserId') || '';
    if (!uid) return;
    fetch(`${API_URL}/twilio/user-config?userId=${uid}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        setTwilioEnabled(!!(d && d.enabled));
        setVoipProvider((d && d.provider) || 'twilio');
        if (d && Array.isArray(d.numbers) && d.numbers.length) setTwilioNumber(d.numbers[0]);
      })
      .catch(() => {});
  }, [userId]);

  // Spuštění hovoru na kontakt – panel VoIP dole zpracuje podle poskytovatele (Twilio/Odorik)
  const startVoipCall = (contact) => {
    window.dispatchEvent(new CustomEvent('twilio:call', { detail: {
      number: contact.phone,
      companyId: company?._id,
      companyName: form.name || company?.name,
      contactName: contact.name
    } }));
  };

  // Pomocná funkce pro převod ISO data na formát yyyy-MM-dd
  const formatDateForInput = (isoDate) => {
    if (!isoDate) return "";
    
    // Pokud už je ve formátu YYYY-MM-DD, vrať jak je
    if (typeof isoDate === 'string' && isoDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return isoDate;
    }
    
    // Pokud je pouze rok (YYYY), vrať jak je
    if (typeof isoDate === 'string' && isoDate.match(/^\d{4}$/)) {
      return isoDate;
    }
    
    try {
      const date = new Date(isoDate);
      // Zkontroluj zda je datum validní
      if (isNaN(date.getTime())) {
        // Pokud není validní datum, vrať původní hodnotu
        return isoDate;
      }
      
      // Oprava: Použij lokální datum místo UTC
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      // Pokud nastane chyba, vrať původní hodnotu
      return isoDate || "";
    }
  };

  // Pomocná funkce pro zobrazení data v input poli ve formátu DD.MM.YYYY HH:MM
  const formatActivityDateForDisplay = (dateString, timeString) => {
    if (!dateString) return "";
    try {
      // Pokud je datum ve formátu YYYY-MM-DD
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        const formattedDate = `${day}.${month}.${year}`;
        if (timeString) {
          return `${formattedDate} ${timeString}`;
        }
        return formattedDate;
      }
      // Jinak vrátíme původní string
      return dateString;
    } catch (error) {
      return dateString;
    }
  };

  // Výpočet počtu dnešních aktivit
  const getTodayActivitiesCount = () => {
    if (!companies || !Array.isArray(companies)) return 0;
    
    const today = new Date();
    const todayDateString = today.toDateString();
    
    let count = 0;
    companies.forEach(comp => {
      if (comp.nextActionDate) {
        const actionDate = new Date(comp.nextActionDate);
        if (actionDate.toDateString() === todayDateString) {
          count++;
        }
      }
    });
    
    return count;
  };

  const [form, setForm] = useState({
    ...defaultCompany,
    ...company,
    forecast: company?.forecast || "0", // Výchozí hodnota 0 pro forecast
    createdBy: company?.createdBy?._id || company?.createdBy || "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showForecastInfo, setShowForecastInfo] = useState(false);
  const [activityText, setActivityText] = useState("");
  const [activityVariantId, setActivityVariantId] = useState("");
  const [showVariantIdInput, setShowVariantIdInput] = useState(false);
  const [activityDate, setActivityDate] = useState("");
  const [activityTime, setActivityTime] = useState("17:00");
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [activityDescExpanded, setActivityDescExpanded] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editingActivityText, setEditingActivityText] = useState("");
  const [screenHeight, setScreenHeight] = useState(() => {
    // Bezpečný přístup k window objektu
    return typeof window !== 'undefined' ? window.innerHeight : 1024;
  }); // Přidáváme state pro výšku obrazovky
  
  // Dynamický výpočet počtu aktivit na základě výšky obrazovky - max 7
  const getActivitiesPerPage = () => {
    // Pevně nastaveno na maximálně 7 aktivit
    return 7;
  };

  // Funkce pro validaci datum a času další akce - pouze pro akce s aktivitou
  const isNextActionValid = () => {
    return activityDate && activityTime;
  };

  // Funkce pro základní validaci formuláře (bez aktivity)
  const isFormValid = () => {
    return form.name && form.ico && !isLoading;
  };

  // Funkce pro validaci aktivity (pokud je vyplněna nějaká část aktivity)
  const hasPartialActivity = () => {
    return activityText.trim() || activityDate || activityTime;
  };

  // Funkce pro validaci uložení - umožňuje uložit i s neúplnou aktivitou
  const isFormWithActivityValid = () => {
    // Pro uložení stačí pouze základní validace formuláře
    // Aktivita se uloží pouze pokud je kompletní, jinak se ignoruje
    return isFormValid();
  };
  
  const [isEdit] = useState(true); // Defaultně odemknuté
  const [superuseryLogins, setSuperuseryLogins] = useState(() => window.__superuseryLogins || []);
  const [vfFreeLogins, setVfFreeLogins] = useState(() => window.__superuseryFreeLogins || []);

  // Načti seznam superuserů (s cache 5 minut)
  useEffect(() => {
    const CACHE_MS = 5 * 60 * 1000;
    const cached = window.__superuseryLogins;
    const loadedAt = window.__superuseryLoadedAt || 0;
    if (cached && cached.length > 0 && Date.now() - loadedAt < CACHE_MS) {
      setSuperuseryLogins(cached);
      setVfFreeLogins(window.__superuseryFreeLogins || []);
      return;
    }
    fetch(`${API_URL}/superusery?userId=${localStorage.getItem('lastUserId') || ''}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          window.__superuseryLogins = data.logins || [];
          window.__superuseryFreeLogins = data.freeLogins || [];
          window.__superuseryLoadedAt = Date.now();
          setSuperuseryLogins(data.logins || []);
          setVfFreeLogins(data.freeLogins || []);
        }
      })
      .catch(() => {});
  }, []);
  const [aresLoading, setAresLoading] = useState(false);
  const [aresError, setAresError] = useState("");
  const [icoEditConfirmed, setIcoEditConfirmed] = useState(false); // Sledování potvrzení změny IČO
  const [duplicateWarning, setDuplicateWarning] = useState(null); // Varování o duplicitním IČO
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [statusObjects, setStatusObjects] = useState([]); // Objekty stavů s plnou konfigurací
  const [hiddenStatuses, setHiddenStatuses] = useState(() => {
    // Načtení skrytých stavů z localStorage
    const saved = localStorage.getItem('hiddenStatuses');
    return saved ? JSON.parse(saved) : [];
  });
  const [showStatusConfig, setShowStatusConfig] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [showPreblokovaniModal, setShowPreblokovaniModal] = useState(false);
  const [preblokaceSettings, setPreblokaceSettings] = useState({ recipient2: '', recipient3: '', recipient4: '' });
  const [preblokaceSettingsLoading, setPreblokaceSettingsLoading] = useState(false);
  const [preblokaceSettingsSaving, setPreblokaceSettingsSaving] = useState(false);
  const [preblokaceEmailSending, setPreblokaceEmailSending] = useState(false);
  const [preblokaceSendMessage, setPreblokaceSendMessage] = useState('');
  const [preblokaceSendError, setPreblokaceSendError] = useState('');
  const [editableSubject, setEditableSubject] = useState('');
  const [editableBody, setEditableBody] = useState('');
  const [adminSubjectTemplate, setAdminSubjectTemplate] = useState('');
  const [adminBodyTemplate, setAdminBodyTemplate] = useState('');
  const [emailBodyViewMode, setEmailBodyViewMode] = useState('edit'); // 'edit' | 'preview'
  const [showUvolnitModal, setShowUvolnitModal] = useState(false);
  const [uvolnitEmailSending, setUvolnitEmailSending] = useState(false);
  const [uvolnitSendMessage, setUvolnitSendMessage] = useState('');
  const [uvolnitSendError, setUvolnitSendError] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  // PB test - automatická přeblokace
  const [pbExtensionActive, setPbExtensionActive] = useState(false);
  const [showPbTestModal, setShowPbTestModal] = useState(false);
  const [pbTestStep, setPbTestStep] = useState('idle');
  const [pbTestLog, setPbTestLog] = useState([]);
  const [pbTestError, setPbTestError] = useState('');
  const [pbTestMultiple, setPbTestMultiple] = useState([]);
  const [pbTestIframeUrl, setPbTestIframeUrl] = useState('');
  // Rozhodovací brána po kontrole vlastníka v Siebelu
  const [pbDecisionNeeded, setPbDecisionNeeded] = useState(false); // zobrazit dialog "přeblokace není možná"
  const [pbCheckedOwner, setPbCheckedOwner] = useState(''); // vlastník zjištěný v Siebelu (prm)
  const [pbCloseCountdown, setPbCloseCountdown] = useState(null); // odpočet do automatického zavření modalu
  // Ref na aktuální seznam uživatelů – aby message handler nečetl zastaralou closure
  const usersRef = useRef(users);
  useEffect(() => { usersRef.current = users; }, [users]);

  // Po dokončení přeblokace ("Hotovo") odpočítat 5s a zavřít modal
  useEffect(() => {
    if (!showPbTestModal || pbTestStep !== 'completed') {
      setPbCloseCountdown(null);
      return;
    }
    setPbCloseCountdown(5);
    const interval = setInterval(() => {
      setPbCloseCountdown((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          clearInterval(interval);
          setShowPbTestModal(false);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showPbTestModal, pbTestStep]);
  
  // Ref pro automatické otevření date pickeru a detekci kliknutí mimo dropdown
  const dateInputRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const dateTimePickerRef = useRef(null);
  const editableBodyRef = useRef(null);
  const adminBodyTemplateRef = useRef(null);
  const monthYearPickerRef = useRef(null);
  const textAreaRef = useRef(null);
  const ownerDropdownRef = useRef(null);
  const manualAssigneeDropdownRef = useRef(null);
  const pbTestIframeRef = useRef(null);
  const pbTestPollRef = useRef(null);
  const pbTestStepRef = useRef('idle');
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [manualButtonId, setManualButtonId] = useState('');
  const [manualAssigneeId, setManualAssigneeId] = useState('');
  const [showManualAssigneeDropdown, setShowManualAssigneeDropdown] = useState(false);
  const [manualAssigneeSearch, setManualAssigneeSearch] = useState('');
  const [leadRatingSaving, setLeadRatingSaving] = useState(false);
  const [showLeadRatingPrompt, setShowLeadRatingPrompt] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState(null);
  const [leadRatingHover, setLeadRatingHover] = useState(0);
  
  // Stav pro hamburger menu na mobilu
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Stav pro lead hamburger menu (hodnocení, provolat, reklamovat)
  const [showLeadMenu, setShowLeadMenu] = useState(false);
  const leadMenuRef = useRef(null);
  const [reklamaceSaving, setReklamaceSaving] = useState(false);
  const [existingLeadComplaint, setExistingLeadComplaint] = useState(null);
  const [showReklamaceModal, setShowReklamaceModal] = useState(false);
  const [reklamaceText, setReklamaceText] = useState('');

  // Stav pro "Co nabídnout?" drawer
  const [showOfferDrawer, setShowOfferDrawer] = useState(false);
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerResult, setOfferResult] = useState(null);

  // Provolat seznam
  const [provolatInfo, setProvolatInfo] = useState(null);
  const [provolatExtending, setProvolatExtending] = useState(false);
  const [provolatExtendSelected, setProvolatExtendSelected] = useState(false);

  // Lead BA
  const [leadBaSettings, setLeadBaSettings] = useState({ enabled: false, tipari: [] });
  useEffect(() => {
    const uid = userId || localStorage.getItem('lastUserId') || '';
    if (!uid) return;
    fetch(`${API_URL}/leadba/settings?userId=${uid}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setLeadBaSettings(data); })
      .catch(() => {});
  }, [userId]);

  // Stav pro Signál modal
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [signalLoading, setSignalLoading] = useState(false);
  const [signalResult, setSignalResult] = useState(null);
  const [signalError, setSignalError] = useState(null);
  const [signalAddress, setSignalAddress] = useState('');

  const checkSignal = async (overrideAddress) => {
    const address = overrideAddress != null ? overrideAddress : (signalAddress || form.address || company?.address);
    if (!address) { setSignalError('Firma nemá vyplněné sídlo společnosti.'); setShowSignalModal(true); return; }
    setSignalLoading(true);
    setSignalResult(null);
    setSignalError(null);
    setShowSignalModal(true);
    try {
      // 1. Geocode address → GPS
      const geoRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=cz&addressdetails=1&limit=1&q=${encodeURIComponent(address)}`);
      const geoData = await geoRes.json();
      if (!geoData.length) { setSignalError('Adresa nebyla nalezena. Zkuste ji upřesnit.'); return; }
      const geo = geoData[0];
      const lat = parseFloat(geo.lat);
      const lon = parseFloat(geo.lon);

      // 2. Convert WGS84 → EPSG:3857 (Web Mercator)
      const R = 6378137;
      const mx = lon * Math.PI / 180 * R;
      const my = Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360)) * R;

      // 3. BBOX: query point at center of 256×256 pixel tile (half = ~2.4km)
      const half = 2446;
      const bbox = `${mx - half},${my - half},${mx + half},${my + half}`;

      // 4. Query ČTÚ VPortal WMS – CORS open (access-control-allow-origin: *)
      // Operator mapping: Czech MNC codes (MCCMNC 230xx)
      const OP_CODE = { 'T-Mobile': '01', 'TM': '01', 'O2': '02', 'Vodafone': '03' };
      const currentOp = form.operator || company?.operator || '';
      const currentOpCode = OP_CODE[currentOp] || null;

      const mc10 = 'https://vportal.ctu.gov.cz/geo/mc10/wms';
      const q11  = `SERVICE=WMS&VERSION=1.1.1&REQUEST=GetFeatureInfo&FORMAT=image%2Fpng&STYLES=&TRANSPARENT=TRUE&TILED=false&info_format=application%2Fjson&feature_count=1&X=128&Y=128&WIDTH=256&HEIGHT=256&SRS=EPSG%3A3857&BBOX=${bbox}`;

      const fetches = [
        fetch(`${mc10}?${q11}&QUERY_LAYERS=mc10%3Acp_03_4g_d&LAYERS=mc10%3Acp_03_4g_d`),
        fetch(`${mc10}?${q11}&QUERY_LAYERS=mc10%3Acp_03_5g_d&LAYERS=mc10%3Acp_03_5g_d`),
        currentOpCode && currentOpCode !== '03'
          ? fetch(`${mc10}?${q11}&QUERY_LAYERS=mc10%3Acp_${currentOpCode}_4g_d&LAYERS=mc10%3Acp_${currentOpCode}_4g_d`)
          : Promise.resolve(null),
        currentOpCode && currentOpCode !== '03'
          ? fetch(`${mc10}?${q11}&QUERY_LAYERS=mc10%3Acp_${currentOpCode}_5g_d&LAYERS=mc10%3Acp_${currentOpCode}_5g_d`)
          : Promise.resolve(null),
      ];
      const [lteRes, fiveGRes, curLteRes, curFiveGRes] = await Promise.all(fetches);
      const [lteData, fiveGData, curLteData, curFiveGData] = await Promise.all([
        lteRes.json(), fiveGRes.json(),
        curLteRes ? curLteRes.json() : Promise.resolve(null),
        curFiveGRes ? curFiveGRes.json() : Promise.resolve(null),
      ]);

      // GRAY_INDEX / 100 = outdoor signal in dBm; 0 = no coverage
      const classify = (dbm) => {
        if (dbm >= -65)  return { bars: 5, quality: 'Výborný',    color: 'green' };
        if (dbm >= -75)  return { bars: 4, quality: 'Dobrý',      color: 'green' };
        if (dbm >= -85)  return { bars: 3, quality: 'Střední',    color: 'amber' };
        if (dbm >= -95)  return { bars: 2, quality: 'Slabý',      color: 'orange' };
        if (dbm >= -110) return { bars: 1, quality: 'Velmi slabý',color: 'red' };
        return null;
      };

      // Estimated speeds [dlMin, dlMax, ulMin, ulMax] by outdoor dBm
      const lteSpeed = (dbm) => {
        if (dbm >= -65) return [80, 150, 25, 50];
        if (dbm >= -75) return [50, 80,  15, 25];
        if (dbm >= -85) return [20, 50,  8,  15];
        if (dbm >= -95) return [8,  20,  3,  8];
        return [2, 8, 1, 3];
      };
      const fiveGSpeed = (dbm) => {
        if (dbm >= -65) return [300, 800, 80, 150];
        if (dbm >= -75) return [150, 300, 40, 80];
        if (dbm >= -85) return [50,  150, 15, 40];
        return [20, 50, 5, 15];
      };

      // Active LTE bands by signal strength (Vodafone CZ allocation)
      const lteBands = (dbm) => [
        dbm > -110 && { id: 'B20', freq: '800 MHz',  note: 'Pokrytí budov' },
        dbm > -105 && { id: 'B28', freq: '700 MHz',  note: 'Venkovské oblasti' },
        dbm > -92  && { id: 'B3',  freq: '1800 MHz', note: 'Základ sítě' },
        dbm > -85  && { id: 'B1',  freq: '2100 MHz', note: 'Kapacitní' },
        dbm > -78  && { id: 'B7',  freq: '2600 MHz', note: 'Hustá zástavba' },
      ].filter(Boolean);

      // Active 5G bands (n1=DSS always if 5G present, n78=3500MHz only strong signal)
      const fiveGBands = (dbm) => [
        { id: 'n1',  freq: '2100 MHz', note: '5G NR (DSS)' },
        dbm > -78 && { id: 'n78', freq: '3500 MHz', note: '5G NR (hlavní)' },
      ].filter(Boolean);

      const parseSignal = (data, speedFn, bandsFn) => {
        if (!data?.features?.length) return null;
        const gi = data.features[0]?.properties?.GRAY_INDEX;
        if (!gi || gi === 0) return null;
        const dbmOut = gi / 100;
        const dbmIn = dbmOut - 12; // ~12 dB building penetration loss (3GPP TR 36.814)
        const indoorOk = classify(dbmIn);
        return {
          dbmOut, dbmIn,
          outdoor: classify(dbmOut),
          indoor: indoorOk,
          speeds: speedFn(dbmOut),
          bands: bandsFn(dbmOut),
        };
      };

      setSignalResult({
        address, lat, lon, displayName: geo.display_name,
        currentOp: currentOp || null,
        isCurrentOpVodafone: currentOpCode === '03' || !currentOpCode,
        lte:      parseSignal(lteData,    lteSpeed,   lteBands),
        fiveG:    parseSignal(fiveGData,  fiveGSpeed, fiveGBands),
        curLte:   curLteData  ? parseSignal(curLteData,   lteSpeed,   lteBands)  : null,
        curFiveG: curFiveGData ? parseSignal(curFiveGData, fiveGSpeed, fiveGBands) : null,
      });
    } catch {
      setSignalError('Tato funkce je dostupná pouze mimo Citrix.');
    } finally {
      setSignalLoading(false);
    }
  };

  const handleOpenOffer = async () => {
    if (!company?.ico || offerLoading) return;
    setOfferLoading(true);
    setOfferResult(null);
    try {
      const res = await fetch(`${API_URL}/ai-offer-suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ico: company.ico, companyName: company.name })
      });
      const data = await res.json();
      if (res.ok) {
        setOfferResult({ suggestion: data.suggestion || '', activities: data.activities || [] });
      } else {
        setOfferResult({ error: data.error || 'Chyba při načítání.' });
      }
    } catch {
      setOfferResult({ error: 'Nepodařilo se připojit k serveru.' });
    } finally {
      setOfferLoading(false);
      setShowOfferDrawer(true);
    }
  };

  // Provolat seznam — zjistit zda je firma v seznamu
  useEffect(() => {
    setProvolatExtendSelected(false);
    if (!company?.ico) { setProvolatInfo(null); return; }
    const uid = localStorage.getItem('lastUserId') || '';
    fetch(`${API_URL}/callcenter/provolat-lists/by-ico/${encodeURIComponent(company.ico)}?userId=${uid}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        setProvolatInfo(data || null);
        setProvolatExtendSelected(!!data?.extended);
      })
      .catch(() => setProvolatInfo(null));
  }, [company?.ico]);

  // Zjistit, zda pro firmu existuje otevřená reklamace leadu
  useEffect(() => {
    if (!company?._id) { setExistingLeadComplaint(null); return; }
    const uid = localStorage.getItem('lastUserId') || '';
    fetch(`${API_URL}/lead-complaints?companyId=${company._id}&userId=${uid}&isAdmin=true`)
      .then(r => r.ok ? r.json() : { complaints: [] })
      .then(data => {
        const list = Array.isArray(data.complaints) ? data.complaints : [];
        const open = list.find(c => c.status === 'open' || c.status === 'in-progress') || list[0] || null;
        setExistingLeadComplaint(open);
      })
      .catch(() => setExistingLeadComplaint(null));
  }, [company?._id]);

  // Zavření lead menu při kliknutí mimo
  useEffect(() => {
    if (!showLeadMenu) return;
    const handler = (e) => {
      if (leadMenuRef.current && !leadMenuRef.current.contains(e.target)) {
        setShowLeadMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showLeadMenu]);

  const handleToggleExtendProvolat = async () => {
    if (!provolatInfo?._id || !company?.ico || provolatExtending) return;
    const uid = localStorage.getItem('lastUserId') || '';
    const nextSelected = !provolatExtendSelected;
    // Optimistická aktualizace UI
    setProvolatExtendSelected(nextSelected);
    setProvolatExtending(true);
    try {
      if (nextSelected) {
        const res = await fetch(`${API_URL}/callcenter/provolat-lists/${provolatInfo._id}/extend?userId=${uid}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ico: company.ico })
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Chyba při ukládání prodloužení.');
        }
        setProvolatInfo(prev => prev ? { ...prev, extended: true } : prev);
      } else {
        const res = await fetch(`${API_URL}/callcenter/provolat-lists/${provolatInfo._id}/extensions/${encodeURIComponent(company.ico)}?userId=${uid}`, {
          method: 'DELETE'
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Chyba při rušení prodloužení.');
        }
        setProvolatInfo(prev => prev ? { ...prev, extended: false } : prev);
      }
    } catch (err) {
      // Rollback při chybě
      setProvolatExtendSelected(!nextSelected);
      alert(err.message || 'Chyba při připojení k serveru.');
    } finally {
      setProvolatExtending(false);
    }
  };
  
  // Stav pro šablony aktivit
  const [activityTemplates, setActivityTemplates] = useState([]);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [previousActivityText, setPreviousActivityText] = useState(null); // undo po aplikaci šablony

  // Osobní dny volna přihlášeného uživatele pro zvýraznění v kalendáři plánování další akce
  const [myDayOffs, setMyDayOffs] = useState(() => new Set());

  useEffect(() => {
    fetch(`${API_URL}/activity-templates?userId=${userId || ''}`) 
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setActivityTemplates(data); })
      .catch(() => {});
  }, [userId]);

  useEffect(() => {
    const uid = userId || localStorage.getItem('lastUserId') || '';
    if (!uid) return;
    fetch(`${API_URL}/calendar/dayoffs?userId=${uid}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && Array.isArray(data.dayOffs)) {
          setMyDayOffs(new Set(data.dayOffs.filter(o => o.userId && String(o.userId) === String(uid)).map(o => o.date)));
        }
      })
      .catch(() => {});
  }, [userId]);

  const applyTemplate = (t) => {
    if (t.description) {
      setPreviousActivityText(activityText);
      setActivityText(t.description);
    }
    if (t.status) handleChange('status', t.status);
    if (t.daysFromNow != null) {
      const d = new Date();
      d.setDate(d.getDate() + Number(t.daysFromNow));
      const dow = d.getDay();
      if (dow === 6) d.setDate(d.getDate() + 2); // sobota → pondělí
      else if (dow === 0) d.setDate(d.getDate() + 1); // neděle → pondělí
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      setActivityDate(`${yyyy}-${mm}-${dd}`);
      setActivityTime(t.timeOfDay || '17:00');
    }
    setShowTemplatePicker(false);
  };

  const undoTemplate = () => {
    if (previousActivityText !== null) {
      setActivityText(previousActivityText);
      setPreviousActivityText(null);
    }
  };

  // State pro custom datetime picker
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);
  const [busySlotModal, setBusySlotModal] = useState(null); // { timeSlot, companies }

  // Mapa obsazených časových slotů pro vybraný den (z dalších firem)
  const busySlotsMap = useMemo(() => {
    if (!activityDate || !companies?.length) return {};
    const map = {};
    companies.forEach(c => {
      if (!c.nextActionDate) return;
      if (c._id === company?._id) return; // ignoruj aktuální firmu
      const d = new Date(c.nextActionDate);
      if (isNaN(d.getTime())) return;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      if (`${year}-${month}-${day}` !== activityDate) return;
      const hour = d.getHours();
      const min = Math.floor(d.getMinutes() / 15) * 15;
      const slotKey = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      if (!map[slotKey]) map[slotKey] = [];
      map[slotKey].push(c);
    });
    return map;
  }, [activityDate, companies, company?._id]);

  // State pro propojování příbuzných firem
  const [showRelatedModal, setShowRelatedModal] = useState(false);
  const [relatedSearch, setRelatedSearch] = useState("");
  const [relatedCompanies, setRelatedCompanies] = useState([]);
  const [searchResults, setSearchResults] = useState([]);

  // State pro call centrum konfiguraci
  const [callCenterConfig, setCallCenterConfig] = useState(null);
  const [loadingCallCenterConfig, setLoadingCallCenterConfig] = useState(false);
  const activeCallCenterButtons = useMemo(() => {
    return (callCenterConfig?.buttons || [])
      .filter(btn => btn.isActive)
      .sort((a, b) => a.order - b.order);
  }, [callCenterConfig]);

  // Zda je hodnocení leadů povoleno pro aktuální šarži firmy
  const leadRatingAllowed = useMemo(() => {
    const batchName = form?.batchName || company?.batchName || '';
    if (!batchName) return false;
    return (callCenterConfig?.buttons || []).some(
      btn => btn.batchName === batchName && btn.leadRatingEnabled
    );
  }, [callCenterConfig, form?.batchName, company?.batchName]);

  // Zda firma pochází z leadu – buď ručně odeslaného ("Lead odeslán"),
  // nebo automaticky přiděleného ("Automaticky přidělen lead ze šarže ...").
  const isFromLead = useMemo(() => {
    const activities = company?.activities || form?.activities || [];
    return activities.some(a => {
      const text = a?.text || '';
      return text.includes('Lead odeslán') || text.includes('Automaticky přidělen lead');
    });
  }, [company?.activities, form?.activities]);

  // Efekt pro detekci kliknutí mimo dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      // Zavření status dropdown při kliknutí mimo
      if (showStatusDropdown && statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
      
      // Zavření datetime pickeru při kliknutí mimo, ignorujeme kliknutí na tlačítko pro otevření
      if (showDateTimePicker && 
          dateTimePickerRef.current && 
          !dateTimePickerRef.current.contains(event.target) && 
          !event.target.closest('.datetime-trigger-button')) {
        setShowDateTimePicker(false);
        setIsEditingDate(false);
      }
      
      // Zavření month/year pickeru při kliknutí mimo
      if (showMonthYearPicker && 
          monthYearPickerRef.current && 
          !monthYearPickerRef.current.contains(event.target)) {
        setShowMonthYearPicker(false);
      }

      // Zavření owner dropdown při kliknutí mimo
      if (showOwnerDropdown && ownerDropdownRef.current && !ownerDropdownRef.current.contains(event.target)) {
        setShowOwnerDropdown(false);
        setOwnerSearch('');
      }

      // Zavření manuálního dropdownu pro obchodníka při kliknutí mimo
      if (showManualAssigneeDropdown && manualAssigneeDropdownRef.current && !manualAssigneeDropdownRef.current.contains(event.target)) {
        setShowManualAssigneeDropdown(false);
        setManualAssigneeSearch('');
      }
    }

    // Přidání event listeneru
    document.addEventListener("mousedown", handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showStatusDropdown, showDateTimePicker, showOwnerDropdown, showManualAssigneeDropdown]);

  // Helper: seskupení uživatelů podle role s vyhledáváním
  const OWNER_ROLE_CONFIG = [
    { role: 'admin', label: '👑 Administrátoři' },
    { role: 'database', label: '🗄️ Databáze' },
    { role: 'contacts', label: '📋 Kontakty' },
  ];
  const groupOwnersByRole = (userList, searchText = '') => {
    const knownRoles = OWNER_ROLE_CONFIG.map(g => g.role);
    const filtered = searchText
      ? userList.filter(u =>
          (u.username || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (u.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (u.email || '').toLowerCase().includes(searchText.toLowerCase())
        )
      : userList;
    const groups = {};
    filtered.forEach(u => {
      const key = knownRoles.includes(u.role) ? u.role : 'sales';
      if (!groups[key]) groups[key] = [];
      groups[key].push(u);
    });
    const result = OWNER_ROLE_CONFIG
      .filter(g => groups[g.role]?.length > 0)
      .map(g => ({ role: g.role, label: g.label, users: groups[g.role] }));
    if (groups.sales?.length > 0) {
      result.push({ role: 'sales', label: '💼 Obchodníci', users: groups.sales });
    }
    return result;
  };

  // Načti stavy při načtení komponenty
  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const statusObjs = await getCompanyStatuses();
        const statusNames = statusObjs.map(s => s.name);
        setAvailableStatuses(statusNames);
        setStatusObjects(statusObjs); // Uložení plných objektů stavů
      } catch (error) {
        console.error('Chyba při načítání stavů:', error);
        // Fallback na základní stavy
        setAvailableStatuses([
          "Nová",
          "Volat později", 
          "Nedovoláno",
          "Kontaktován",
          "Zájem",
          "Čekám na podklady",
          "Vytvořit nabídku",
          "Návrh zaslán",
          "Zákazník se rozhoduje",
          "V jednání",
          "Schůzka domluvena",
          "Vytvořit smlouvu",
          "Implementace",
          "Hotovo",
          "Nemá zájem",
          "Nedaří se spojit",
          "Odloženo",
          "Neplatný telefon",
          "Už je u Vodafone",
          "lead",
          "callcentrum"
        ]);
      }
    };
    
    loadStatuses();
    
    // Načti call centrum konfiguraci vždy – call centrum ji potřebuje pro tlačítka,
    // ostatní uživatelé pro zobrazení hodnocení leadů (leadRatingEnabled) na kartě firmy.
    const loadCallCenterConfig = async () => {
      try {
        setLoadingCallCenterConfig(true);
        const uid = userId || localStorage.getItem('lastUserId') || '';
        const response = await fetch(`${API_URL}/callcenter/config?userId=${uid}`);
        const data = await response.json();
        setCallCenterConfig(data);
      } catch (error) {
        console.error('Chyba při načítání konfigurace call centra:', error);
      } finally {
        setLoadingCallCenterConfig(false);
      }
    };
    
    loadCallCenterConfig();
  }, [userRole]);

  useEffect(() => {
    if (activeCallCenterButtons.length === 0) {
      if (manualButtonId) setManualButtonId('');
      return;
    }
    if (!manualButtonId || !activeCallCenterButtons.some(btn => String(btn._id) === String(manualButtonId))) {
      setManualButtonId(String(activeCallCenterButtons[0]._id));
    }
  }, [activeCallCenterButtons, manualButtonId]);

  // Sledování změn velikosti okna pro dynamické přizpůsobení
  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setScreenHeight(window.innerHeight);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Zavření dropdown při kliknutí mimo něj
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.status-dropdown-container')) {
        setShowStatusDropdown(false);
      }
      if (!event.target.closest('.status-config-container')) {
        setShowStatusConfig(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Zavření dropdown při kliknutí mimo něj
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStatusConfig && !event.target.closest('.status-config-container')) {
        setShowStatusConfig(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusConfig]);

  useEffect(() => {
    if (company) {
      // Normalizuj forecast hodnotu
      let normalizedForecast = "";
      if (company.forecast !== undefined && company.forecast !== null && company.forecast !== "") {
        const forecastNum = parseInt(company.forecast);
        if (!isNaN(forecastNum)) {
          normalizedForecast = String(forecastNum);
        }
      }
      
      // Formátuj telefony v kontaktech
      const formattedContacts = (company.contacts || []).map(contact => ({
        ...contact,
        phone: contact.phone ? formatPhone(contact.phone) : contact.phone
      }));

      // Automaticky rozpoznej typ osoby pokud není nastaven
      const autoPersonType = company.personType || detectPersonType(company.name);

      // Migrace starých hodnot operátora
      let normalizedOperator = company.operator;
      if (company.operator === 'TM') {
        normalizedOperator = 'T-Mobile';
      }

      setForm({
        ...defaultCompany,
        ...company,
        forecast: normalizedForecast,
        operator: normalizedOperator,
        createdBy: company?.createdBy?._id || company?.createdBy || "",
        contacts: formattedContacts,
        personType: autoPersonType,
        // Pro callcentrum uživatele nastav výchozí stav na "lead"
        status: userRole === "callcentrum" && !company.status ? "lead" : company.status
      });
    } else {
      // Pro novou firmu použij čistý defaultCompany
      setForm({
        ...defaultCompany,
        contacts: [{ name: "", phone: "", email: "" }], // Ujisti se, že má pouze jeden prázdný kontakt
        // Pro callcentrum uživatele nastav výchozí stav na "lead"
        status: userRole === "callcentrum" ? "lead" : ""
      });
    }
    setActivityText("");
    setActivityVariantId("");
    setShowVariantIdInput(false);
    setActivityDescExpanded(false);
    
    // Pro callcentrum uživatele nastav datum aktivity na dnešní den
    if (userRole === "callcentrum") {
      const today = new Date();
      // Oprava: Použij lokální formát data
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayString = `${year}-${month}-${day}`;
      setActivityDate(todayString);
    } else {
      setActivityDate("");
    }
    
    setActivityTime("17:00"); // ← zde výchozí čas
    
    // Reset potvrzení změny IČO při načtení nové/jiné firmy
    setIcoEditConfirmed(false);
  }, [company, userRole]);

  // Formátování telefonu
  const formatPhone = (val) => {
    let num = val.replace(/\D/g, "");
    if (num.length === 9 && !num.startsWith("420")) num = "420" + num;
    if (num.length >= 12)
      return `+${num.slice(0, 3)} ${num.slice(3, 6)} ${num.slice(6, 9)} ${num.slice(9, 12)}`;
    if (num.length >= 9)
      return `+${num.slice(0, 3)} ${num.slice(3, 6)} ${num.slice(6, 9)}`;
    return val;
  };

  // Funkce pro generování vlastního kalendáře
  const generateCalendar = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay() + 1); // Začni od pondělí
    
    const days = [];
    const today = new Date();
    
    for (let i = 0; i < 42; i++) { // 6 týdnů x 7 dní
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = currentDate.getMonth() === month;
      const isToday = currentDate.toDateString() === today.toDateString();
      
      // Oprava: Použij lokální formát data místo UTC
      const dateYear = currentDate.getFullYear();
      const dateMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
      const dateDay = String(currentDate.getDate()).padStart(2, '0');
      const dateString = `${dateYear}-${dateMonth}-${dateDay}`;
      
      const isSelected = activityDate === dateString;
      
      days.push({
        date: currentDate,
        day: currentDate.getDate(),
        isCurrentMonth,
        isToday,
        isSelected,
        isMyDayOff: myDayOffs.has(dateString),
        dateString: dateString
      });
    }
    
    return days;
  };

  // State pro kalendář navigaci
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  // Reset kalendáře na současný měsíc při změně firmy
  useEffect(() => {
    const now = new Date();
    setCurrentMonth(now.getMonth());
    setCurrentYear(now.getFullYear());
  }, [company?.id || company?._id]); // Resetuje při změně ID firmy

  // Funkce pro navigaci v kalendáři
  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  // Názvy měsíců
  const monthNames = [
    'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
    'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'
  ];

  // Názvy dní
  const dayNames = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

  // Pomocná funkce pro rozpoznání typu osoby podle názvu
  const detectPersonType = (companyName) => {
    if (!companyName || typeof companyName !== 'string') return '';
    
    const trimmed = companyName.trim();
    // Pokud název končí tečkou, je to právnická osoba
    if (trimmed.endsWith('.')) {
      return 'Právnická osoba';
    } else {
      return 'Fyzická osoba';
    }
  };

  // Funkce pro správu skrytých stavů
  const toggleStatusVisibility = (status) => {
    const newHiddenStatuses = hiddenStatuses.includes(status)
      ? hiddenStatuses.filter(s => s !== status)
      : [...hiddenStatuses, status];
    
    setHiddenStatuses(newHiddenStatuses);
    localStorage.setItem('hiddenStatuses', JSON.stringify(newHiddenStatuses));
  };

  const getVisibleStatuses = () => {
    // Všichni uživatelé (včetně callcentra) mají přístup ke všem stavům
    return availableStatuses.filter(status => !hiddenStatuses.includes(status));
  };

  // Filtrované stavy podle search query
  const getFilteredStatuses = () => {
    const visibleStatuses = getVisibleStatuses();
    if (!statusFilter.trim()) {
      return visibleStatuses;
    }
    
    return visibleStatuses.filter(status => 
      status.toLowerCase().includes(statusFilter.toLowerCase())
    );
  };

  // Funkce pro generování 15minutových časových slotů
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  // Kontrola duplicitního IČO
  const checkForDuplicateIco = async (ico) => {
    // Vymaž předchozí varování
    setDuplicateWarning(null);
    
    // Pouze pokud je IČO vyplněno a má správnou délku
    if (!ico || ico.trim().length !== 8) {
      return;
    }

    try {
      // Kontroluj IČO pouze v rámci stejného tenanta
      const response = await fetch(`${API_URL}/check-duplicates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ icos: [ico.trim()], userId: userId || '' })
      });

      if (!response.ok) {
        console.error('Chyba při kontrole duplicit');
        return;
      }

      const data = await response.json();
      
      // Pokud našel duplicitu a není to tato firma (při editaci)
      if (data.duplicates && data.duplicates.length > 0) {
        const duplicate = data.duplicates[0];
        
        // Pokud editujeme existující firmu, zkontroluj že to není ona sama
        if (company?._id && duplicate.existingCompany.id === company._id) {
          return; // Je to tatáž firma, není to duplicita
        }

        // Načti detail firmy včetně vlastníka
        const detailResponse = await fetch(`${API_URL}/companies/${duplicate.existingCompany.id}`);
        if (detailResponse.ok) {
          const detailData = await detailResponse.json();
          
          let ownerName = 'Neznámý';
          if (detailData.createdBy?.name) {
            ownerName = detailData.createdBy.name;
          }
          
          setDuplicateWarning({
            ico: ico.trim(),
            company: duplicate.existingCompany,
            ownerName: ownerName,
            count: 1
          });
        }
      }
    } catch (error) {
      console.error('Chyba při kontrole duplicitního IČO:', error);
    }
  };

  // Změna hodnoty pole
  const handleChange = (field, value) => {
    // Speciální zpracování pro IČO - potvrzení změny pokud už je vyplněno (pouze při editaci existující firmy)
    if (field === 'ico' && company?._id && form.ico && form.ico.trim() && !icoEditConfirmed) {
      const confirmChange = window.confirm(
        `IČO je již vyplněno (${form.ico}). Opravdu chcete IČO změnit?`
      );
      
      if (!confirmChange) {
        return; // Nepokračuj ve změně
      }
      
      // Uživatel potvrdil, může teď editovat normálně
      setIcoEditConfirmed(true);
    }
    
    // Speciální zpracování pro stav - kontrola Forecast
    if (field === 'status' && value) {
      const statusObj = statusObjects.find(s => s.name === value);
      
      // Automatické vyplnění popisu aktivity pro stav "Nedovoláno"
      if (value === 'Nedovoláno') {
        setActivityText('Nedovoláno');
      }
      
      if (statusObj) {
        // Kontrola autoSetForecastToZero - automaticky nastavit Forecast na 0
        if (statusObj.autoSetForecastToZero) {
          setForm(prev => ({ ...prev, status: value, forecast: '0' }));
          
          // Automatické otevření datetime pickeru
          setTimeout(() => {
            setShowDateTimePicker(true);
          }, 100);
          
          return; // Ukončíme funkci zde
        }
        
        // Kontrola requireForecast - vyžadovat vyplnění Forecast
        if (statusObj.requireForecast) {
          const currentForecast = form.forecast;
          
          // Pokud je nastavena konkrétní požadovaná hodnota
          if (statusObj.requiredForecastValue && statusObj.requiredForecastValue !== '') {
            if (currentForecast !== statusObj.requiredForecastValue) {
              alert(`Pro stav "${value}" je nutné mít Forecast nastavený na ${statusObj.requiredForecastValue}%. Aktuální hodnota: ${currentForecast}%`);
              return; // Zablokuj změnu stavu
            }
          } else {
            // Jen kontrola že je Forecast vyplněný
            if (!currentForecast || currentForecast === '') {
              alert(`Pro stav "${value}" je nutné mít vyplněný Forecast.`);
              return; // Zablokuj změnu stavu
            }
          }
        }
      }
    }
    
    // Speciální zpracování pro datum pole - zachovej hodnotu jak je
    let processedValue = value;
    if (field === 'rsEndDate' || field === 'closedDate' || field === 'opportunityDate') {
      // Pro datum pole zachovej hodnotu jak je - ať už je to YYYY-MM-DD nebo jen YYYY
      processedValue = value;
    }
    
    let updatedForm = { ...form, [field]: processedValue };
    
    // Automatické rozpoznání typu osoby při změně názvu firmy
    if (field === 'name') {
      updatedForm.personType = detectPersonType(value);
    }
    
    setForm(updatedForm);
    
    // Vymazat ARES chybu při změně IČO a zkontrolovat duplicity
    if (field === 'ico') {
      setAresError("");
      // Kontroluj duplicity pouze pokud je zadáno přesně 8 číslic
      if (value && value.trim().length === 8) {
        checkForDuplicateIco(value);
      } else {
        setDuplicateWarning(null);
      }
    }
    
    // Automatické otevření datetime pickeru po výběru stavu/aktivity
    if (field === 'status' && value && value !== '') {
      // Použijeme setTimeout aby se dropdown nejdříve zavřel a pak se otevřel datetime picker
      setTimeout(() => {
        setShowDateTimePicker(true);
      }, 100);
    }
  };

  // Doplní nuly zleva při opuštění pole IČO (pokud je zadáno méně než 8 číslic)
  const handleIcoBlur = () => {
    const value = form.ico;
    if (value && /^\d+$/.test(value) && value.length < 8) {
      const paddedIco = value.padStart(8, '0');
      setForm(prev => ({ ...prev, ico: paddedIco }));
      checkForDuplicateIco(paddedIco);
    }
  };

  // Handler pro změnu textu aktivity s automatickým otevřením kalendáře
  const handleActivityTextChange = (e) => {
    setActivityText(e.target.value);
    // Otevři kalendář pokud se začne psát aktivita a kalendář ještě není otevřený
    if (e.target.value.trim() && !showDateTimePicker && !activityDate) {
      setTimeout(() => {
        setShowDateTimePicker(true);
      }, 300); // Kratší delay aby se otevřel až když uživatel něco napíše
    }
  };

  // Funkce pro odeslání popisu aktivity do AI chatu
  const handleAIAssistance = () => {
    if (!activityText.trim()) {
      alert('Nejdříve napište popis aktivity');
      return;
    }

    // Vytvoř zprávu s kontextem firmy a aktivity
    const companyContext = `Firma: ${form.name || 'Nová firma'}${form.ico ? ` (IČO: ${form.ico})` : ''}

Popis aktivity: ${activityText}

Prosím o doporučení vhodného řešení od Vodafone`;

    // Vyšli událost pro otevření AI chatu s automaticky vyplněnou zprávou
    const chatEvent = new CustomEvent('openAIChat', {
      detail: {
        message: companyContext,
        autoSend: true
      }
    });
    
    window.dispatchEvent(chatEvent);
  };

  // Funkce pro kopírování do schránky
  const handleCopyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Nepodařilo se zkopírovat:', err);
    }
  };

  const currentUser = users.find(u => u._id === userId);
  const currentUserName = currentUser?.name || localStorage.getItem('name') || '';
  const currentUserEmail = currentUser?.email || '';
  const isAdminUser = userRole === 'admin';

  const PREBLOKACE_DEFAULT_SUBJECT_TEMPLATE = 'Žádost o uvolnění klienta / {{companyName}} / IČO: {{ico}}';
  const PREBLOKACE_DEFAULT_BODY_TEMPLATE = 'Ahoj,\n\nbylo by prosím možné uvolnit tohoto klienta?\nMáme zde historickou vazbu.\n\nCA: {{ca}}\nPůvodní vlastník: {{prm}}\n\nDěkuji.\n\nS pozdravem\n{{senderName}}';

  const applyPreblokaceTemplate = (template, values) =>
    (template || '')
      .replace(/\{\{companyName\}\}/g, values.companyName || '')
      .replace(/\{\{ico\}\}/g, values.ico || '')
      .replace(/\{\{ca\}\}/g, values.ca || '')
      .replace(/\{\{prm\}\}/g, values.prm || '')
      .replace(/\{\{senderName\}\}/g, values.senderName || '');

  const markdownToHtml = (text) => {
    if (!text) return '';
    return String(text)
      .replace(/\*\*(.*?)\*\*/gs, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/gs, '<em>$1</em>')
      .replace(/__(.*?)__/gs, '<u>$1</u>')
      .replace(/==(.*?)==/gs, '<mark style="background:#ffd700;color:#000;">$1</mark>')
      .replace(/\n/g, '<br>');
  };

  const wrapSelection = (marker, ref, setState) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const val = el.value;
    const newVal = val.substring(0, start) + marker + val.substring(start, end) + marker + val.substring(end);
    setState(newVal);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + marker.length, end + marker.length);
    });
  };

  const loadPreblokaceSettings = useCallback(async () => {
    const uid = userId || localStorage.getItem('lastUserId') || '';
    if (!uid) return null;

    try {
      setPreblokaceSettingsLoading(true);
      const res = await fetch(`${API_URL}/preblokace/settings?userId=${uid}`);
      if (!res.ok) throw new Error('Nepodařilo se načíst nastavení příjemců.');
      const data = await res.json();
      setPreblokaceSettings({
        recipient2: data.recipient2 || '',
        recipient3: data.recipient3 || '',
        recipient4: data.recipient4 || ''
      });
      return data;
    } catch (err) {
      console.error('Chyba při načítání preblokace nastavení:', err);
      return null;
    } finally {
      setPreblokaceSettingsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!showPreblokovaniModal) return;
    setPreblokaceSendMessage('');
    setPreblokaceSendError('');

    const tplValues = {
      companyName: form.name || '',
      ico: String(form.ico || '').padStart(8, '0'),
      ca: form.ca || '',
      prm: form.prm || '',
      senderName: currentUserName
    };

    loadPreblokaceSettings().then(data => {
      const subjTpl = (data && data.subjectTemplate) || PREBLOKACE_DEFAULT_SUBJECT_TEMPLATE;
      const bodyTpl = (data && data.bodyTemplate) || PREBLOKACE_DEFAULT_BODY_TEMPLATE;
      setAdminSubjectTemplate(subjTpl);
      setAdminBodyTemplate(bodyTpl);
      setEditableSubject(applyPreblokaceTemplate(subjTpl, tplValues));
      setEditableBody(applyPreblokaceTemplate(bodyTpl, tplValues));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreblokovaniModal]);

  const handleSavePreblokaceSettings = async () => {
    const uid = userId || localStorage.getItem('lastUserId') || '';
    if (!uid) {
      alert('Chybí userId.');
      return;
    }

    try {
      setPreblokaceSettingsSaving(true);
      const res = await fetch(`${API_URL}/preblokace/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: uid,
          recipient2: preblokaceSettings.recipient2,
          recipient3: preblokaceSettings.recipient3,
          recipient4: preblokaceSettings.recipient4,
          subjectTemplate: adminSubjectTemplate,
          bodyTemplate: adminBodyTemplate
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Nepodařilo se uložit příjemce.');

      setPreblokaceSettings({
        recipient2: data.recipient2 || '',
        recipient3: data.recipient3 || '',
        recipient4: data.recipient4 || ''
      });
      if (data.subjectTemplate) setAdminSubjectTemplate(data.subjectTemplate);
      if (data.bodyTemplate) setAdminBodyTemplate(data.bodyTemplate);
      alert('Nastavení přeblokace bylo uloženo.');
    } catch (err) {
      console.error('Chyba při ukládání příjemců přeblokace:', err);
      alert(err.message || 'Nepodařilo se uložit nastavení přeblokace.');
    } finally {
      setPreblokaceSettingsSaving(false);
    }
  };

  useEffect(() => {
    if (!showUvolnitModal) return;
    setUvolnitSendMessage('');
    setUvolnitSendError('');
    const tplValues = {
      companyName: form.name || '',
      ico: String(form.ico || '').padStart(8, '0'),
      ca: form.ca || '',
      prm: form.prm || '',
      senderName: currentUserName
    };
    loadPreblokaceSettings().then(data => {
      const subjTpl = (data && data.subjectTemplate) || PREBLOKACE_DEFAULT_SUBJECT_TEMPLATE;
      const bodyTpl = (data && data.bodyTemplate) || PREBLOKACE_DEFAULT_BODY_TEMPLATE;
      setAdminSubjectTemplate(subjTpl);
      setAdminBodyTemplate(bodyTpl);
      setEditableSubject(applyPreblokaceTemplate(subjTpl, tplValues));
      setEditableBody(applyPreblokaceTemplate(bodyTpl, tplValues));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUvolnitModal]);

  const handleSendUvolnitEmail = async () => {
    const uid = userId || localStorage.getItem('lastUserId') || '';
    if (!uid) { setUvolnitSendError('Chybí userId přihlášeného uživatele.'); return; }
    if (!currentUserEmail) { setUvolnitSendError('Přihlášený uživatel nemá vyplněný email.'); return; }

    try {
      setUvolnitEmailSending(true);
      setUvolnitSendMessage('');
      setUvolnitSendError('');

      const res = await fetch(`${API_URL}/preblokace/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: uid,
          companyName: form.name,
          ico: form.ico?.padStart(8, '0'),
          ca: form.ca,
          originalOwner: form.prm,
          customSubject: editableSubject,
          customBody: editableBody,
          customHtmlBody: markdownToHtml(editableBody)
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Nepodařilo se odeslat žádost.');

      setUvolnitSendMessage('Žádost o uvolnění byla úspěšně odeslána emailem.');
      setTimeout(() => {
        setShowUvolnitModal(false);
        setUvolnitSendMessage('');
      }, 2500);
    } catch (err) {
      console.error('Chyba při odesílání uvolnění emailu:', err);
      setUvolnitSendError(err.message || 'Nepodařilo se odeslat email žádosti.');
    } finally {
      setUvolnitEmailSending(false);
    }
  };

  const handleSendPreblokaceEmail = async () => {
    const uid = userId || localStorage.getItem('lastUserId') || '';
    if (!uid) {
      setPreblokaceSendError('Chybí userId přihlášeného uživatele.');
      return;
    }

    if (!currentUserEmail) {
      setPreblokaceSendError('Přihlášený uživatel nemá vyplněný email.');
      return;
    }

    try {
      setPreblokaceEmailSending(true);
      setPreblokaceSendMessage('');
      setPreblokaceSendError('');

      const res = await fetch(`${API_URL}/preblokace/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: uid,
          companyName: form.name,
          ico: form.ico?.padStart(8, '0'),
          ca: form.ca,
          originalOwner: form.prm,
          customSubject: editableSubject,
          customBody: editableBody,
          customHtmlBody: markdownToHtml(editableBody)
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Nepodařilo se odeslat žádost.');

      setPreblokaceSendMessage('Žádost byla úspěšně odeslána emailem.');
    } catch (err) {
      console.error('Chyba při odeslání přeblokace emailu:', err);
      setPreblokaceSendError(err.message || 'Nepodařilo se odeslat email žádosti.');
    } finally {
      setPreblokaceEmailSending(false);
    }
  };

  // ─── PB test – automatická přeblokace ─────────────────────────────────────

  const pbAddLog = useCallback((msg) => {
    const time = new Date().toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setPbTestLog(prev => [...prev, `${time}  ${msg}`]);
  }, []);

  // Detekce extension + příjem zpráv
  const [pbExtensionVersion, setPbExtensionVersion] = useState('');
  useEffect(() => {
    // 1) Synchronní check – injected.js nastavuje global při document_start
    if (window.__PB_EXT_ACTIVE__) {
      setPbExtensionActive(true);
      setPbExtensionVersion(window.__PB_EXT_VERSION__ || '');
    }

    // 2) Message listener – pro progress, updates a fallback ping/pong
    const handler = (event) => {
      if (event.source !== window) return;
      if (event.data?.__pbPage) return;
      const msg = event.data;
      switch (msg?.type) {
        case 'PB_EXTENSION_ACTIVE':
          setPbExtensionActive(true);
          if (msg.version) setPbExtensionVersion(msg.version);
          break;
        case 'PB_PROGRESS':
          pbAddLog(msg.message);
          if (msg.step) setPbTestStep(msg.step);
          break;
        case 'PB_MULTIPLE_ACCOUNTS':
          setPbTestMultiple(msg.accounts || []);
          break;
        case 'PB_UPDATE_CARD':
          // Pozor: tento handler je registrován jednou při mountu, takže by
          // uzavřel zastaralý `form`. Použij funkční setForm, aby se zachovaly
          // aktivity a všechny ostatní změny – měníme jen ca/prm.
          setForm(prev => ({
            ...prev,
            ...(msg.ca ? { ca: msg.ca } : {}),
            ...(msg.prm ? { prm: msg.prm } : {}),
          }));
          break;
        case 'PB_SIEBEL_RESULT': {
          // Siebel hotovo – ověřit, zda vlastník (prm) odpovídá VF loginům / volným loginům
          // Normalizace: nezalomitelné mezery (\u00a0) → mezera, sloučení mezer, uppercase.
          // Ošetří scrapované hodnoty ze Siebelu, kde login bývá obalen dalším textem
          // (např. "Kovář (XKOVAC01)") nebo obsahuje nezalomitelné mezery.
          const norm = (s) => String(s || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim().toUpperCase();
          const owner = norm(msg.prm);
          setPbCheckedOwner(msg.prm || '');
          const allowedList = [];
          (window.__superuseryFreeLogins || []).forEach(l => { const n = norm(l); if (n) allowedList.push(n); });
          (usersRef.current || []).forEach(u => { const n = norm(u?.vfLogin); if (n) allowedList.push(n); });
          const allowed = new Set(allowedList);
          // Login může být uvnitř delšího řetězce – porovnej i po tokenizaci vlastníka.
          const ownerTokens = owner.split(/[^A-Z0-9]+/).filter(Boolean);
          const isOwnerAllowed = !!owner && (
            allowed.has(owner) ||
            allowedList.some(l => ownerTokens.includes(l))
          );
          if (isOwnerAllowed) {
            // Vlastník je náš → pokračovat automaticky (vyplnit i odeslat)
            pbAddLog('✅ Vlastník odpovídá VF loginu – pokračuji automaticky.');
            setPbTestStep('pa-opening');
            window.postMessage({ type: 'PB_DECISION', action: 'auto' }, '*');
          } else {
            // Vlastník není náš → zobrazit rozhodovací dialog
            pbAddLog(`⏸️ Vlastník "${msg.prm || '?'}" neodpovídá VF loginu. Přeblokace není možná bez uvolnění.`);
            setPbTestStep('decision');
            setPbDecisionNeeded(true);
          }
          break;
        }
        case 'PB_DONE':
          setPbTestStep('completed');
          pbAddLog(msg.submitted === false
            ? '📎 Formulář vyplněn. Přiložte soubor a odešlete ručně v PowerApps.'
            : '✅ Přeblokace úspěšně odeslána!');
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', handler);

    // 3) Ping jako záloha (pro případ race condition)
    window.postMessage({ type: 'PB_PING' }, '*');

    return () => window.removeEventListener('message', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pbAddLog]);

  // Reset při zavření modalu
  useEffect(() => {
    if (!showPbTestModal) {
      if (pbTestPollRef.current) { clearInterval(pbTestPollRef.current); pbTestPollRef.current = null; }
      setPbDecisionNeeded(false);
      setPbCheckedOwner('');
      // Říct background.js aby resetoval stav – jinak příští run bude blokován
      window.postMessage({ type: 'PB_RESET' }, '*');
    }
  }, [showPbTestModal]);

  const pbStartAutomation = useCallback(() => {
    setPbTestLog([]);
    setPbTestError('');
    setPbTestMultiple([]);
    setPbDecisionNeeded(false);
    setPbCheckedOwner('');
    setPbTestStep('starting');

    if (pbExtensionActive) {
      // Spustit přes extension – otevře Siebel i PowerApps automaticky
      const vfLogin = users.find(u => u._id === userId)?.vfLogin || '';
      // Nejprve reset stavu (pokud předchozí run skončil ve stavu done)
      window.postMessage({ type: 'PB_RESET' }, '*');
      setTimeout(() => window.postMessage({
        type: 'PB_START',
        payload: {
          ico: form.ico?.padStart(8, '0') || form.ico || '',
          ca: form.ca || '',
          prm: form.prm || '',
          vfLogin
        }
      }, '*'), 100);
    } else {
      // Fallback: otevřít PowerApps v iframe pro manuální kopírování
      setPbTestStep('ready');
      setPbTestIframeUrl(
        `https://apps.powerapps.com/play/e/default-68283f3b-8487-4c86-adb3-a5228f18b893/a/32f8c771-c3ee-4c46-a691-335c98725914?tenantId=68283f3b-8487-4c86-adb3-a5228f18b893&hint=e8908d19-d616-4fd4-9f1f-272ad6ac229a&sourcetime=${Date.now()}`
      );
    }
  }, [pbExtensionActive, form.ico, form.ca, form.prm, userId, users]);

  // ──────────────────────────────────────────────────────────────────────────

  // Načtení z ARES
  const handleLoadFromARES = async () => {
    if (!form.ico) {
      setAresError("Zadejte IČO");
      return;
    }

    const cleanIco = form.ico.replace(/\s/g, '').replace(/\D/g, '');
    if (cleanIco.length !== 8) {
      setAresError("IČO musí mít 8 číslic");
      return;
    }

    // Kontrola validace IČO, ale povolíme pokračovat i když není platné
    const isValid = validateIco(form.ico);
    if (!isValid) {
      const userConfirms = window.confirm(
        "IČO má neplatný kontrolní součet. Chcete přesto pokračovat ve vyhledávání v ARES?"
      );
      if (!userConfirms) {
        setAresError("Vyhledávání zrušeno - zkontrolujte IČO");
        return;
      }
    }

    setAresLoading(true);
    setAresError("");

    try {
      const aresData = await fetchCompanyFromARES(form.ico);
      
      // Doplní pouze prázdná pole, nepřepíše již vyplněné
      // Pro personType použij automatické rozpoznání podle názvu
      const updatedName = form.name || aresData.name;
      const autoPersonType = detectPersonType(updatedName);
      
      const icoStatus = aresData.stav
        ? (/zanik|vymaz|likvidac|zruš/i.test(aresData.stav)
            ? 'neaktivní'
            : /pozastav/i.test(aresData.stav)
              ? 'pozastavené'
              : 'aktivní')
        : 'aktivní'; // Pokud ARES vrátil data, firma existuje = aktivní

      setForm(prev => ({
        ...prev,
        name: updatedName,
        address: aresData.address || prev.address, // ARES adresa má přednost
        personType: autoPersonType, // Použij automatické rozpoznání místo ARES dat
        ico: aresData.ico, // IČO vždy aktualizuj na vyčištěnou verzi
        icoStatus,
      }));
      
      setAresError("");
    } catch (error) {
      setAresError(error.message);
    } finally {
      setAresLoading(false);
    }
  };

  // Změna kontaktní osoby
  const handleContactChange = (idx, field, value) => {
    const contacts = [...form.contacts];
    contacts[idx][field] = field === "phone" ? formatPhone(value) : value;
    setForm((prev) => ({ ...prev, contacts }));
  };

  // Přidání/odebrání kontaktní osoby
  const addContact = () => setForm((prev) => ({
    ...prev,
    contacts: [...prev.contacts, { name: "", phone: "", email: "" }],
  }));
  const removeContact = (idx) => setForm((prev) => ({
    ...prev,
    contacts: prev.contacts.filter((_, i) => i !== idx),
  }));

  // Pomocná funkce pro uložení včetně nové aktivity
  const handleUpdateActivity = async (activityId) => {
    try {
      const response = await fetch(`${API_URL}/companies/${company._id}/activities/${activityId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: editingActivityText
        })
      });

      if (!response.ok) throw new Error('Chyba při aktualizaci aktivity');

      const updatedCompany = await response.json();
      onSave(updatedCompany);
      setEditingActivityId(null);
      setEditingActivityText("");
    } catch (err) {
      console.error('Chyba při aktualizaci aktivity:', err);
      alert('Nepodařilo se aktualizovat aktivitu');
    }
  };

  // Smazání aktivity – pouze pro administrátory
  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Opravdu smazat tuto aktivitu?')) return;
    try {
      const uid = userId || localStorage.getItem('lastUserId') || '';
      const response = await fetch(`${API_URL}/companies/${company._id}/activities/${activityId}?userId=${uid}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Chyba při mazání aktivity');

      const updatedCompany = await response.json();
      onSave(updatedCompany);
    } catch (err) {
      console.error('Chyba při mazání aktivity:', err);
      alert('Nepodařilo se smazat aktivitu');
    }
  };

  const resetPendingActivityFields = () => {
    setActivityText("");
    setActivityVariantId("");
    setShowVariantIdInput(false);
    setActivityDate("");
    setActivityTime("");
  };

  const persistFormChanges = async ({ updateLocalState = false, formOverrides = {} } = {}) => {
    // Validace: aktivita a datum musí být buď obě vyplněny, nebo obě prázdné
    const _actText = activityText && activityText.trim();
    const _actDate = activityDate;
    if (_actDate && !_actText) {
      alert('Je vybrán datum příští aktivity, ale není zapsán text aktivity. Prosím doplňte aktivitu nebo odstraňte datum.');
      return null;
    }
    if (_actText && !_actDate) {
      alert('Je zapsána aktivita, ale není vybrán datum příští aktivity. Prosím doplňte datum nebo odstraňte text aktivity.');
      return null;
    }

    let newForm = { ...form, ...formOverrides };

    // Pro aktivitu potřebujeme kompletní informace (text i datum s časem)
    const hasActivityText = activityText && activityText.trim();
    const hasDateTime = activityDate && activityTime;
    const isActivityComplete = hasActivityText && hasDateTime;

    if (isActivityComplete) {
      let localISOTime = null;

      if (hasDateTime) {
        // Oprava: Vytvoř datum bez timezone posunu
        // Rozděl datum na části
        const [year, month, day] = activityDate.split('-').map(Number);
        const [hours, minutes] = activityTime.split(':').map(Number);

        // Vytvoř Date objekt přímo s lokálními hodnotami
        const localDateTime = new Date(year, month - 1, day, hours, minutes);

        // Použij přímo místní čas bez dalších převodů
        localISOTime = localDateTime.toISOString();
      }

      newForm.activities = [
        ...(newForm.activities || []),
        {
          text: activityText || "",
          variantId: activityVariantId || null,
          nextActionDate: localISOTime,
          status: form.status,
          createdAt: new Date().toISOString(),
          createdBy: userId,
        },
      ];

      if (localISOTime) {
        newForm.nextActionDate = localISOTime;
      }
      newForm.status = form.status;
    }

    if (newForm.activities && newForm.activities.length > 0) {
      newForm.activities = newForm.activities.map((a, idx) =>
        idx === newForm.activities.length - 1
          ? { ...a, status: newForm.status }
          : a
      );
    }

    if (onSave) {
      await onSave(newForm);
    }

    if (updateLocalState) {
      resetPendingActivityFields();
      setForm(newForm);
    }

    return newForm;
  };

  const handleSave = async (action, skipRatingCheck = false) => {
    // Pokud firma pochází z leadu a hodnocení je povoleno a lead ještě nebyl ohodnocen, zobraz modal
    if (
      !skipRatingCheck &&
      leadRatingAllowed &&
      company &&
      isFromLead &&
      Number(form.leadRating?.score || 0) === 0
    ) {
      setPendingSaveAction(action);
      setShowLeadRatingPrompt(true);
      return;
    }
    setIsLoading(true);
    try {
      const newForm = await persistFormChanges({ updateLocalState: action === "stay" });
      if (!newForm) {
        return;
      }

      // Navigace
      if (action === "list") {
        if (company) {
          // Pro existující firmu jdi na seznam
          navigate("/");
        } else {
          // Pro novou firmu zavři modální okno
          if (onCancel) onCancel();
        }
      } else if (action === "next" && nextCompanyId) {
        navigate(`/companies/${nextCompanyId}`);
      }
    } catch (error) {
      console.error("Chyba při ukládání:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Ohodnocení leadu přímo z modalu a následné uložení
  const handleLeadRatingAndSave = async (score) => {
    setShowLeadRatingPrompt(false);
    const action = pendingSaveAction;
    setPendingSaveAction(null);
    // handleLeadRating vrací uložený leadRating synchronně — React setForm je async,
    // takže form v closure by stále měl score:0 a PUT by ho přepsal zpět.
    const savedLeadRating = await handleLeadRating(score);
    setIsLoading(true);
    try {
      const newForm = await persistFormChanges({
        updateLocalState: action === 'stay',
        formOverrides: savedLeadRating ? { leadRating: savedLeadRating } : {},
      });
      if (!newForm) return;
      if (action === 'list') {
        if (company) navigate('/');
        else if (onCancel) onCancel();
      } else if (action === 'next' && nextCompanyId) {
        navigate(`/companies/${nextCompanyId}`);
      }
    } catch (err) {
      console.error('Chyba při ukládání po hodnocení leadu:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // "Ohodnotím later" - zavřít modal a přistoupit k uložení bez hodnocení
  const handleLeadRatingSkip = async () => {
    setShowLeadRatingPrompt(false);
    const action = pendingSaveAction;
    setPendingSaveAction(null);
    await handleSave(action, true);
  };

  // Funkce pro odeslání leadu (pouze pro callcentrum)
  const handleSendLead = async (action, buttonConfig = null) => {
    setIsLoading(true);
    try {
      // KROK 1: Nejdřív uložit celý formulář stejným save flow jako tlačítko Uložit
      const currentForm = await persistFormChanges();
      if (!currentForm) {
        return;
      }

      const usersResponse = await fetch(`${API_URL}/users?userId=${userId || ''}`);
      const usersData = await usersResponse.json();

      // Standardní callcenter tlačítka vždy předávají na kontakty@vyjedname.cz.
      // Přímé předání na jiného obchodníka je povoleno jen přes "Manuálně předat lead".
      const targetEmail = buttonConfig?.isManualTransfer
        ? (buttonConfig?.assignTo || "kontakty@vyjedname.cz")
        : "kontakty@vyjedname.cz";
      const targetBatchName = buttonConfig?.batchName || "Leady";
      
      // Hledáme cílového uživatele - bezpečně (nejdřív přesný email/ID, až pak username)
      const normalizedTargetEmail = String(targetEmail || '').trim().toLowerCase();
      const targetLocalPart = normalizedTargetEmail.split('@')[0];

      let targetUser = null;

      // 1) Přímý výběr podle ID (hlavně pro manuální předání)
      if (buttonConfig?.targetUserId) {
        targetUser = usersData.find(user => String(user._id) === String(buttonConfig.targetUserId));
      }

      // 2) Přesná shoda emailu
      if (!targetUser && normalizedTargetEmail) {
        targetUser = usersData.find(user => String(user.email || '').trim().toLowerCase() === normalizedTargetEmail);
      }

      // 3) Přesná shoda username (nejprve celý target, pak local-part)
      if (!targetUser && normalizedTargetEmail) {
        targetUser = usersData.find(user => String(user.username || '').trim().toLowerCase() === normalizedTargetEmail);
      }
      if (!targetUser && targetLocalPart) {
        targetUser = usersData.find(user => String(user.username || '').trim().toLowerCase() === targetLocalPart);
      }

      // 4) Speciální fallback pro výchozí předání na kontakty
      if (!targetUser && normalizedTargetEmail === 'kontakty@vyjedname.cz') {
        targetUser = usersData.find(user => String(user.email || '').trim().toLowerCase() === 'kontakty@vyjedname.cz')
          || usersData.find(user => String(user.role || '').toLowerCase() === 'contacts')
          || usersData.find(user => String(user.email || '').trim().toLowerCase().startsWith('kontakty@'));
      }
      
      if (!targetUser) {
        console.error(`Uživatel '${targetEmail}' nebyl nalezen v systému!`);
        console.log("Dostupní uživatelé:", usersData.map(u => ({ name: u.name, email: u.email, username: u.username })));
        
        // Pokusit se najít admin uživatele jako fallback
        targetUser = usersData.find(user => user.role === "admin");
        
        if (!targetUser) {
          alert("Chyba: Nebyl nalezen žádný vhodný uživatel pro přiřazení leadu. Kontaktujte administrátora.");
          return;
        } else {
          console.log("Použiju admin uživatele jako fallback:", targetUser.name);
        }
      }

      // KROK 3: Vytvořit aktivitu o odeslání leadu
      const leadActivity = {
        text: `Lead odeslán - vlastník změněn na ${targetUser.name} (${targetUser.email || targetUser.username}), šarže nastavena na ${targetBatchName}${buttonConfig ? `, typ: ${buttonConfig.description}` : ''}`,
        date: new Date().toISOString(),
        type: "system",
        createdBy: userId, // Přidáváme ID uživatele, který lead odesílá
      };

      // KROK 4: Připravit finální data s přehodnocením vlastníka a šarže
      let finalForm = { 
        ...currentForm, // Použij aktualizovaný formulář s uloženou aktivitou
        createdBy: targetUser._id, // Použít ObjectId cílového uživatele
        assignedTo: targetUser._id, // Pro listy a vlastnictví musí být kontakt přiřazen i v assignedTo
        batchName: targetBatchName, // EXPLICITNĚ nastavit šarži podle konfigurace
        _transferMeta: {
          transferType: 'assignment',
          initiatedBy: userId,
          batchName: targetBatchName
        },
        activities: [
          ...(currentForm.activities || []),
          leadActivity
        ]
      };

      console.log("Odesílám lead s finálními daty:", {
        originalOwner: form.createdBy,
        originalBatchName: form.batchName,
        currentActivityText: activityText,
        savedActivitiesCount: currentForm.activities?.length || 0,
        newOwner: targetUser._id,
        newOwnerName: targetUser.name,
        newBatchName: targetBatchName,
        companyName: finalForm.name,
        finalActivitiesCount: finalForm.activities.length,
        lastActivity: leadActivity,
        buttonConfig: buttonConfig
      });

      // KROK 5: Uložit finální data s přehodnocením vlastníka
      if (onSave) {
        console.log("Volám onSave s finálními daty:", finalForm);
        await onSave(finalForm);
        console.log("Lead úspěšně odeslán - finální uložení dokončeno");
      } else {
        console.error("onSave funkce není definována!");
      }
      
      // KROK 6: Uložit statistiku, pokud existuje konfigurace tlačítka
      if (buttonConfig && buttonConfig._id) {
        try {
          // Získat aktuální uživatelské jméno
          const currentUser = usersData.find(u => u._id === userId);
          const userName = currentUser?.name || "Neznámý uživatel";
          
          const statsResponse = await fetch(`${API_URL}/callcenter/stats`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: userId,
              userName: userName,
              buttonId: buttonConfig._id,
              buttonDescription: buttonConfig.description,
              batchName: targetBatchName,
              value: buttonConfig.value || 0,
              assignedTo: targetEmail,
              companyId: company?._id,
              companyName: finalForm.name
            })
          });
          
          if (statsResponse.ok) {
            console.log("Statistika call centra úspěšně uložena");
          } else {
            console.error("Chyba při ukládání statistiky call centra");
          }
        } catch (statsError) {
          console.error("Chyba při ukládání statistiky:", statsError);
          // Nekritická chyba - lead byl odeslán, jen se neuložila statistika
        }
      }
      
      // Aktualizovat lokální stav po úspěšném uložení
      setForm(finalForm);
      
      // Vyčistit formulář aktivity
      resetPendingActivityFields();

      // Aktualizovat seznam firem, aby se odeslané firmy skryly z pohledu callcentra
      if (fetchCompanies && userId) {
        console.log("Aktualizuji seznam firem pro userId:", userId);
        await fetchCompanies(userId);
      }

      console.log("Lead úspěšně odeslán a seznam firem aktualizován");

      // Provést akci podle tlačítka
      if (action === "list") {
        navigate("/");
      } else if (action === "next") {
        if (nextCompanyId) {
          navigate(`/companies/${nextCompanyId}`);
        } else {
          navigate("/");
        }
      }
      // Pokud action === "stay", zůstaneme na stránce

    } catch (error) {
      console.error("Chyba při odesílání leadu:", error);
      alert("Chyba při odesílání leadu: " + error.message + ". Zkuste to prosím znovu.");
    } finally {
      setIsLoading(false);
    }
  };

  const activitiesToShow = showAllActivities
    ? (form.activities || []).slice().reverse() // Zobrazí všechny aktivity bez stránkování
    : (form.activities || []).slice(-Math.min(getActivitiesPerPage(), 8)).reverse(); // Zobrazí více aktivit podle velikosti obrazovky
    
  const totalActivities = (form.activities || []).length;

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave("list");
  };

  const handleLeadRating = async (score) => {
    if (!company?._id || !userId) return null;
    try {
      setLeadRatingSaving(true);
      const res = await fetch(`${API_URL}/companies/${company._id}/lead-rating`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, ratedBy: userId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Nepodařilo se uložit hodnocení leadu');
      }
      const data = await res.json();
      setForm(prev => ({
        ...prev,
        leadRating: data.leadRating || prev.leadRating,
        leadRatingHistory: data.leadRatingHistory || prev.leadRatingHistory
      }));
      return data.leadRating || null; // vrátíme hodnotu pro synchronní použití
    } catch (error) {
      console.error('Chyba při ukládání hodnocení leadu:', error);
      alert(error.message || 'Nepodařilo se uložit hodnocení leadu');
      return null;
    } finally {
      setLeadRatingSaving(false);
    }
  };

  const handleReklamovatLead = async () => {
    if (!company?._id || !userId) return;
    // Otevři modal s tmavým režimem místo systémového okna
    setReklamaceText('');
    setShowReklamaceModal(true);
    setShowLeadMenu(false);
  };

  const handleSubmitReklamace = async () => {
    if (!company?._id || !userId) return;
    try {
      setReklamaceSaving(true);
      const res = await fetch(`${API_URL}/lead-complaints`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId: company._id, userId, description: reklamaceText.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || 'Nepodařilo se odeslat reklamaci.');
      } else {
        setExistingLeadComplaint(data.complaint || { status: 'open' });
        setShowReklamaceModal(false);
        setReklamaceText('');
      }
    } catch (e) {
      alert('Chyba připojení k serveru.');
    } finally {
      setReklamaceSaving(false);
    }
  };

  const handleManualLeadTransfer = async () => {
    const selectedButton = activeCallCenterButtons.find(btn => String(btn._id) === String(manualButtonId));
    if (!selectedButton) {
      alert('Nejdříve vyberte typ manuálního předání leadu.');
      return;
    }

    if (!manualAssigneeId) {
      alert('Nejdříve vyberte obchodníka, kterému se má lead předat.');
      return;
    }

    const selectedUser = (users || []).find(u => String(u._id) === String(manualAssigneeId));
    if (!selectedUser) {
      alert('Vybraný obchodník nebyl nalezen. Zkuste vybrat znovu.');
      return;
    }

    const selectedUserEmail = selectedUser.email || selectedUser.username || selectedUser.name;
    const manualConfig = {
      ...selectedButton,
      isManualTransfer: true,
      targetUserId: selectedUser._id,
      assignTo: selectedUserEmail,
      batchName: selectedButton.batchName || selectedButton.description || 'Leady',
      description: `${selectedButton.description || 'Manuální předání'} (manuálně)`
    };

    await handleSendLead('stay', manualConfig);
  };

  // Funkce pro práci s příbuznými firmami
  const openRelatedModal = () => {
    console.log('🔍 ===== OTEVÍRÁM RELATED MODAL =====');
    console.log('🔍 userId na začátku:', userId);
    console.log('🔍 API_URL na začátku:', API_URL);
    setShowRelatedModal(true);
    setRelatedSearch("");
    setSearchResults([]); // Vyčisti předchozí výsledky
    // Načti stávající příbuzné firmy
    if (form.relatedCompanies) {
      console.log('🔍 Načítám existující příbuzné firmy:', form.relatedCompanies.length);
      setRelatedCompanies(form.relatedCompanies);
    }
  };

  const searchCompanies = useCallback(async () => {
    console.log('🔍 ===== SEARCH COMPANIES START =====');
    console.log('🔍 searchCompanies volána, hledaný text:', relatedSearch);
    console.log('🔍 userId:', userId);
    console.log('🔍 API_URL:', API_URL);
    console.log('🔍 showRelatedModal:', showRelatedModal);
    
    if (relatedSearch.trim().length < 2) {
      console.log('🔍 Příliš krátký vyhledávací text (', relatedSearch.length, ' znaků), mazání výsledků');
      setSearchResults([]);
      return;
    }

    try {
      // Vyhledej firmy podle názvu nebo IČO pouze pro aktuálního uživatele
      const apiUrl = `${API_URL}/companies?userId=${userId}&all=true`;
      console.log('🔍 Volání API:', apiUrl);
      
      const response = await fetch(apiUrl);
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 API chyba:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      const responseData = await response.json();
      
      console.log('🔍 Response data typ:', typeof responseData);
      console.log('🔍 Response data keys:', Object.keys(responseData || {}));
      console.log('🔍 Response data:', responseData);
      console.log('🔍 Je to pole?', Array.isArray(responseData));
      console.log('🔍 responseData.companies exists?', !!responseData.companies);
      console.log('🔍 responseData.companies is array?', Array.isArray(responseData.companies));
      
      // Zkus najít pole firem v response
      let allCompanies;
      if (Array.isArray(responseData)) {
        allCompanies = responseData;
      } else if (responseData.companies && Array.isArray(responseData.companies)) {
        allCompanies = responseData.companies;
      } else if (responseData.data && Array.isArray(responseData.data)) {
        allCompanies = responseData.data;
      } else {
        console.error('🔍 ❌ API nevrátilo pole firem, vrátilo:', responseData);
        setSearchResults([]);
        return;
      }
      
      console.log('🔍 Načteno firem celkem:', allCompanies.length);
      console.log('🔍 Ukázka prvních 3 firem:', allCompanies.slice(0, 3));
      
      const searchTerm = relatedSearch.toLowerCase().trim();
      console.log('🔍 Hledaný termín (lowercase):', searchTerm);
      
      const filtered = allCompanies.filter(searchCompany => {
        const currentId = company ? company._id : form._id; // ID současné firmy
        console.log('🔍 Kontroluji firmu:', searchCompany.name, 'IČO:', searchCompany.ico, 'ID:', searchCompany._id);
        
        const nameMatch = searchCompany.name && searchCompany.name.toLowerCase().includes(searchTerm);
        const icoMatch = searchCompany.ico && searchCompany.ico.includes(searchTerm);
        const matchesSearch = nameMatch || icoMatch;
        const isNotCurrent = searchCompany._id !== currentId;
        
        console.log('🔍 Výsledek pro firmu', searchCompany.name, '- nameMatch:', nameMatch, 'icoMatch:', icoMatch, 'isNotCurrent:', isNotCurrent);
        
        if (matchesSearch && isNotCurrent) {
          console.log('🔍 ✅ Nalezena shoda:', searchCompany.name, searchCompany.ico);
        }
        
        return isNotCurrent && matchesSearch;
      }).slice(0, 10); // Omez na 10 výsledků
      
      console.log('🔍 Filtrované výsledky celkem:', filtered.length);
      console.log('🔍 Filtrované výsledky:', filtered);
      setSearchResults(filtered);
    } catch (error) {
      console.error('Chyba při vyhledávání firem:', error);
      setSearchResults([]);
    }
  }, [relatedSearch, userId, company, form._id]);

  const addRelatedCompany = (company) => {
    // Zkontroluj, jestli už není přidaná
    if (relatedCompanies.find(rel => rel._id === company._id)) {
      return;
    }

    const newRelated = [...relatedCompanies, {
      _id: company._id,
      name: company.name,
      ico: company.ico,
      status: company.status
    }];
    
    setRelatedCompanies(newRelated);
    // Aktualizuj formulář
    setForm(prev => ({
      ...prev,
      relatedCompanies: newRelated
    }));
  };

  const removeRelatedCompany = (companyId) => {
    const newRelated = relatedCompanies.filter(rel => rel._id !== companyId);
    setRelatedCompanies(newRelated);
    // Aktualizuj formulář
    setForm(prev => ({
      ...prev,
      relatedCompanies: newRelated
    }));
  };

  // Efekt pro vyhledávání při změně textu
  useEffect(() => {
    console.log('🔍 ===== USE EFFECT TRIGGERED =====');
    console.log('🔍 relatedSearch:', `"${relatedSearch}"`, 'length:', relatedSearch.length);
    console.log('🔍 showRelatedModal:', showRelatedModal);
    console.log('🔍 userId:', userId);
    
    const timer = setTimeout(() => {
      if (showRelatedModal && relatedSearch.trim().length >= 2) {
        console.log('🔍 ✅ Podmínky splněny, spouštím searchCompanies po 300ms delay');
        searchCompanies();
      } else {
        console.log('🔍 ❌ Podmínky nesplněny:', 'showRelatedModal:', showRelatedModal, 'searchLength:', relatedSearch.trim().length);
      }
    }, 300);
    return () => {
      console.log('🔍 useEffect cleanup');
      clearTimeout(timer);
    };
  }, [relatedSearch, showRelatedModal, userId, searchCompanies]); // Přidána searchCompanies do dependencies

  return (
    <div className="w-full mx-auto p-1 sm:p-2" style={{maxWidth: "1500px"}}>
      {/* Tlačítko zpět nahoře - kompaktnější */}
      <div className="sticky top-0 z-10 bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-900 dark:to-gray-800 pt-2 pb-1 mb-2"
           style={{ top: isIOSPWA ? 'env(safe-area-inset-top)' : 0 }}>
        <div className="flex justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => onCancel ? onCancel() : navigate("/")}
              className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white rounded-lg shadow-lg transition font-semibold justify-center sm:justify-start"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm sm:text-base">Zpět na seznam firem</span>
            </button>
            
            {/* Název společnosti a IČO - přímo klikatelný pro kopírování */}
            {company && (
              <div className="hidden sm:flex items-center gap-2">
                <div 
                  className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer transition-colors group"
                  onClick={(e) => {
                    const textToCopy = `${company.name} - IČO: ${company.ico}`;
                    navigator.clipboard.writeText(textToCopy).then(() => {
                      // Krátká vizuální zpětná vazba
                      const div = e.currentTarget;
                      const originalBg = div.className;
                      div.className = div.className.replace(/bg-gray-\d+/, 'bg-green-500');
                      setTimeout(() => {
                        div.className = originalBg;
                      }, 500);
                    }).catch(err => {
                      console.error('Nepodařilo se zkopírovat text:', err);
                      // Fallback pro starší prohlížeče
                      const textArea = document.createElement('textarea');
                      textArea.value = textToCopy;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                    });
                  }}
                  title="Klikněte pro kopírování názvu a IČO"
                >
                  <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                    {company.name} - IČO: {company.ico}
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-gray-400 group-hover:text-gray-600 ml-2 opacity-50 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                {(userRole === 'callcentrum' || isFromLead) && (
                  userRole === 'callcentrum' ? (
                    /* Callcentrum: provolat seznam (do kdy provolat) zobrazit vždy hned, jakmile je firma v seznamu – nezávisle na leadu */
                    provolatInfo ? (
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-orange-50 border-orange-300 text-gray-900'}`}>
                        <span className="text-sm font-semibold whitespace-nowrap" title="Do kdy provolat">
                          📋 {provolatInfo.label.replace(/^provolat\s*/i, '')}
                        </span>
                        <button
                          type="button"
                          onClick={() => { if (!provolatExtending) handleToggleExtendProvolat(); }}
                          disabled={provolatExtending}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition ${provolatExtendSelected ? 'bg-amber-500 text-white' : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white border border-orange-300 hover:bg-orange-100')} disabled:opacity-50`}
                          title="Požádat o prodloužení o +1 měsíc"
                        >
                          <span className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center ${provolatExtendSelected ? 'bg-white border-white' : 'border-gray-400'}`}>
                            {provolatExtendSelected && (
                              <svg className="w-2.5 h-2.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </span>
                          <span>Prodloužení +1 měsíc</span>
                        </button>
                      </div>
                    ) : null
                  ) : (
                  <div className="relative" ref={leadMenuRef}>
                    <button
                      type="button"
                      onClick={() => setShowLeadMenu(v => !v)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border select-none transition ${
                        (leadRatingAllowed && Number(form.leadRating?.score || 0) === 0)
                          ? 'bg-orange-500 border-orange-300 animate-pulse shadow-lg shadow-orange-500/40'
                          : 'bg-orange-500 border-orange-600 hover:bg-orange-600'
                      }`}
                      title="Lead z callcentra – nabídka akcí"
                    >
                      <span className="text-sm font-bold text-white">L</span>
                      {leadRatingAllowed && Number(form.leadRating?.score || 0) > 0 && (
                        <span className="text-xs text-yellow-200 font-semibold">{Number(form.leadRating.score)}★</span>
                      )}
                      {existingLeadComplaint && (existingLeadComplaint.status === 'open' || existingLeadComplaint.status === 'in-progress') && (
                        <span className="text-xs text-white" title="Otevřená reklamace">🚩</span>
                      )}
                      {/* hamburger ikona */}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>

                    {showLeadMenu && (
                      <div className={`absolute left-0 mt-2 w-72 rounded-lg shadow-2xl border z-50 p-4 space-y-4 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
                        {/* Hodnocení leadu */}
                        {leadRatingAllowed && (
                          <div>
                            <div className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Hodnocení leadu</div>
                            <div className={`mb-2 rounded-md p-2 text-[11px] leading-snug space-y-1 ${isDarkMode ? 'bg-gray-700/60 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                              <div><span className="text-yellow-400">★</span> = Zákazník nemá zájem, byl nepříjemný.</div>
                              <div><span className="text-yellow-400">★★</span> = Zákazník řekl, že to nemá cenu, že to říkal telefonistce.</div>
                              <div><span className="text-yellow-400">★★★</span> = Dovoláno, nabídka, 2 a více chybných údajů (email, adresy, neověřeno s kým mluví).</div>
                              <div><span className="text-yellow-400">★★★★</span> = Dovoláno, nabídka (max 1 chyba – email, adresa, neověřeno s kým mluví).</div>
                              <div><span className="text-yellow-400">★★★★★</span> = Dovoláno, nabídka, zákazník o callu ví dopředu.</div>
                            </div>
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => {
                                const currentScore = Number(form.leadRating?.score || 0);
                                const isActive = star <= (leadRatingHover || currentScore);
                                return (
                                  <button
                                    key={star}
                                    type="button"
                                    onMouseEnter={() => setLeadRatingHover(star)}
                                    onMouseLeave={() => setLeadRatingHover(0)}
                                    onClick={() => handleLeadRating(star)}
                                    disabled={leadRatingSaving}
                                    className={`text-xl leading-none ${isActive ? 'text-yellow-400' : (isDarkMode ? 'text-gray-500' : 'text-gray-300')} hover:text-yellow-500 disabled:opacity-60`}
                                    title={`Hodnotit ${star}/5`}
                                  >
                                    {isActive ? '★' : '☆'}
                                  </button>
                                );
                              })}
                              <button
                                type="button"
                                onClick={() => handleLeadRating(0)}
                                disabled={leadRatingSaving}
                                className={`ml-2 text-[11px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} disabled:opacity-60`}
                                title="Nastavit hodnocení 0/5"
                              >
                                0
                              </button>
                            </div>
                            {form.leadRating?.ratedAt && (
                              <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {`Hodnoceno ${new Date(form.leadRating.ratedAt).toLocaleDateString('cs-CZ')} ${new Date(form.leadRating.ratedAt).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Provolat seznam + prodloužení */}
                        {provolatInfo && (
                          <div>
                            <div className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Provolat seznam</div>
                            <div className={`text-sm mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              📋 {provolatInfo.label.replace(/^provolat\s*/i, '')}
                            </div>
                            <button
                              type="button"
                              onClick={() => { if (!provolatExtending) handleToggleExtendProvolat(); }}
                              disabled={provolatExtending}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition ${provolatExtendSelected ? 'bg-amber-500 text-white' : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200')} disabled:opacity-50`}
                            >
                              <span>Prodloužení +1 měsíc</span>
                              <span className={`w-4 h-4 rounded border-2 flex items-center justify-center ${provolatExtendSelected ? 'bg-white border-white' : (isDarkMode ? 'border-gray-400' : 'border-gray-400')}`}>
                                {provolatExtendSelected && (
                                  <svg className="w-3 h-3 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                            </button>
                          </div>
                        )}

                        {/* Reklamovat lead */}
                        <div>
                          {existingLeadComplaint ? (
                            <div className={`text-xs p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                              <div className="font-semibold mb-0.5">Reklamace: {
                                { 'open': 'Nová', 'in-progress': 'V řešení', 'approved': 'Uznáno', 'rejected': 'Neuznáno' }[existingLeadComplaint.status] || existingLeadComplaint.status
                              }</div>
                              {existingLeadComplaint.adminReply && (
                                <div className={isDarkMode ? 'text-green-300' : 'text-green-700'}>↩ {existingLeadComplaint.adminReply}</div>
                              )}
                              {(existingLeadComplaint.status === 'approved' || existingLeadComplaint.status === 'rejected') && (
                                <button
                                  type="button"
                                  onClick={handleReklamovatLead}
                                  disabled={reklamaceSaving}
                                  className="mt-2 w-full px-3 py-2 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                                >
                                  {reklamaceSaving ? 'Odesílám…' : '🚩 Reklamovat znovu'}
                                </button>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={handleReklamovatLead}
                              disabled={reklamaceSaving}
                              className="w-full px-3 py-2 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                            >
                              {reklamaceSaving ? 'Odesílám…' : '🚩 Reklamovat lead'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  )
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Desktop tlačítka - skryté na mobilu */}
            {company && (
              <>
                {/* Lead BA pole - zobrazit jen pokud je funkce zapnutá */}
                {leadBaSettings.enabled && (
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-rose-50 dark:bg-rose-900/30 border border-rose-300 dark:border-rose-700 rounded-lg shadow-sm">
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!form.isLeadBa}
                        onChange={e => setForm(f => ({ ...f, isLeadBa: e.target.checked, leadBaTipar: e.target.checked ? f.leadBaTipar : "" }))}
                        className="w-4 h-4 text-rose-600 border-rose-300 rounded focus:ring-rose-500"
                      />
                      <span className="text-sm font-semibold text-rose-700 dark:text-rose-300 whitespace-nowrap">LEAD BA</span>
                    </label>
                    {form.isLeadBa && leadBaSettings.tipari.length > 0 && (
                      <select
                        value={form.leadBaTipar || ""}
                        onChange={e => setForm(f => ({ ...f, leadBaTipar: e.target.value }))}
                        className="border border-rose-300 dark:border-rose-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-rose-500"
                      >
                        <option value="">— Vyberte tipaře —</option>
                        {leadBaSettings.tipari.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Provolat seznam byl přesunut do hamburger menu u "L" badge */}

                {/* Tlačítko Co nabídnout? - desktop */}
                <button
                  type="button"
                  onClick={handleOpenOffer}
                  disabled={offerLoading}
                  className={`hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg shadow-lg transition font-semibold ${offerLoading ? 'opacity-80 cursor-wait' : ''}`}
                  title="Zobrazit návrhy produktů na základě předmětů podnikání"
                >
                  {offerLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      <span>Načítám…</span>
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <span>Co nabídnout?</span>
                    </>
                  )}
                </button>

                {/* Tlačítko Signál - desktop */}
                <button
                  type="button"
                  className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-lg transition font-semibold"
                  title="Zkontrolovat LTE pokrytí Vodafone na adrese sídla společnosti"
                  onClick={() => { const a = form.address || company?.address || ''; setSignalAddress(a); checkSignal(a); }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                  </svg>
                  <span>Signál</span>
                </button>

                {/* Tlačítko Naplánovat schůzku - desktop */}
                <button
                  type="button"
                  onClick={() => onOpenMeetingPlanner && onOpenMeetingPlanner(company)}
                  className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-lg transition font-semibold"
                  title="Naplánovat schůzku s tímto zákazníkem"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Schůzka</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => window.open(`#/requests?companyId=${company._id}&companyName=${encodeURIComponent(company.name)}&companyIco=${company.ico}`, '_blank')}
                  className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg shadow-lg transition font-semibold"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Požadavky</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => window.open(`#/requests?companyId=${company._id}&companyName=${encodeURIComponent(company.name)}&companyIco=${company.ico}&new=true`, '_blank')}
                  className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-lg transition font-semibold"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Požadavek</span>
                </button>
                
                {/* Hamburger menu pro mobil */}
                <button
                  type="button"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="sm:hidden inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-lg transition font-semibold"
                >
                  <div className="flex flex-col gap-1">
                    <div className="w-5 h-0.5 bg-white"></div>
                    <div className="w-5 h-0.5 bg-white"></div>
                    <div className="w-5 h-0.5 bg-white"></div>
                  </div>
                </button>
              </>
            )}
            
            {/* Informační box s počtem dnešních aktivit */}
            <div className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg shadow-lg font-semibold text-xs sm:text-base">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="hidden sm:inline">Zbývá {getTodayActivitiesCount()}</span>
              <span className="sm:hidden">{getTodayActivitiesCount()}</span>
            </div>
          </div>
        </div>

        {company && isFromLead && (
          <div className="sm:hidden mt-2 flex flex-col gap-2 px-3 py-2 bg-orange-500 rounded-lg border border-orange-600">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-white">L • Lead</span>
              {leadRatingAllowed && (
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const currentScore = Number(form.leadRating?.score || 0);
                    const isActive = star <= currentScore;
                    return (
                      <button
                        key={`mobile-lead-rating-${star}`}
                        type="button"
                        onClick={() => handleLeadRating(star)}
                        disabled={leadRatingSaving}
                        className={`text-sm leading-none ${isActive ? 'text-yellow-200' : 'text-orange-200'} hover:text-yellow-100 disabled:opacity-60`}
                        title={`Hodnotit ${star}/5`}
                      >
                        {isActive ? '★' : '☆'}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => handleLeadRating(0)}
                    disabled={leadRatingSaving}
                    className="ml-1 text-[10px] px-1 py-0.5 rounded bg-orange-600 text-orange-100 hover:bg-orange-700 disabled:opacity-60"
                    title="Nastavit hodnocení 0/5"
                  >
                    0
                  </button>
                </div>
              )}
            </div>
            {provolatInfo && (
              <button
                type="button"
                onClick={() => { if (!provolatExtending) handleToggleExtendProvolat(); }}
                disabled={provolatExtending}
                className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm font-medium ${provolatExtendSelected ? 'bg-amber-600 text-white' : 'bg-orange-600 text-orange-100'} disabled:opacity-50`}
              >
                <span>📋 {provolatInfo.label.replace(/^provolat\s*/i, '')} – prodloužení +1m</span>
                <span>{provolatExtendSelected ? '✓' : ''}</span>
              </button>
            )}
            {existingLeadComplaint && (existingLeadComplaint.status === 'open' || existingLeadComplaint.status === 'in-progress') ? (
              <div className="text-xs text-white bg-orange-600 rounded px-2 py-1.5">
                🚩 Reklamace: {existingLeadComplaint.status === 'open' ? 'Nová' : 'V řešení'}
                {existingLeadComplaint.adminReply && <div className="text-orange-100 mt-0.5">↩ {existingLeadComplaint.adminReply}</div>}
              </div>
            ) : (
              <button
                type="button"
                onClick={handleReklamovatLead}
                disabled={reklamaceSaving}
                className="w-full px-3 py-2 rounded text-sm font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                {reklamaceSaving ? 'Odesílám…' : '🚩 Reklamovat lead'}
              </button>
            )}
          </div>
        )}
        
        {/* Mobilní menu dropdown */}
        {showMobileMenu && company && (
          <div className="sm:hidden mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-2 space-y-2">
            {/* Lead BA - mobilní */}
            {leadBaSettings.enabled && (
              <div className="flex flex-col gap-2 px-4 py-3 bg-rose-50 dark:bg-rose-900/30 border border-rose-300 dark:border-rose-700 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.isLeadBa}
                    onChange={e => setForm(f => ({ ...f, isLeadBa: e.target.checked, leadBaTipar: e.target.checked ? f.leadBaTipar : "" }))}
                    className="w-5 h-5 text-rose-600 border-rose-300 rounded focus:ring-rose-500"
                  />
                  <span className="text-sm font-semibold text-rose-700 dark:text-rose-300">LEAD BA</span>
                </label>
                {form.isLeadBa && leadBaSettings.tipari.length > 0 && (
                  <select
                    value={form.leadBaTipar || ""}
                    onChange={e => setForm(f => ({ ...f, leadBaTipar: e.target.value }))}
                    className="w-full border border-rose-300 dark:border-rose-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="">— Vyberte tipaře —</option>
                    {leadBaSettings.tipari.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                handleOpenOffer();
                setShowMobileMenu(false);
              }}
              disabled={offerLoading}
              className={`w-full flex items-center gap-3 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition font-semibold ${offerLoading ? 'opacity-80 cursor-wait' : ''}`}
            >
              {offerLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              )}
              <span>{offerLoading ? 'Načítám…' : 'Co nabídnout?'}</span>
            </button>

            {/* Tlačítko Signál - mobilní */}
            <button
              type="button"
              onClick={() => { const a = form.address || company?.address || ''; setSignalAddress(a); checkSignal(a); setShowMobileMenu(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-semibold"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
              <span>Signál</span>
            </button>

            <button
              type="button"
              onClick={() => {
                onOpenMeetingPlanner && onOpenMeetingPlanner(company);
                setShowMobileMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition font-semibold"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Schůzka</span>
            </button>
            
            <button
              type="button"
              onClick={() => {
                window.open(`#/requests?companyId=${company._id}&companyName=${encodeURIComponent(company.name)}&companyIco=${company.ico}`, '_blank');
                setShowMobileMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition font-semibold"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Požadavky</span>
            </button>
            
            <button
              type="button"
              onClick={() => {
                window.open(`#/requests?companyId=${company._id}&companyName=${encodeURIComponent(company.name)}&companyIco=${company.ico}&new=true`, '_blank');
                setShowMobileMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition font-semibold"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Požadavek</span>
            </button>
          </div>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white dark:bg-gray-800 p-2 sm:p-3 md:p-4 rounded-lg shadow-lg mx-auto border border-gray-200 dark:border-gray-700"
        style={{ width: "100%", minWidth: 320 }}
      >
        {/* Mobilní: vše pod sebou, Desktop: vedle sebe */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 w-full justify-center items-start">
          {/* LEVÁ ČÁST - základní informace */}
          <div className="w-full lg:w-1/2 space-y-4">
            {/* Název firmy a IČO - na mobilu pod sebou */}
            <div className="mb-4">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 sm:items-end">
                <div className="flex-1 sm:flex-[1.8]">
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block font-semibold text-gray-900 dark:text-gray-100">Název firmy</label>
                    {company && ( // Zobrazit pouze pro existující firmy
                      <button
                        type="button"
                        onClick={openRelatedModal}
                        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                        title="Propojit s příbuznými firmami"
                      >
                        🔗
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-11 text-base"
                      placeholder="Název firmy"
                      value={form.name}
                      onChange={e => handleChange("name", e.target.value)}
                      disabled={!isEdit}
                    />
                    {form.name && (
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        title="Kopírovat název firmy"
                        onClick={() => navigator.clipboard.writeText(form.name)}
                      >
                        📋
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 sm:w-32">
                    <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100">
                      IČO{form.icoStatus && (
                        <span className={`ml-1.5 text-xs font-medium px-1.5 py-0.5 rounded-full ${
                          getIcoStatusCategory(form.icoStatus) === 'active'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                            : getIcoStatusCategory(form.icoStatus) === 'suspended'
                              ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                        }`}>{form.icoStatus}</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        className="w-full px-2 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-11 text-base"
                        placeholder="IČO"
                        maxLength={8}
                        value={form.ico}
                        onChange={e => handleChange("ico", e.target.value)}
                        onBlur={handleIcoBlur}
                        disabled={!isEdit}
                      />
                      {form.ico && (
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title="Kopírovat IČO"
                          onClick={() => navigator.clipboard.writeText(form.ico)}
                        >
                          📋
                        </button>
                      )}
                    </div>
                  </div>
                  {isEdit && (
                    <div className="flex gap-2">
                      {/* Tlačítko pro aktualizaci z ARES */}
                      <div>
                        <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 opacity-0">Aktualizace</label>
                        <button
                          type="button"
                          className={`px-3 py-2 rounded font-semibold transition h-11 text-xs ${
                            aresLoading 
                              ? 'bg-gray-300 cursor-not-allowed' 
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                          onClick={handleLoadFromARES}
                          disabled={aresLoading}
                          title="Načíst a aktualizovat údaje z ARES"
                        >
                          {aresLoading ? 'Načítám...' : 'Aktualizace'}
                        </button>
                      </div>
                      
                      {/* Tlačítko pro otevření ARES v novém okně */}
                      {form.ico && /^\d+$/.test(form.ico) && (
                        <div>
                          <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 opacity-0">ARES</label>
                          <button
                            type="button"
                            className="px-3 py-2 rounded font-semibold transition h-11 text-xs bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => window.open(`https://ares.gov.cz/ekonomicke-subjekty?ico=${form.ico.padStart(8, '0')}`, '_blank')}
                            title="Otevřít ARES v novém okně"
                          >
                            ARES
                          </button>
                        </div>
                      )}

                      {/* Tlačítko Uvolnit – rychlé odeslání žádosti emailem */}
                      {form.ico && /^\d+$/.test(form.ico) && (
                        <div>
                          <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 opacity-0">Uvolnit</label>
                          <button
                            type="button"
                            className="px-2 py-2 rounded font-semibold transition h-11 text-xs bg-amber-500 hover:bg-orange-500 text-white whitespace-nowrap"
                            onClick={() => setShowUvolnitModal(true)}
                            title="Odeslat žádost o uvolnění emailem"
                          >
                            Uvolnit
                          </button>
                        </div>
                      )}

                      {/* Tlačítko pro Přeblokaci na SharePoint */}
                      {/* PB test – automatická přeblokace */}
                      {form.ico && /^\d+$/.test(form.ico) && (
                        <div>
                          <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 opacity-0">Přeblokovat</label>
                          <button
                            type="button"
                            className="px-2 py-2 rounded font-semibold transition h-11 text-xs bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap"
                            onClick={() => { setShowPbTestModal(true); pbStartAutomation(); }}
                            title="Automatická přeblokace přes Siebel + PowerApps"
                          >
                            Přeblokovat
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {aresError && (
                <p className="text-red-600 text-sm mt-1">{aresError}</p>
              )}

              {/* Varování o duplicitním IČO */}
              {duplicateWarning && (
                <div className="mt-2 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded">
                  <div className="flex items-start gap-2">
                    <div className="text-orange-500 text-xl">⚠️</div>
                    <div className="flex-1">
                      <p className="text-orange-800 dark:text-orange-200 font-semibold text-sm">
                        Zákazník s tímto IČO je již v CRM založený!
                      </p>
                      <p className="text-orange-700 dark:text-orange-300 text-sm mt-1">
                        <strong>{duplicateWarning.company.name}</strong> (vlastník: {duplicateWarning.ownerName})
                        {duplicateWarning.count > 1 && ` + ${duplicateWarning.count - 1} dalších`}
                      </p>
                    </div>
                    <button
                      onClick={() => setDuplicateWarning(null)}
                      className="text-orange-500 hover:text-orange-700 text-lg leading-none"
                      title="Zavřít varování"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              {/* Příbuzné firmy */}
              {company && form.relatedCompanies && form.relatedCompanies.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">🔗 Příbuzné firmy:</span>
                  </div>
                  <div className="space-y-1">
                    {form.relatedCompanies.map(relCompany => (
                      <div key={relCompany._id} className="flex items-center justify-between text-sm">
                        <button
                          type="button"
                          onClick={() => window.open(`/#/companies/${relCompany._id}`, '_blank')}
                          className="text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 underline cursor-pointer flex-1 text-left"
                          title="Otevřít firmu v nové kartě"
                        >
                          {relCompany.name} ({relCompany.ico})
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRelatedCompany(relCompany._id)}
                          className="text-red-500 hover:text-red-700 text-xs ml-2"
                          title="Odebrat propojení"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sídlo společnosti (2/4), Typ osoby (1/4), CA (1/4) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
              <div className="col-span-2 lg:col-span-2">
                <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">Sídlo společnosti</label>
                <div className="relative">
                  <input
                    className="w-full px-2 py-2 pr-16 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                    placeholder="Sídlo společnosti"
                    value={form.address || ""}
                    onChange={e => handleChange("address", e.target.value)}
                    disabled={!isEdit}
                  />
                  {form.address && (
                    <>
                      <button
                        type="button"
                        className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                        title="Otevřít v Google Maps"
                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.address)}`, '_blank', 'noopener,noreferrer')}
                      >
                        🗺️
                      </button>
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        title="Kopírovat adresu"
                        onClick={() => navigator.clipboard.writeText(form.address)}
                      >
                        📋
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="col-span-1 lg:col-span-1">
                <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">Typ osoby</label>
                <select
                  className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                  value={form.personType || ""}
                  onChange={e => handleChange("personType", e.target.value)}
                  disabled={!isEdit}
                  title="Automaticky rozpoznáno podle názvu (s tečkou = právnická, bez tečky = fyzická)"
                >
                  <option value="">Vyberte typ</option>
                  <option value="Fyzická osoba">Fyzická osoba</option>
                  <option value="Právnická osoba">Právnická osoba</option>
                </select>
              </div>
              <div className="col-span-1 lg:col-span-1">
                <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">CA</label>
                <div className="relative">
                  <input
                    className="w-full px-2 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                    placeholder="Číslo zákazníka"
                    value={form.ca || ""}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10); // Pouze číslice, max 10
                      handleChange("ca", value);
                    }}
                    disabled={!isEdit}
                    maxLength={10}
                  />
                  {form.ca && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      title="Kopírovat CA"
                      onClick={() => navigator.clipboard.writeText(form.ca)}
                    >
                      📋
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Řádek 3: Forecast (1/4), BMSL (1/4), Délka smlouvy (1/4), Zobchodováno (1/4) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
              <div className="col-span-1 lg:col-span-1">
                <label className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1 font-semibold text-gray-900 dark:text-gray-100 text-sm">
                    Forecast
                    <span
                      className="relative inline-flex"
                      onMouseEnter={() => setShowForecastInfo(true)}
                      onMouseLeave={() => setShowForecastInfo(false)}
                    >
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowForecastInfo(v => !v); }}
                        className="w-4 h-4 flex items-center justify-center rounded-full border border-gray-400 dark:border-gray-500 text-[10px] font-bold text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        title="Vysvětlivky k forecastům"
                        aria-label="Vysvětlivky k forecastům"
                      >
                        i
                      </button>
                      {showForecastInfo && (
                        <div className="absolute z-50 top-6 left-0 w-64 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg text-xs text-gray-700 dark:text-gray-200 font-normal">
                          <div className="font-semibold mb-1.5 text-gray-900 dark:text-gray-100">Vysvětlivky k forecastům</div>
                          <ul className="space-y-1">
                            <li><span className="font-semibold">0 %</span> = Zákazník nemá zájem.</li>
                            <li><span className="font-semibold">25 %</span> = Prohra.</li>
                            <li><span className="font-semibold">50 %</span> = Zákazník se rozhoduje.</li>
                            <li><span className="font-semibold">75 %</span> = Zákazník má zájem o přechod.</li>
                            <li><span className="font-semibold">99 %</span> = Čekám na podpis.</li>
                            <li><span className="font-semibold">100 %</span> = Výhra.</li>
                          </ul>
                        </div>
                      )}
                    </span>
                  </span>
                  {/* Tlačítko Upsell+ se zobrazí pouze když forecast = 100% a isEdit = true */}
                  {form.forecast === "100" && isEdit && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const btn = document.getElementById('upsell-create-btn');
                        if (btn) btn.click();
                      }}
                      className="px-2 py-0.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded font-semibold transition-colors"
                      title="Přidat upsell"
                    >
                      Upsell+
                    </button>
                  )}
                </label>
                <select
                  className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                  value={String(form.forecast || "")}
                  onChange={e => handleChange("forecast", e.target.value)}
                  disabled={!isEdit}
                >
                  <option value="">Vyberte forecast</option>
                  <option value="0">0%</option>
                  <option value="25">25%</option>
                  <option value="50">50%</option>
                  <option value="75">75%</option>
                  <option value="99">99%</option>
                  <option value="100">100%</option>
                </select>
              </div>
              <div className="col-span-1 lg:col-span-1">
                <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">BMSL</label>
                <input
                  className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                  placeholder="BMSL"
                  type="number"
                  min={0}
                  value={form.bmsl || ""}
                  onChange={e => handleChange("bmsl", e.target.value)}
                  disabled={!isEdit}
                />
              </div>
              <div className="col-span-1 lg:col-span-1">
                <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">Délka smlouvy</label>
                <select
                  className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                  value={form.contractLength || ""}
                  onChange={e => handleChange("contractLength", e.target.value)}
                  disabled={!isEdit || form.forecast !== "100"}
                >
                  <option value="">Vyberte</option>
                  <option value="12">12 měsíců</option>
                  <option value="24">24 měsíců</option>
                  <option value="36">36 měsíců</option>
                  <option value="48">48 měsíců</option>
                </select>
              </div>
              <div className="col-span-1 lg:col-span-1">
                <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">
                  Zobchodováno
                </label>
                <input
                  className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm date-input-mobile lg:date-input-mobile-none"
                  type="date"
                  value={formatDateForInput(form.closedDate)}
                  onChange={e => handleChange("closedDate", e.target.value)}
                  disabled={!isEdit || form.forecast !== "100"}
                />
              </div>
            </div>

            {/* Upselly na celou šířku - zobrazí se jen když forecast = 100% */}
            {company?._id && form.forecast === "100" && (
              <div className="mb-4">
                <UpsellManager
                  companyId={company._id}
                  mainForecast={form.forecast}
                  isEdit={isEdit}
                  isDarkMode={isDarkMode}
                  currentUserId={userId}
                />
              </div>
            )}

            {/* Řádek 4: Operátor (1/5), Konec RS (1/5), Příležitost (1/5), Vlastník firmy CRM (1/5), PRM Vodafone (1/5) */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
              <div className="col-span-1 lg:col-span-1">
                <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">Operátor</label>
                <select
                  className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                  value={form.operator || ""}
                  onChange={e => handleChange("operator", e.target.value)}
                  disabled={!isEdit}
                >
                  <option value="">Operátor</option>
                  <option value="O2">O2</option>
                  <option value="T-Mobile">T-Mobile</option>
                  <option value="Vodafone">Vodafone</option>
                  {form.operator && !["" , "O2", "T-Mobile", "Vodafone"].includes(form.operator) && (
                    <option value={form.operator}>{form.operator}</option>
                  )}
                </select>
              </div>
              <div className="col-span-1 lg:col-span-1">
                <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">Konec RS</label>
                <input
                  className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm date-input-mobile lg:date-input-mobile-none"
                  type="date"
                  value={formatDateForInput(form.rsEndDate)}
                  onChange={e => handleChange("rsEndDate", e.target.value)}
                  disabled={!isEdit}
                />
              </div>
              <div className="col-span-1 lg:col-span-1">
                <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">Příležitost</label>
                {(() => {
                  const val = form.opportunityDate || "";
                  const isDateValue = /^\d{4}-\d{2}-\d{2}$/.test(val);
                  // Textová hodnota (např. "Neověřeno") – zobraz text s možností přepsat na datum
                  if (val && !isDateValue) {
                    return (
                      <div className="flex items-center gap-1">
                        <div
                          className="flex-1 px-2 py-2 border border-amber-300 dark:border-amber-700 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 h-10 text-sm flex items-center truncate font-medium"
                          title={val}
                        >
                          {val}
                        </div>
                        {isEdit && (
                          <button
                            type="button"
                            onClick={() => handleChange("opportunityDate", "")}
                            title="Zadat datum"
                            className="flex-shrink-0 px-2 h-10 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm"
                          >
                            📅
                          </button>
                        )}
                      </div>
                    );
                  }
                  return (
                    <input
                      className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm date-input-mobile lg:date-input-mobile-none"
                      type="date"
                      value={formatDateForInput(form.opportunityDate)}
                      onChange={e => handleChange("opportunityDate", e.target.value)}
                      disabled={!isEdit}
                    />
                  );
                })()}
              </div>
              <div className="col-span-1 lg:col-span-1">
                <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">Vlastník firmy CRM</label>
                <div className="relative" ref={ownerDropdownRef}>
                  {/* Trigger tlačítko */}
                  <button
                    type="button"
                    disabled={!isEdit}
                    onClick={() => { setShowOwnerDropdown(v => !v); setOwnerSearch(''); }}
                    className={`w-full px-2 py-2 border rounded text-sm h-10 text-left flex items-center justify-between ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                    } disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    <span className="truncate">
                      {(users || []).find(u => u._id === (form.createdBy?._id || form.createdBy))?.name || 'Vyberte vlastníka'}
                    </span>
                    <span className="ml-1 flex-shrink-0 text-gray-400">▾</span>
                  </button>

                  {/* Dropdown panel */}
                  {showOwnerDropdown && (
                    <div className={`absolute top-full left-0 w-full z-50 mt-1 border rounded-lg shadow-xl ${
                      isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
                    }`}>
                      {/* Vyhledávání */}
                      <div className={`p-2 border-b ${ isDarkMode ? 'border-gray-600' : 'border-gray-200' }`}>
                        <input
                          type="text"
                          placeholder="🔍 Hledat uživatele..."
                          value={ownerSearch}
                          onChange={e => setOwnerSearch(e.target.value)}
                          autoFocus
                          className={`w-full px-2 py-1.5 text-sm border rounded ${
                            isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        />
                      </div>

                      {/* Seznam uživatelů seskupených podle role */}
                      <div className="max-h-56 overflow-y-auto">
                        {/* Prázdná možnost */}
                        {!ownerSearch && (
                          <button
                            type="button"
                            onClick={() => { setForm({ ...form, createdBy: '' }); setShowOwnerDropdown(false); setOwnerSearch(''); }}
                            className={`w-full text-left px-3 py-2 text-sm ${
                              !form.createdBy
                                ? isDarkMode ? 'bg-blue-900/50 text-blue-200 font-semibold' : 'bg-blue-100 text-blue-900 font-semibold'
                                : isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                            }`}
                          >
                            -- Vyberte vlastníka --
                          </button>
                        )}

                        {groupOwnersByRole(users || [], ownerSearch).map(({ role, label, users: roleUsers }) => (
                          <div key={role}>
                            <div className={`px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                              isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {label} ({roleUsers.length})
                            </div>
                            {roleUsers.map(user => {
                              const uid = form.createdBy?._id || form.createdBy;
                              const isSelected = user._id === uid;
                              return (
                                <button
                                  key={user._id}
                                  type="button"
                                  onClick={() => { setForm({ ...form, createdBy: user._id }); setShowOwnerDropdown(false); setOwnerSearch(''); }}
                                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                    isSelected
                                      ? isDarkMode ? 'bg-blue-900/50 text-blue-200 font-semibold' : 'bg-blue-100 text-blue-900 font-semibold'
                                      : isDarkMode ? 'text-white hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-100'
                                  }`}
                                >
                                  {user.name || user.username}
                                </button>
                              );
                            })}
                          </div>
                        ))}

                        {ownerSearch && groupOwnersByRole(users || [], ownerSearch).length === 0 && (
                          <div className={`px-3 py-3 text-sm text-center ${ isDarkMode ? 'text-gray-400' : 'text-gray-500' }`}>
                            Žádný uživatel nenalezen
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-1 lg:col-span-1">
                <label className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">PRM Vodafone</span>
                  {isEdit && users.find(u => u._id === userId)?.vfLogin && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const myLogin = users.find(u => u._id === userId)?.vfLogin || '';
                        if (myLogin) handleChange("prm", myLogin);
                      }}
                      className="px-2 py-0.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded font-semibold transition-colors"
                      title="Vyplnit mým VF loginem"
                    >
                      ✅
                    </button>
                  )}
                </label>
                <div className="relative">
                  {(() => {
                    const loggedInVfLogin = users.find(u => u._id === userId)?.vfLogin || '';
                    const isSuperuser = superuseryLogins.length > 0 && form.prm && superuseryLogins.includes(form.prm);
                    const isMatch = loggedInVfLogin && form.prm && form.prm === loggedInVfLogin;
                    const isMismatch = loggedInVfLogin && form.prm && form.prm !== loggedInVfLogin;
                    const borderClass = isMatch
                      ? 'border-green-400 ring-1 ring-green-400'
                      : isSuperuser
                      ? 'border-yellow-400 ring-1 ring-yellow-400'
                      : isMismatch
                      ? 'border-red-400 ring-1 ring-red-400'
                      : 'border-gray-300 dark:border-gray-600';
                    const colorClass = isMatch
                      ? 'bg-green-50 text-green-900 dark:bg-green-900/30 dark:text-green-200'
                      : isSuperuser
                      ? 'bg-yellow-50 text-yellow-900 dark:bg-yellow-900/30 dark:text-yellow-200'
                      : isMismatch
                      ? 'bg-red-50 text-red-900 dark:bg-red-900/30 dark:text-red-200'
                      : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
                    return (
                      <input
                        className={`w-full px-2 py-2 pr-8 border rounded h-10 text-sm transition-colors ${colorClass} ${borderClass}`}
                        style={{}}
                        placeholder="Zadejte PRM"
                        value={form.prm || ""}
                        onChange={e => {
                          const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 15);
                          handleChange("prm", value);
                        }}
                        disabled={!isEdit}
                        maxLength={15}
                      />
                    );
                  })()}
                  {form.prm && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      title="Kopírovat PRM"
                      onClick={() => navigator.clipboard.writeText(form.prm)}
                    >
                      📋
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Pokrytí – uložený snapshot */}
            {form.coverageSnapshot && (() => {
              const sn = form.coverageSnapshot;
              const date = new Date(sn.savedAt).toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
              const dot = (quality) => {
                const colors = { 'Výborný': 'bg-green-500', 'Dobrý': 'bg-lime-500', 'Slabý': 'bg-yellow-500', 'Velmi slabý': 'bg-orange-500', 'Kritický': 'bg-red-500' };
                return <span className={`inline-block w-2 h-2 rounded-full mr-0.5 ${colors[quality] || 'bg-gray-400'}`} />;
              };
              return (
                <div className={`mb-4 rounded-xl border px-3 py-2 text-xs flex items-center justify-between gap-2 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-3 flex-wrap min-w-0">
                    <span className="font-bold text-red-600 dark:text-red-400 whitespace-nowrap">VOD</span>
                    <span className={`whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      LTE: {sn.lte ? <>{dot(sn.lte.quality)}{sn.lte.quality} ({sn.lte.dbm.toFixed(1)} dBm)</> : <span className="text-gray-400">–</span>}
                    </span>
                    <span className={`whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      5G: {sn.fiveG ? <>{dot(sn.fiveG.quality)}{sn.fiveG.quality} ({sn.fiveG.dbm.toFixed(1)} dBm)</> : <span className="text-gray-400">–</span>}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[11px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{date}</span>
                    {isEdit && (
                      <button onClick={() => handleChange('coverageSnapshot', null)} className="text-gray-400 hover:text-red-500 transition" title="Odstranit snapshot">✕</button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Moje poznámky */}
            <div className="mb-4">
              <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">Moje poznámky</label>
              <textarea
                className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none"
                placeholder="Doplň poznámku"
                rows={3}
                value={form.myNotes || ""}
                onChange={e => handleChange("myNotes", e.target.value)}
                disabled={!isEdit}
              />
            </div>

            {/* Kontaktní osoby - optimalizace pro mobilní */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block font-semibold text-gray-900 dark:text-gray-100">Kontaktní osoby</label>
              </div>
              <div className="space-y-3 overflow-y-auto" style={{ maxHeight: `${Math.max(405, (screenHeight || 1024) * 0.4 + 5)}px` }}>
                {form.contacts.map((contact, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                    {/* Mobilní: všechno pod sebou, Desktop: rozloženo */}
                    <div className="space-y-2">
                      <div className="flex gap-2 items-center">
                        <div className="flex-1 relative">
                          <input
                            className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                            placeholder="Jméno kontaktní osoby"
                            value={contact.name}
                            onChange={e => handleContactChange(idx, "name", e.target.value)}
                            disabled={!isEdit}
                          />
                          {contact.name && (
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                              title="Kopírovat jméno"
                              onClick={() => navigator.clipboard.writeText(contact.name)}
                            >
                              📋
                            </button>
                          )}
                        </div>
                        {form.contacts.length > 1 && isEdit && (
                          <button
                            type="button"
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded font-medium h-10 transition-colors"
                            onClick={() => removeContact(idx)}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="relative">
                          <input
                            className="w-full px-3 py-2 pr-16 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                            placeholder="Telefon"
                            value={contact.phone}
                            onChange={e => handleContactChange(idx, "phone", e.target.value)}
                            disabled={!isEdit}
                          />
                          {contact.phone && contact.phone.length > 5 && (
                            <>
                              <a
                                href={`tel:${contact.phone.replace(/\s/g, '')}`}
                                className="absolute right-10 top-1/2 -translate-y-1/2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors"
                                title="Zavolat"
                              >
                                📞
                              </a>
                              <button
                                type="button"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                title="Kopírovat číslo (420...)"
                                onClick={() => {
                                  let n = contact.phone.replace(/\s/g, '').replace(/^\+/, '');
                                  if (n.startsWith('0')) n = '420' + n.slice(1);
                                  else if (!n.startsWith('420')) n = '420' + n;
                                  navigator.clipboard.writeText(n);
                                }}
                              >
                                📋
                              </button>
                            </>
                          )}
                        </div>
                        <div className="relative">
                          <input
                            className="w-full px-3 py-2 pr-16 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                            placeholder="Email"
                            value={contact.email}
                            onChange={e => handleContactChange(idx, "email", e.target.value)}
                            disabled={!isEdit}
                          />
                          {contact.email && contact.email.includes('@') && (
                            <>
                              <button
                                type="button"
                                className="absolute right-8 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                                onClick={() => {
                                  // Vytvoř mailto: link pro email
                                  window.open(`mailto:${contact.email}`, '_self');
                                }}
                                title="Odeslat email"
                              >
                                📧
                              </button>
                              <button
                                type="button"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                title="Kopírovat email"
                                onClick={() => navigator.clipboard.writeText(contact.email)}
                              >
                                📋
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {/* VoIP: volat přímo z CRM (jen pokud má uživatel povoleno) */}
                      {twilioEnabled && contact.phone && contact.phone.length > 5 && (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm font-semibold transition-colors"
                            title={voipProvider === 'odorik' ? 'Volat přes Odorik (VoIP)' : 'Volat přes VoIP z CRM'}
                            onClick={() => startVoipCall(contact)}
                          >
                            ☎️ Volat{voipProvider !== 'odorik' && twilioNumber ? ` z ${twilioNumber}` : ''} (VoIP)
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Tlačítko Přidat kontakt - vně scrollovací oblasti, aby bylo vždy viditelné */}
              {isEdit && (
                <button
                  type="button"
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold mt-3 transition-colors flex items-center justify-center gap-2 shadow-lg"
                  onClick={addContact}
                >
                  ➕ Přidat kontakt
                </button>
              )}
            </div>
          </div>

          {/* PRAVÁ ČÁST - Aktivity - na mobilu pod levou částí */}
          <div className="w-full lg:w-1/2 lg:border-l border-gray-200 dark:border-gray-600 lg:pl-6 border-t lg:border-t-0 pt-4 lg:pt-0">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                📋 Aktivity
              </h3>
              {(form.activities || []).length > 0 && (
                <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full">
                  {(form.activities || []).length}
                </span>
              )}
            </div>
            {/* Seznam aktivit - fixní výška s možností scrollování */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
              <ul className="space-y-3" style={{ 
                height: activityDescExpanded
                  ? `${(screenHeight || 1024) < 900 ? '64px' : (screenHeight || 1024) <= 1080 ? '96px' : '128px'}`
                  : `${(screenHeight || 1024) < 900 ? '256px' : (screenHeight || 1024) <= 1080 ? '336px' : '536px'}`,
                overflowY: 'auto',
                transition: 'height 0.35s ease'
              }}>
                {activitiesToShow.length === 0 && (
                  <li className="text-gray-400 dark:text-gray-500 text-center py-4">
                    📝 Žádné aktivity
                  </li>
                )}
                {activitiesToShow.map((act, idx) => (
                  <li key={act._id || idx} className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm border border-gray-200 dark:border-gray-600">
                    {/* Inline editace poslední aktivity - pouze text */}
                    {idx === 0 && editingActivityId === act._id ? (
                      <div className="space-y-2">
                        <div className="flex items-start justify-between mb-2">
                          <textarea
                            value={editingActivityText}
                            onChange={(e) => setEditingActivityText(e.target.value)}
                            className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm mr-2"
                            rows="3"
                            placeholder="Text aktivity..."
                          />
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {act.status && (
                              <span className="font-semibold text-blue-700 dark:text-blue-400 text-xs">🏷️ {act.status}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => handleUpdateActivity(act._id)}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                          >
                            ✓ Uložit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingActivityId(null);
                              setEditingActivityText("");
                            }}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                          >
                            ✕ Zrušit
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Horní řádek: Popis aktivity vlevo, přehrávač + Stav vpravo */}
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <div 
                            className="font-medium text-gray-900 dark:text-white text-sm leading-tight flex-1 pr-2" 
                            style={{whiteSpace: 'pre-wrap'}}
                            dangerouslySetInnerHTML={{ __html: act.text }}
                          />
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Přehrání nahrávky hovoru (Twilio) – napravo od textu, respektuje světlý/tmavý režim */}
                            {act.twilioCallId && (
                              <audio
                                controls
                                preload="none"
                                src={`${API_URL}/twilio/recording-file/${act.twilioCallId}?userId=${userId || localStorage.getItem('lastUserId') || ''}`}
                                style={{ colorScheme: isDarkMode ? 'dark' : 'light' }}
                                className="h-8 w-[220px] max-w-[45vw]"
                              />
                            )}
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center gap-1">
                                {act.status && (
                                  <span className="font-semibold text-blue-700 dark:text-blue-400 text-xs">🏷️ {act.status}</span>
                                )}
                                {/* Tlačítko editace poslední aktivity - pouze text */}
                                {idx === 0 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingActivityId(act._id);
                                      setEditingActivityText(act.text);
                                    }}
                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs px-1"
                                    title="Upravit text aktivity"
                                  >
                                    ✏️
                                  </button>
                                )}
                                {/* Smazání aktivity - pouze pro administrátory */}
                                {userRole === 'admin' && act._id && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteActivity(act._id)}
                                    className="text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 text-xs leading-none px-0.5"
                                    title="Smazat aktivitu"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                              {/* Variant ID pod stavem */}
                              {act.variantId && (
                                <span className="font-mono text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                                  ID: {act.variantId}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Dolní řádek: Zapsáno vlevo, Uložil uprostřed, Další akce vpravo */}
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>📅 Zapsáno: {act.createdAt ? new Date(act.createdAt).toLocaleString('cs-CZ') : "-"}</span>
                      {act.createdBy && (
                        <span className="text-gray-600 dark:text-gray-400">👤 Uložil: {
                          typeof act.createdBy === 'object' 
                            ? act.createdBy.name 
                            : users?.find(u => u._id === act.createdBy)?.name || 'Systém'
                        }</span>
                      )}
                      {act.nextActionDate && (
                        <span>⏰ Další akce: {new Date(act.nextActionDate).toLocaleString('cs-CZ')}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            
            
            {/* Tlačítko Zobrazit další + počítadla podkladů vpravo */}
            <div className="mb-4 flex items-center justify-between">
              <div>
                {totalActivities > Math.min(getActivitiesPerPage(), 8) && (
                  <button
                    type="button"
                    className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-3 py-2 rounded text-sm font-medium border border-gray-300 dark:border-gray-600 transition"
                    onClick={() => {
                      setShowAllActivities(!showAllActivities);
                    }}
                  >
                    {showAllActivities ? `Zobrazit jen posledních ${Math.min(getActivitiesPerPage(), 8)}` : `Zobrazit další`}
                  </button>
                )}
              </div>
              <MiniCounterButtons userId={userId} isDarkMode={isDarkMode} />
            </div>
            {/* Formulář nové aktivity - kompaktní layout */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-4 border border-blue-200 dark:border-gray-600">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  ➕ Přidat aktivitu
                </h4>
                <div className="flex items-center gap-2">
                  {userRole === 'admin' && (
                    <button
                      type="button"
                      onClick={() => setShowTemplatesModal(true)}
                      className="p-1 text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 transition-colors rounded hover:bg-teal-50 dark:hover:bg-gray-600"
                      title="Správa šablon aktivit"
                    >
                      ⚡
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowStatusConfig(!showStatusConfig)}
                    className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors rounded hover:bg-gray-100 dark:hover:bg-gray-600"
                    title="Nastavit viditelné stavy"
                  >
                    ⚙️
                  </button>
                </div>
              </div>
              
              {/* Stav, Datum a Čas na jednom řádku - upraveno na rovnoměrné rozdělení */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div className="relative status-config-container">
                  <label className="block font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">
                    Stav
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-1" title="Klikněte pro otevření dropdownu s rychlým filtrem">
                      📋
                    </span>
                  </label>
                  
                  {/* Vlastní dropdown s filtrem */}
                  <div className="relative status-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm text-left flex items-center justify-between"
                    >
                      <span className={!form.status ? "text-gray-500 dark:text-gray-400" : ""}>
                        {form.status || "Vyberte stav"}
                      </span>
                      <span className="text-gray-400">
                        {showStatusDropdown ? "▲" : "▼"}
                      </span>
                    </button>
                    
                    {/* Dropdown menu s filtrem - otevírá se nahoru */}
                    {showStatusDropdown && (
                      <div ref={statusDropdownRef} className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-40 max-h-[40rem] overflow-hidden">
                        {/* Status options - nyní nahoře */}
                        <div className="max-h-[33rem] overflow-y-auto">
                          {/* Prázdná možnost */}
                          <button
                            type="button"
                            onClick={() => {
                              handleChange("status", "");
                              setShowStatusDropdown(false);
                              setStatusFilter("");
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-600"
                          >
                            Vyberte stav
                          </button>
                          
                          {getFilteredStatuses().map((status) => (
                            <button
                              key={status}
                              type="button"
                              onClick={() => {
                                handleChange("status", status);
                                setShowStatusDropdown(false);
                                setStatusFilter("");
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                            >
                              {status}
                            </button>
                          ))}
                          
                          {getFilteredStatuses().length === 0 && statusFilter && (
                            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
                              Žádné stavy nevyhovují filtru "{statusFilter}"
                            </div>
                          )}
                        </div>
                        
                        {/* Filter input - nyní dole */}
                        <div className="p-2 border-t border-gray-200 dark:border-gray-600">
                          <input
                            type="text"
                            placeholder="Hledat stav..."
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setShowStatusDropdown(false);
                                setStatusFilter("");
                              }
                              if (e.key === 'Enter' && getFilteredStatuses().length === 1) {
                                handleChange("status", getFilteredStatuses()[0]);
                                setShowStatusDropdown(false);
                                setStatusFilter("");
                              }
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                            autoFocus
                          />
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            💡 ESC pro zavření, Enter pro výběr jediného výsledku
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Konfigurace stavů */}
                  {showStatusConfig && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Viditelné stavy:</span>
                        <button
                          type="button"
                          onClick={() => setShowStatusConfig(false)}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="space-y-1" style={{ maxHeight: `${Math.max(200, (screenHeight || 1024) * 0.2)}px`, overflowY: 'auto' }}>
                        {availableStatuses.map(status => (
                          <label key={status} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded text-sm">
                            <input
                              type="checkbox"
                              checked={!hiddenStatuses.includes(status)}
                              onChange={() => toggleStatusVisibility(status)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-gray-900 dark:text-white">
                              {status}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <label className="block font-semibold mb-1 text-gray-900 dark:text-gray-100 text-sm">
                    Datum a čas další akce
                  </label>
                  
                  {/* Datum input s možností ručního zadání */}
                  <div className="flex">
                    <input
                      type="text"
                      placeholder="Vyber nebo napiš datum"
                      value={isEditingDate ? activityDate : formatActivityDateForDisplay(activityDate, activityTime)}
                      onClick={() => setShowDateTimePicker(true)}
                      onFocus={() => setShowDateTimePicker(true)}
                      onKeyDown={(e) => {
                        // Jednodušší mazání - Backspace nebo Delete vždy smaže celé datum
                        if (e.key === 'Backspace' || e.key === 'Delete') {
                          e.preventDefault();
                          setIsEditingDate(true);
                          setActivityDate("");
                          return;
                        }
                        // Ctrl+A pro výběr veškerého textu
                        if (e.ctrlKey && e.key === 'a') {
                          e.target.select();
                        }
                      }}
                      onChange={(e) => {
                        // Aktivuj editovací mód při psaní
                        if (!isEditingDate) {
                          setIsEditingDate(true);
                        }
                        
                        let value = e.target.value.trim();
                        
                        // Automatické formátování během psaní pro český formát
                        if (value.match(/^\d{1,2}$/) && value.length === 2) {
                          value += '.';
                        } else if (value.match(/^\d{1,2}\.\d{1,2}$/) && value.length === 5) {
                          value += '.';
                        }
                        
                        setActivityDate(value);
                      }}
                      onBlur={(e) => {
                        let value = e.target.value.trim();
                        
                        // Automatické doplnění nul pro jednotlivé části data
                        if (value.match(/^\d{1,2}\.\d{1,2}(\.\d{2,4})?$/)) {
                          const parts = value.split('.');
                          const day = parts[0].padStart(2, '0');
                          const month = parts[1].padStart(2, '0');
                          
                          if (parts.length === 3) {
                            // Máme také rok
                            let year = parts[2];
                            if (year.length === 2) {
                              year = `20${year}`;
                            }
                            value = `${day}.${month}.${year}`;
                          } else {
                            // Pouze den a měsíc
                            value = `${day}.${month}`;
                          }
                        }
                        
                        // Pokud je zadané datum ve formátu DD.MM.YYYY, převedeme na YYYY-MM-DD
                        if (value.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                          const [day, month, year] = value.split('.');
                          const normalizedDate = `${year}-${month}-${day}`;
                          setActivityDate(normalizedDate);
                        }
                        // Pokud je zadané datum ve formátu DD.MM.YY, převedeme na YYYY-MM-DD  
                        else if (value.match(/^\d{2}\.\d{2}\.\d{2}$/)) {
                          const [day, month, year] = value.split('.');
                          const fullYear = `20${year}`;
                          const normalizedDate = `${fullYear}-${month}-${day}`;
                          setActivityDate(normalizedDate);
                        }
                        // Validace YYYY-MM-DD formátu
                        else if (value.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
                          const [year, month, day] = value.split('-');
                          const normalizedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                          setActivityDate(normalizedDate);
                        }
                        // Pokud máme pouze částečně zadané datum (např. "01.09"), necháme ho tak
                        else if (value.match(/^\d{2}\.\d{2}$/)) {
                          setActivityDate(value);
                        }
                        
                        // Ukončení editovacího módu
                        setIsEditingDate(false);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-10 text-sm"
                    />
                  </div>

                  {/* Custom DateTime Picker v roletce */}
                  {showDateTimePicker && (
                    <div ref={dateTimePickerRef} className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-2xl z-50 p-4">
                      <div className="grid grid-cols-1 gap-4">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-600 pb-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">📅 Vyberte datum a čas</h3>
                          <button
                            type="button"
                            onClick={() => {
                              setShowDateTimePicker(false);
                              setIsEditingDate(false);
                            }}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl"
                          >
                            ×
                          </button>
                        </div>

                        {/* Graf plánování aktivit */}
                        <div className="mb-4">
                          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                            {(() => {
                              // Výpočet pracovních dnů
                              const calculateWorkingDays = (startDate, days) => {
                                const result = [];
                                let currentDate = new Date(startDate);
                                while (result.length < days) {
                                  currentDate.setDate(currentDate.getDate() + 1);
                                  const dayOfWeek = currentDate.getDay();
                                  if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                                    result.push(new Date(currentDate));
                                  }
                                }
                                return result;
                              };

                              // Výpočet aktivit pro dny
                              const getActivitiesForDays = (companies, days) => {
                                return days.map(day => {
                                  return companies.filter(company => {
                                    if (!company.nextActionDate) return false;
                                    const actionDate = new Date(company.nextActionDate);
                                    return actionDate.toDateString() === day.toDateString();
                                  }).length;
                                });
                              };

                              const workingDays = calculateWorkingDays(new Date(), 9);
                              const activitiesData = getActivitiesForDays(companies, workingDays);

                              // Dny se schůzkou (stavy schůzky)
                              const MEETING_STATUSES = ['Schůzka domluvena', 'Naplánovaná schůzka'];
                              const meetingDays = new Set();
                              (companies || []).forEach(c => {
                                if (MEETING_STATUSES.includes(c.status) && c.nextActionDate) {
                                  const d = new Date(c.nextActionDate);
                                  meetingDays.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
                                }
                              });

                              const getDayName = (date) => {
                                const days = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
                                return days[date.getDay()];
                              };

                              const formatDateForComparison = (date) => {
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                return `${year}-${month}-${day}`;
                              };

                              const selectedDateFormatted = activityDate || null;
                              const maxActivities = Math.max(...activitiesData, 1);
                              
                              // Dynamická výška kontejneru: nejvyšší sloupec + prostor pro text + rezerva
                              const maxBarHeight = Math.max((maxActivities / maxActivities) * 50, 6); // zmenšená max výška sloupce
                              const textSpace = 32; // zmenšený prostor pro text dne a data
                              const reserve = 8; // menší rezerva
                              const containerHeight = maxBarHeight + textSpace + reserve;

                              return (
                                <div className="flex items-end gap-0.5" style={{ height: `${containerHeight}px` }}>
                                  {workingDays.map((day, index) => {
                                    const isSelected = selectedDateFormatted === formatDateForComparison(day);
                                    const activityCount = activitiesData[index];
                                    const height = Math.max((activityCount / maxActivities) * 50, 6); // zmenšená výška sloupců

                                    const dayStr = formatDateForComparison(day);
                                    const isMyDayOff = myDayOffs.has(dayStr);
                                    const hasMeeting = meetingDays.has(`${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`);

                                    // Barevné schéma podle počtu aktivit
                                    let colorClass = '';
                                    if (isMyDayOff) {
                                      colorClass = isSelected || !selectedDateFormatted
                                        ? 'bg-amber-500 hover:bg-amber-600'
                                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500';
                                    } else if (activityCount <= 40) {
                                      colorClass = isSelected || !selectedDateFormatted 
                                        ? 'bg-green-500 hover:bg-green-600' 
                                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500';
                                    } else if (activityCount <= 60) {
                                      colorClass = isSelected || !selectedDateFormatted 
                                        ? 'bg-yellow-500 hover:bg-yellow-600' 
                                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500';
                                    } else if (activityCount <= 80) {
                                      colorClass = isSelected || !selectedDateFormatted 
                                        ? 'bg-orange-500 hover:bg-orange-600' 
                                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500';
                                    } else {
                                      colorClass = isSelected || !selectedDateFormatted 
                                        ? 'bg-red-500 hover:bg-red-600' 
                                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500';
                                    }

                                    // Zjisti, zda je aktuální den pondělí a předchozí byl pátek
                                    const isMonday = day.getDay() === 1;
                                    const shouldShowSeparator = index > 0 && isMonday;

                                    return (
                                      <React.Fragment key={index}>
                                        {/* Vertikální oddělovač mezi týdny */}
                                        {shouldShowSeparator && (
                                          <div className="flex flex-col items-center justify-end px-0.5" style={{ height: `${containerHeight}px` }}>
                                            <div className="w-px bg-gray-300 dark:bg-gray-500 opacity-30" style={{ height: `${containerHeight - 5}px` }}></div>
                                          </div>
                                        )}
                                        
                                        <div className="flex flex-col items-center justify-end flex-1" style={{ height: `${containerHeight}px` }}>
                                          <div className={`text-xs mb-0.5 ${
                                            isSelected || !selectedDateFormatted 
                                              ? 'text-gray-600 dark:text-gray-400' 
                                              : 'text-gray-400 dark:text-gray-600'
                                          }`} style={{ fontSize: '10px' }}>
                                            {getDayName(day)}
                                          </div>
                                          <div className={`text-xs mb-0.5 ${
                                            isSelected || !selectedDateFormatted 
                                              ? 'text-gray-500 dark:text-gray-500' 
                                              : 'text-gray-400 dark:text-gray-600'
                                          }`} style={{ fontSize: '9px' }}>
                                            {day.getDate()}.{day.getMonth() + 1}
                                          </div>
                                          <div className="flex-1 flex items-end w-full">
                                            <div 
                                              className={`relative cursor-pointer transition-all duration-200 rounded-t-sm w-full ${colorClass} ${
                                                isSelected ? 'transform scale-110 shadow-lg' : ''
                                              }`}
                                              style={{ 
                                                height: `${height}px`, 
                                                minHeight: '8px',
                                                opacity: isSelected || !selectedDateFormatted ? 1 : 0.5
                                              }}
                                              onClick={() => {
                                                const year = day.getFullYear();
                                                const month = String(day.getMonth() + 1).padStart(2, '0');
                                                const dayNum = String(day.getDate()).padStart(2, '0');
                                                const dateString = `${year}-${month}-${dayNum}`;
                                                setActivityDate(dateString);
                                                setIsEditingDate(false);
                                                
                                                // Nastavit kalendář na správný měsíc
                                                setCurrentMonth(day.getMonth());
                                                setCurrentYear(day.getFullYear());
                                              }}
                                              title={`${getDayName(day)} ${day.getDate()}.${day.getMonth() + 1}.${day.getFullYear()}: ${activityCount} aktivit${activityCount === 1 ? 'a' : activityCount < 5 ? 'y' : ''}${isMyDayOff ? ' · MOJE VOLNO' : ''}${hasMeeting ? ' · SCHŮZKA' : ''}`}
                                            >
                                              {hasMeeting && (
                                                <div
                                                  className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-purple-500 ring-2 ring-white dark:ring-gray-800"
                                                  title="Naplánovaná schůzka"
                                                ></div>
                                              )}
                                              <div className="absolute inset-0 flex items-center justify-center">
                                                <span className={`font-bold ${
                                                  isSelected || !selectedDateFormatted 
                                                    ? 'text-white' 
                                                    : 'text-gray-600 dark:text-gray-300'
                                                }`} style={{ fontSize: '10px' }}>
                                                  {isMyDayOff ? '🌴' : activityCount}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Dvousloupcové rozložení - rozšířený kalendář vlevo, užší čas vpravo */}
                        <div className="grid grid-cols-3 gap-4">
                          {/* Levý sloupec - Vlastní kalendář - zabírá 2/3 prostoru */}
                          <div className="col-span-2">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                              📅 Kalendář
                            </h4>
                            
                            {/* Navigace kalendáře */}
                            <div className="flex items-center justify-between mb-3">
                              <button
                                type="button"
                                onClick={() => navigateMonth('prev')}
                                className="p-1.5 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900 dark:hover:bg-blue-800 rounded-lg transition-colors border-2 border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-300 font-bold text-lg shadow-sm hover:shadow-md"
                                title="Předchozí měsíc"
                              >
                                ←
                              </button>
                              <div className="relative" ref={monthYearPickerRef}>
                                <button
                                  type="button"
                                  onClick={() => setShowMonthYearPicker(!showMonthYearPicker)}
                                  className="text-sm font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer flex items-center gap-1"
                                  title="Klikněte pro rychlý výběr měsíce a roku"
                                >
                                  {monthNames[currentMonth]} {currentYear}
                                  <span className="text-xs">▼</span>
                                </button>
                                
                                {/* Dropdown pro rychlý výběr měsíce a roku */}
                                {showMonthYearPicker && (
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 p-4 w-64">
                                    <div className="grid grid-cols-2 gap-4 mb-3">
                                      {/* Výběr měsíce */}
                                      <div>
                                        <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Měsíc</h5>
                                        <div className="grid grid-cols-3 gap-1 max-h-32 overflow-y-auto">
                                          {monthNames.map((monthName, monthIndex) => (
                                            <button
                                              key={monthIndex}
                                              type="button"
                                              onClick={() => {
                                                setCurrentMonth(monthIndex);
                                              }}
                                              className={`text-xs p-1.5 rounded transition-colors ${
                                                currentMonth === monthIndex
                                                  ? 'bg-blue-500 text-white'
                                                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                              }`}
                                            >
                                              {monthName.substring(0, 3)}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                      
                                      {/* Výběr roku */}
                                      <div>
                                        <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Rok</h5>
                                        <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto">
                                          {Array.from({ length: 8 }, (_, i) => {
                                            const year = new Date().getFullYear() + i;
                                            return (
                                              <button
                                                key={year}
                                                type="button"
                                                onClick={() => {
                                                  setCurrentYear(year);
                                                }}
                                                className={`text-xs p-1.5 rounded transition-colors ${
                                                  currentYear === year
                                                    ? 'bg-blue-500 text-white'
                                                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                                }`}
                                              >
                                                {year}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Tlačítka pro akci */}
                                    <div className="flex gap-2 border-t border-gray-200 dark:border-gray-600 pt-3">
                                      <button
                                        type="button"
                                        onClick={() => setShowMonthYearPicker(false)}
                                        className="flex-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors"
                                      >
                                        Zrušit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setShowMonthYearPicker(false)}
                                        className="flex-1 px-3 py-1.5 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                                      >
                                        Potvrdit
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => navigateMonth('next')}
                                className="p-1.5 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900 dark:hover:bg-blue-800 rounded-lg transition-colors border-2 border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-300 font-bold text-lg shadow-sm hover:shadow-md"
                                title="Další měsíc"
                              >
                                →
                              </button>
                            </div>

                            {/* Kalendářová mřížka - kompaktnější */}
                            <div className="grid grid-cols-7 gap-0.5">
                              {/* Hlavička s názvy dní */}
                              {dayNames.map((dayName) => (
                                <div key={dayName} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-0.5">
                                  {dayName}
                                </div>
                              ))}
                              
                              {/* Dny v měsíci - menší velikost pro kompaktnější kalendář */}
                              {generateCalendar(currentYear, currentMonth).map((day, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => {
                                    setActivityDate(day.dateString);
                                    setIsEditingDate(false);
                                  }}
                                  title={day.isMyDayOff ? 'Moje volno' : undefined}
                                  className={`
                                    text-center text-xs py-1.5 rounded transition-colors relative
                                    ${!day.isCurrentMonth 
                                      ? 'text-gray-300 dark:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700' 
                                      : day.isSelected
                                      ? 'bg-blue-500 text-white font-bold'
                                      : day.isMyDayOff
                                      ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-semibold hover:bg-amber-200 dark:hover:bg-amber-900/60'
                                      : day.isToday
                                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 font-semibold'
                                      : 'text-gray-900 dark:text-white hover:bg-blue-50 dark:hover:bg-blue-900/50'
                                    }
                                  `}
                                >
                                  {day.day}
                                  {day.isMyDayOff && day.isCurrentMonth && !day.isSelected && (
                                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[7px] leading-none">🌴</span>
                                  )}
                                </button>
                              ))}
                            </div>

                            {/* Odstraněno zobrazení vybraného data pro odstranění duplicity */}
                          </div>

                          {/* Pravý sloupec - Pole pro ruční zadání času + scrollovací seznam */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                              🕐 Čas
                            </h4>
                            
                            {/* Pole pro ruční zadání času */}
                            <div className="mb-3">
                              <input
                                type="time"
                                value={activityTime}
                                onChange={(e) => setActivityTime(e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                              />
                            </div>
                            
                            {/* Seznam časových slotů s barevným označením */}
                            <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                              {generateTimeSlots().filter(slot => {
                                const hour = parseInt(slot.split(':')[0]);
                                return hour >= 9 && hour <= 17;
                              }).map((timeSlot) => {
                                const busyCompanies = busySlotsMap[timeSlot] || [];
                                const isBusy = busyCompanies.length > 0;
                                const isSelected = timeSlot === activityTime;
                                return (
                                  <button
                                    key={timeSlot}
                                    type="button"
                                    onClick={() => setActivityTime(timeSlot)}
                                    className={`w-full text-left px-2 py-1 text-xs border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors flex items-center justify-between ${
                                      isSelected
                                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 font-semibold border-l-2 border-l-blue-500'
                                        : isBusy
                                        ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50'
                                        : activityDate
                                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40'
                                        : 'text-gray-900 dark:text-white hover:bg-blue-50 dark:hover:bg-blue-900/50 hover:text-blue-900 dark:hover:text-blue-100'
                                    }`}
                                  >
                                    <span>{timeSlot}</span>
                                    {isBusy && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setBusySlotModal({ timeSlot, companies: busyCompanies }); }}
                                        className="w-4 h-4 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 ml-1 transition-colors"
                                      >
                                        !
                                      </button>
                                    )}
                                  </button>
                                );
                              })}
                            </div>

                            {/* Odstraněno zobrazení vybraného času pro odstranění duplicity */}
                          </div>
                        </div>

                        {/* Ukazatel datumu, času a dne - zobrazuje se stále */}
                        <div className="p-3 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/30 dark:to-green-900/30 rounded-lg border border-gray-300 dark:border-gray-600">
                          <div className="text-center">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2">
                              <span>
                                {activityDate && activityTime 
                                  ? `📅 ${new Date(activityDate).toLocaleDateString('cs-CZ')} v ${activityTime}`
                                  : activityDate 
                                  ? `📅 ${new Date(activityDate).toLocaleDateString('cs-CZ')} - vyberte čas`
                                  : "📅 Vyberte datum a čas"
                                }
                              </span>
                              {activityDate && (
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  ({new Date(activityDate).toLocaleDateString('cs-CZ', { weekday: 'long' })})
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {activityTemplates.length > 0 && (
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm shrink-0">Šablony</span>
                  {activityTemplates.map(t => (
                    <button
                      key={t._id}
                      type="button"
                      onClick={() => applyTemplate(t)}
                      className="px-2 py-1 rounded-lg border text-xs font-medium transition-colors bg-white dark:bg-gray-700 border-teal-200 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:border-teal-400"
                      title={[t.status && `Stav: ${t.status}`, t.daysFromNow != null && `+${t.daysFromNow} dní`, t.description && t.description.slice(0, 60)].filter(Boolean).join(' · ')}
                    >
                      {t.name}
                    </button>
                  ))}
                  {previousActivityText !== null && (
                    <button
                      type="button"
                      onClick={undoTemplate}
                      className="ml-auto px-2 py-1 rounded-lg border text-xs font-medium transition-colors bg-white dark:bg-gray-700 border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30 hover:border-orange-400"
                      title="Vrátit původní text"
                    >
                      ↩ Zpět
                    </button>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between mb-1">
                <label className="block font-semibold text-gray-900 dark:text-gray-100 text-sm">
                  Popis aktivity
                </label>
                <div className="flex items-center gap-2">
                  {/* Variant ID input - vlevo od tlačítka */}
                  {showVariantIdInput && (
                    <input
                      type="text"
                      className="w-24 px-2 py-1 border border-blue-300 dark:border-blue-600 rounded bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-white text-sm"
                      placeholder="Variant ID..."
                      value={activityVariantId}
                      onChange={e => setActivityVariantId(e.target.value.replace(/\D/g, '').slice(0, 7))}
                      maxLength={7}
                    />
                  )}
                  {/* Ikonka pro Variant ID */}
                  <button
                    type="button"
                    onClick={() => setShowVariantIdInput(!showVariantIdInput)}
                    className={`${showVariantIdInput ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400'} transition-colors text-lg`}
                    title="Zadat Variant ID nabídky"
                  >
                    📋
                  </button>
                  {/* AI Robot */}
                  <button
                    type="button"
                    onClick={handleAIAssistance}
                    disabled={!activityText.trim()}
                    className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed transition-colors text-lg"
                    title="Odeslat do AI chatu pro analýzu a doporučení"
                  >
                    🤖
                  </button>
                  {/* Zvětšení popisu aktivity */}
                  <button
                    type="button"
                    onClick={() => setActivityDescExpanded(!activityDescExpanded)}
                    className={`transition-colors flex items-center justify-center ${activityDescExpanded ? 'text-orange-500 dark:text-orange-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                    title={activityDescExpanded ? 'Zmenšit popis aktivity' : 'Zvětšit popis aktivity'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      {activityDescExpanded ? (
                        <>
                          <polyline points="4 14 10 14 10 20" />
                          <polyline points="20 10 14 10 14 4" />
                          <line x1="10" y1="14" x2="3" y2="21" />
                          <line x1="21" y1="3" x2="14" y2="10" />
                        </>
                      ) : (
                        <>
                          <polyline points="15 3 21 3 21 9" />
                          <polyline points="9 21 3 21 3 15" />
                          <line x1="21" y1="3" x2="14" y2="10" />
                          <line x1="3" y1="21" x2="10" y2="14" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="mb-3">
                <textarea
                  ref={textAreaRef}
                  value={activityText}
                  onChange={handleActivityTextChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none overflow-y-auto"
                  style={{
                    height: activityDescExpanded
                      ? `${(screenHeight || 1024) < 900 ? '220px' : (screenHeight || 1024) <= 1080 ? '308px' : '460px'}`
                      : `${(screenHeight || 1024) < 900 ? '90px' : '120px'}`,
                    transition: 'height 0.35s ease'
                  }}
                  placeholder="Co bylo projednáno, co je potřeba udělat..."
                />
              </div>
              
              {/* Tlačítka vedle sebe */}
              <div className={`grid gap-3 pt-2 border-t border-gray-200 dark:border-gray-600 ${!company ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3'}`}>
                {!company ? (
                  // Tlačítka pro novou firmu
                  <>
                    <button
                      type="button"
                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-2 rounded-lg transition font-semibold text-sm flex items-center justify-center gap-2 shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                      onClick={() => handleSave("stay")}
                      disabled={!form.name || !form.ico || isLoading}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Uložit
                    </button>
                    <button
                      type="submit"
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg transition font-semibold text-sm flex items-center justify-center gap-2 shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                      disabled={!form.name || !form.ico || isLoading}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Uložit a zpět na seznam firem
                    </button>
                  </>
                ) : (
                  // Tlačítka pro editaci existující firmy
                  <>
                    {/* Běžná tlačítka pro všechny role */}
                    <button
                      type="button"
                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-2 rounded-lg transition font-semibold text-sm flex items-center justify-center gap-2 shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                      onClick={() => handleSave("stay")}
                      disabled={!isFormWithActivityValid()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Uložit
                    </button>
                    <button
                      type="submit"
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg transition font-semibold text-sm flex items-center justify-center gap-2 shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                      disabled={!isFormWithActivityValid()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Uložit a zpět na seznam
                    </button>
                    <button
                      type="button"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg transition font-semibold text-sm disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                      onClick={() => handleSave("next")}
                      disabled={!isFormWithActivityValid()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                      </svg>
                      Uložit a další firma
                    </button>

                    {/* Speciální tlačítka pro callcentrum - dynamicky podle konfigurace */}
                    {userRole === "callcentrum" && (
                      <>
                        {loadingCallCenterConfig ? (
                          <div className="w-full col-span-3 text-center py-4 text-gray-500">
                            Načítání konfigurace...
                          </div>
                        ) : (
                          <>
                            {activeCallCenterButtons
                              ?.map((buttonConfig, index) => {
                                const colorMap = {
                                  purple: { base: 'purple-500', hover: 'purple-600', darker: 'purple-700' },
                                  blue: { base: 'blue-500', hover: 'blue-600', darker: 'blue-700' },
                                  green: { base: 'green-500', hover: 'green-600', darker: 'green-700' },
                                  red: { base: 'red-500', hover: 'red-600', darker: 'red-700' },
                                  orange: { base: 'orange-500', hover: 'orange-600', darker: 'orange-700' },
                                  pink: { base: 'pink-500', hover: 'pink-600', darker: 'pink-700' },
                                  indigo: { base: 'indigo-500', hover: 'indigo-600', darker: 'indigo-700' }
                                };
                                
                                const colors = colorMap[buttonConfig.color] || colorMap.purple;
                                
                                return (
                                  <React.Fragment key={buttonConfig._id}>
                                    {/* Popis tlačítka - přes všechny 3 sloupce */}
                                    <div className="col-span-3 text-xs font-semibold text-gray-600 dark:text-gray-400 mt-2">
                                      {buttonConfig.description}
                                    </div>
                                    
                                    {/* Tři tlačítka vedle sebe - každé v jednom sloupci */}
                                    <button
                                      type="button"
                                      className="w-full text-white py-2 rounded-lg transition font-semibold text-sm flex items-center justify-center gap-2 shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                                      style={{
                                        backgroundColor: `var(--color-${buttonConfig.color}-500, #8b5cf6)`,
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = `var(--color-${buttonConfig.color}-600, #7c3aed)`;
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = `var(--color-${buttonConfig.color}-500, #8b5cf6)`;
                                      }}
                                      onClick={() => handleSendLead("stay", buttonConfig)}
                                      disabled={!isFormWithActivityValid()}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Odeslat
                                    </button>
                                    
                                    <button
                                      type="button"
                                      className="w-full text-white py-2 rounded-lg transition font-semibold text-sm flex items-center justify-center gap-2 shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                                      style={{
                                        backgroundColor: `var(--color-${buttonConfig.color}-600, #7c3aed)`,
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = `var(--color-${buttonConfig.color}-700, #6d28d9)`;
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = `var(--color-${buttonConfig.color}-600, #7c3aed)`;
                                      }}
                                      onClick={() => handleSendLead("list", buttonConfig)}
                                      disabled={!isFormWithActivityValid()}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Odeslat a zpět na seznam
                                    </button>
                                    
                                    <button
                                      type="button"
                                      className="w-full text-white py-2 rounded-lg transition font-semibold text-sm disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                                      style={{
                                        backgroundColor: `var(--color-${buttonConfig.color}-700, #6d28d9)`,
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = `var(--color-${buttonConfig.color}-800, #5b21b6)`;
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = `var(--color-${buttonConfig.color}-700, #6d28d9)`;
                                      }}
                                      onClick={() => handleSendLead("next", buttonConfig)}
                                      disabled={!isFormWithActivityValid()}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                                      </svg>
                                      Odeslat a další firma
                                    </button>
                                  </React.Fragment>
                                );
                              })}

                            {activeCallCenterButtons.length > 0 && (
                              <>
                                <div className="col-span-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                  <div className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-2">
                                    Manuální předání leadu
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Za jakou hodnotu</label>
                                      <select
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                        value={manualButtonId}
                                        onChange={(e) => setManualButtonId(e.target.value)}
                                      >
                                        {activeCallCenterButtons.map(btn => (
                                          <option key={btn._id} value={btn._id}>
                                            {btn.description} ({btn.value || 0} Kč)
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    <div className="relative" ref={manualAssigneeDropdownRef}>
                                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Komu předat</label>
                                      <button
                                        type="button"
                                        onClick={() => { setShowManualAssigneeDropdown(v => !v); setManualAssigneeSearch(''); }}
                                        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between ${
                                          isDarkMode
                                            ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                                            : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                                        }`}
                                      >
                                        <span className="truncate">
                                          {(users || []).find(u => String(u._id) === String(manualAssigneeId))?.name || 'Vyberte obchodníka'}
                                        </span>
                                        <span className="ml-1 flex-shrink-0 text-gray-400">▾</span>
                                      </button>

                                      {showManualAssigneeDropdown && (
                                        <div className={`absolute bottom-full left-0 w-full z-50 mb-1 border rounded-lg shadow-xl ${
                                          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
                                        }`}>
                                          <div className={`p-2 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                            <input
                                              type="text"
                                              placeholder="🔍 Hledat obchodníka..."
                                              value={manualAssigneeSearch}
                                              onChange={e => setManualAssigneeSearch(e.target.value)}
                                              autoFocus
                                              className={`w-full px-2 py-1.5 text-sm border rounded ${
                                                isDarkMode
                                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                  : 'bg-white border-gray-300 text-gray-900'
                                              }`}
                                            />
                                          </div>

                                          <div className="max-h-56 overflow-y-auto">
                                            {groupOwnersByRole((users || []).filter(u => u.role !== 'callcentrum'), manualAssigneeSearch).map(({ role, label, users: roleUsers }) => (
                                              <div key={role}>
                                                <div className={`px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                                                  isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                                                }`}>
                                                  {label} ({roleUsers.length})
                                                </div>
                                                {roleUsers.map(user => {
                                                  const isSelected = String(user._id) === String(manualAssigneeId);
                                                  return (
                                                    <button
                                                      key={user._id}
                                                      type="button"
                                                      onClick={() => {
                                                        setManualAssigneeId(user._id);
                                                        setShowManualAssigneeDropdown(false);
                                                        setManualAssigneeSearch('');
                                                      }}
                                                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                                        isSelected
                                                          ? isDarkMode ? 'bg-blue-900/50 text-blue-200 font-semibold' : 'bg-blue-100 text-blue-900 font-semibold'
                                                          : isDarkMode ? 'text-white hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-100'
                                                      }`}
                                                    >
                                                      {user.name || user.username}
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                            ))}

                                            {manualAssigneeSearch && groupOwnersByRole((users || []).filter(u => u.role !== 'callcentrum'), manualAssigneeSearch).length === 0 && (
                                              <div className={`px-3 py-3 text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Žádný obchodník nenalezen
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    className="mt-3 w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg transition font-semibold text-sm flex items-center justify-center gap-2 shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                                    onClick={handleManualLeadTransfer}
                                    disabled={!isFormWithActivityValid() || !manualButtonId || !manualAssigneeId}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Manuálně předat lead
                                  </button>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Modální okno pro propojení příbuzných firem */}
      {showRelatedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                🔗 Propojení s příbuznými firmami
              </h3>
              <button
                onClick={() => setShowRelatedModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl"
              >
                ×
              </button>
            </div>

            {/* Vyhledávací pole */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                Vyhledat firmu (název nebo IČO):
              </label>
              <input
                type="text"
                value={relatedSearch}
                onChange={(e) => {
                  console.log('🔍 Input onChange triggered, nová hodnota:', e.target.value);
                  setRelatedSearch(e.target.value);
                }}
                placeholder="Zadejte název firmy nebo IČO..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                autoFocus
              />
            </div>

            {/* Výsledky vyhledávání */}
            {searchResults.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                  Výsledky vyhledávání:
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {searchResults.map(company => (
                    <div
                      key={company._id}
                      className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {company.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          IČO: {company.ico} • {company.status}
                        </div>
                      </div>
                      <button
                        onClick={() => addRelatedCompany(company)}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                        disabled={relatedCompanies.find(rel => rel._id === company._id)}
                      >
                        {relatedCompanies.find(rel => rel._id === company._id) ? '✓ Přidáno' : '+ Přidat'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Současné propojené firmy */}
            {relatedCompanies.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                  Propojené firmy:
                </h4>
                <div className="space-y-2">
                  {relatedCompanies.map(company => (
                    <div
                      key={company._id}
                      className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded"
                    >
                      <button
                        onClick={() => window.open(`/companies/${company._id}`, '_blank')}
                        className="flex-1 text-left hover:bg-blue-100 dark:hover:bg-blue-800/30 rounded p-1 transition-colors"
                        title="Otevřít firmu v nové kartě"
                      >
                        <div className="font-medium text-blue-800 dark:text-blue-200">
                          {company.name}
                        </div>
                        <div className="text-sm text-blue-600 dark:text-blue-300">
                          IČO: {company.ico} • {company.status}
                        </div>
                      </button>
                      <button
                        onClick={() => removeRelatedCompany(company._id)}
                        className="text-red-500 hover:text-red-700 text-lg ml-2"
                        title="Odebrat propojení"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tlačítka */}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowRelatedModal(false)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                Hotovo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pro Přeblokování byl odstraněn – přeblokace nyní probíhá přes tlačítko "Přeblokovat" (automatizace + extension). Starý obsah ponechán skrytý (nikdy se nezobrazí). */}
      {false && showPreblokovaniModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[95vw] h-[90vh] flex flex-col">
            {/* Header */}
            <div className="bg-purple-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                <h3 className="text-xl font-bold">Přeblokování zákazníka</h3>
              </div>
              <button
                onClick={() => setShowPreblokovaniModal(false)}
                className="text-white hover:bg-purple-700 rounded-lg p-1 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body - dva sloupce */}
            <div className="flex-1 flex overflow-hidden">
              {/* Levá strana - Údaje k přepsání */}
              <div className="w-1/3 p-6 space-y-4 overflow-y-auto border-r border-gray-200 dark:border-gray-700">
                <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-4 mb-4">
                  <p className="text-sm text-purple-800 dark:text-purple-200 font-semibold">
                    📋 Klikněte na tlačítka pro kopírování údajů
                  </p>
                </div>

                {/* IČO zákazníka */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                  <label className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-2 block uppercase tracking-wide">
                    IČO zákazníka
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold font-mono bg-white dark:bg-gray-800 px-4 py-3 rounded-lg border border-purple-300 dark:border-purple-600 flex-1 text-center tracking-wider text-gray-900 dark:text-gray-100 select-all">
                      {form.ico?.padStart(8, '0')}
                    </div>
                    <button
                      onClick={() => handleCopyToClipboard(form.ico?.padStart(8, '0'), 'ico')}
                      className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all flex-shrink-0 ${
                        copiedField === 'ico'
                          ? 'bg-green-500 text-white'
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}
                    >
                      {copiedField === 'ico' ? '✓ Zkopírováno' : '📋 Kopírovat'}
                    </button>
                  </div>
                </div>

                {/* CA zákazníka */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                  <label className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-2 block uppercase tracking-wide">
                    CA zákazníka
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold font-mono bg-white dark:bg-gray-800 px-4 py-3 rounded-lg border border-purple-300 dark:border-purple-600 flex-1 text-center tracking-wider text-gray-900 dark:text-gray-100 select-all">
                      {form.ca}
                    </div>
                    <button
                      onClick={() => handleCopyToClipboard(form.ca, 'ca')}
                      className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all flex-shrink-0 ${
                        copiedField === 'ca'
                          ? 'bg-green-500 text-white'
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}
                    >
                      {copiedField === 'ca' ? '✓ Zkopírováno' : '📋 Kopírovat'}
                    </button>
                  </div>
                </div>

                {/* Původní vlastník (PRM) */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                  <label className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-2 block uppercase tracking-wide">
                    Původní vlastník
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold font-mono bg-white dark:bg-gray-800 px-4 py-3 rounded-lg border border-purple-300 dark:border-purple-600 flex-1 text-center tracking-wider text-gray-900 dark:text-gray-100 select-all">
                      {form.prm}
                    </div>
                    <button
                      onClick={() => handleCopyToClipboard(form.prm, 'prm')}
                      className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all flex-shrink-0 ${
                        copiedField === 'prm'
                          ? 'bg-green-500 text-white'
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}
                    >
                      {copiedField === 'prm' ? '✓ Zkopírováno' : '📋 Kopírovat'}
                    </button>
                  </div>
                </div>

                {/* Požadovaný vlastník (VF Login) */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                  <label className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-2 block uppercase tracking-wide">
                    Požadovaný vlastník
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold font-mono bg-white dark:bg-gray-800 px-4 py-3 rounded-lg border border-purple-300 dark:border-purple-600 flex-1 text-center tracking-wider text-gray-900 dark:text-gray-100 select-all">
                      {users.find(u => u._id === userId)?.vfLogin || 'Neuvedeno'}
                    </div>
                    <button
                      onClick={() => {
                        const currentUser = users.find(u => u._id === userId);
                        const vfLogin = currentUser?.vfLogin || '';
                        handleCopyToClipboard(vfLogin, 'vfLogin');
                      }}
                      className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all flex-shrink-0 ${
                        copiedField === 'vfLogin'
                          ? 'bg-green-500 text-white'
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}
                    >
                      {copiedField === 'vfLogin' ? '✓ Zkopírováno' : '📋 Kopírovat'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Pravá strana - iFrame + Email mustr */}
              <div className="w-2/3 bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden">
                {/* PowerApps iFrame */}
                <div className="flex flex-col border-b border-gray-300 dark:border-gray-600 flex-shrink-0" style={{ height: '320px' }}>
                  <div className="bg-indigo-600 text-white px-3 py-2 text-xs font-semibold flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <span>⚡</span>
                      <span>Přeblokační formulář (PowerApps)</span>
                    </div>
                    <a
                      href="https://apps.powerapps.com/play/e/default-68283f3b-8487-4c86-adb3-a5228f18b893/a/32f8c771-c3ee-4c46-a691-335c98725914?tenantId=68283f3b-8487-4c86-adb3-a5228f18b893&hint=e8908d19-d616-4fd4-9f1f-272ad6ac229a&source=sharebutton&sourcetime=1781019302930"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold transition-colors"
                    >
                      ↗️ Otevřít v novém okně
                    </a>
                  </div>
                  <iframe
                    src="https://apps.powerapps.com/play/e/default-68283f3b-8487-4c86-adb3-a5228f18b893/a/32f8c771-c3ee-4c46-a691-335c98725914?tenantId=68283f3b-8487-4c86-adb3-a5228f18b893&hint=e8908d19-d616-4fd4-9f1f-272ad6ac229a&source=sharebutton&sourcetime=1781019302930"
                    style={{ flex: 1, width: '100%', border: 'none', minHeight: 0 }}
                    title="Přeblokační formulář"
                    allow="fullscreen"
                  />
                </div>

                {/* Email mustr – zbytek */}
                <div className="flex flex-col flex-1 min-h-0">
                {/* Ovládací panel */}
                <div className="bg-purple-600 text-white p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="font-semibold">Mustr emailu – žádost o uvolnění</span>
                  </div>
                  <button
                    onClick={() => {
                      window.open(
                        'https://vodafone.sharepoint.com/sites/indirect_business_field/_layouts/15/FormServer.aspx?XsnLocation=/sites/indirect_business_field/DB_preblokaci_IBF/Forms/x_template.xsn',
                        '_blank',
                        'width=1200,height=800'
                      );
                    }}
                    className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold transition-colors"
                  >
                    ↗️ Odkaz na přeblokační formulář
                  </button>
                </div>

                {/* Email mustr obsah */}
                <div className="flex-1 p-6 space-y-5 overflow-y-auto">
                  {/* Předmět – editovatelný */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-purple-200 dark:border-purple-700 p-4">
                    <label className="text-xs font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wide mb-3 block">
                      📧 Předmět emailu
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={editableSubject}
                        onChange={e => setEditableSubject(e.target.value)}
                        className="flex-1 text-sm border border-purple-300 dark:border-purple-600 rounded-lg px-3 py-2 bg-purple-50 dark:bg-purple-900/20 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      />
                      <button
                        onClick={() => handleCopyToClipboard(editableSubject, 'emailSubject')}
                        className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                          copiedField === 'emailSubject' ? 'bg-green-500 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white'
                        }`}
                      >
                        {copiedField === 'emailSubject' ? '✓' : '📋'}
                      </button>
                    </div>
                  </div>

                  {/* Text emailu – editovatelný s formátovacím toolbarem */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-purple-200 dark:border-purple-700 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wide">
                        📝 Text emailu
                      </label>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => setEmailBodyViewMode('edit')}
                          className={`px-2 py-1 text-xs rounded border transition-colors ${
                            emailBodyViewMode === 'edit' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50'
                          }`}>
                          ✏️ Upravit
                        </button>
                        <button type="button" onClick={() => setEmailBodyViewMode('preview')}
                          className={`px-2 py-1 text-xs rounded border transition-colors ${
                            emailBodyViewMode === 'preview' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50'
                          }`}>
                          👁 Náhled
                        </button>
                      </div>
                    </div>

                    {/* Formátovací toolbar */}
                    <div className="flex items-center gap-1 mb-2 px-2 py-1 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 flex-wrap">
                      <span className="text-xs text-gray-400 mr-1">Formát:</span>
                      <button type="button" title="Tučný (**text**)" onClick={() => wrapSelection('**', editableBodyRef, setEditableBody)}
                        className="px-2 py-0.5 text-xs font-bold border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200">
                        B
                      </button>
                      <button type="button" title="Kurzíva (*text*)" onClick={() => wrapSelection('*', editableBodyRef, setEditableBody)}
                        className="px-2 py-0.5 text-xs italic border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200">
                        I
                      </button>
                      <button type="button" title="Podtržení (__text__)" onClick={() => wrapSelection('__', editableBodyRef, setEditableBody)}
                        className="px-2 py-0.5 text-xs underline border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200">
                        U
                      </button>
                      <button type="button" title="Zvýraznění (==text==)" onClick={() => wrapSelection('==', editableBodyRef, setEditableBody)}
                        className="px-2 py-0.5 text-xs border border-yellow-400 rounded bg-yellow-200 hover:bg-yellow-300 dark:bg-yellow-700 dark:hover:bg-yellow-600 text-yellow-900 dark:text-yellow-100">
                        🔦
                      </button>
                      <span className="ml-1 text-xs text-gray-400">Vyberte text + klikněte</span>
                    </div>

                    {emailBodyViewMode === 'edit' ? (
                      <textarea
                        ref={editableBodyRef}
                        rows={10}
                        value={editableBody}
                        onChange={e => setEditableBody(e.target.value)}
                        className="w-full text-sm border border-purple-300 dark:border-purple-600 rounded-lg px-3 py-3 bg-purple-50 dark:bg-purple-900/20 text-gray-900 dark:text-gray-100 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-purple-400 mb-3 font-mono"
                      />
                    ) : (
                      <div
                        className="w-full min-h-[14rem] text-sm border border-purple-300 dark:border-purple-600 rounded-lg px-4 py-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 leading-relaxed mb-3 overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: markdownToHtml(editableBody) }}
                      />
                    )}

                    <button
                      onClick={() => handleCopyToClipboard(editableBody, 'emailBody')}
                      className={`w-full py-2 rounded-lg text-sm font-semibold transition-all ${
                        copiedField === 'emailBody' ? 'bg-green-500 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}
                    >
                      {copiedField === 'emailBody' ? '✓ Text zkopírován' : '📋 Kopírovat text emailu'}
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleSendPreblokaceEmail}
                    disabled={preblokaceEmailSending}
                    className={`w-full py-3 rounded-lg text-sm font-semibold transition-all ${
                      preblokaceEmailSending
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {preblokaceEmailSending ? 'Odesílám...' : '✉️ Odeslat žádost emailem'}
                  </button>

                  {preblokaceSendMessage && (
                    <div className="text-sm rounded-lg px-3 py-2 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border border-green-300 dark:border-green-700">
                      ✅ {preblokaceSendMessage}
                    </div>
                  )}

                  {preblokaceSendError && (
                    <div className="text-sm rounded-lg px-3 py-2 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 border border-red-300 dark:border-red-700">
                      ❌ {preblokaceSendError}
                    </div>
                  )}

                  {isAdminUser && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-purple-200 dark:border-purple-700 p-4 space-y-3">
                      <label className="text-xs font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wide block">
                        ⚙️ Admin nastavení (pro všechny uživatele)
                      </label>

                      {preblokaceSettingsLoading ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Načítám nastavení...</p>
                      ) : (
                        <>
                          {/* Šablona předmětu */}
                          <div>
                            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block">
                              Šablona předmětu
                            </label>
                            <input
                              type="text"
                              value={adminSubjectTemplate}
                              onChange={e => setAdminSubjectTemplate(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                          </div>

                          {/* Šablona těla */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                Šablona textu emailu
                              </label>
                            </div>
                            {/* Toolbar pro šablonu */}
                            <div className="flex items-center gap-1 mb-1 px-2 py-1 bg-gray-100 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 flex-wrap">
                              <span className="text-xs text-gray-400 mr-1">Formát:</span>
                              <button type="button" title="Tučný (**text**)" onClick={() => wrapSelection('**', adminBodyTemplateRef, setAdminBodyTemplate)}
                                className="px-2 py-0.5 text-xs font-bold border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200">
                                B
                              </button>
                              <button type="button" title="Kurzíva (*text*)" onClick={() => wrapSelection('*', adminBodyTemplateRef, setAdminBodyTemplate)}
                                className="px-2 py-0.5 text-xs italic border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200">
                                I
                              </button>
                              <button type="button" title="Podtržení (__text__)" onClick={() => wrapSelection('__', adminBodyTemplateRef, setAdminBodyTemplate)}
                                className="px-2 py-0.5 text-xs underline border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200">
                                U
                              </button>
                              <button type="button" title="Zvýraznění (==text==)" onClick={() => wrapSelection('==', adminBodyTemplateRef, setAdminBodyTemplate)}
                                className="px-2 py-0.5 text-xs border border-yellow-400 rounded bg-yellow-200 hover:bg-yellow-300 dark:bg-yellow-700 dark:hover:bg-yellow-600 text-yellow-900 dark:text-yellow-100">
                                🔦
                              </button>
                            </div>
                            <textarea
                              ref={adminBodyTemplateRef}
                              rows={8}
                              value={adminBodyTemplate}
                              onChange={e => setAdminBodyTemplate(e.target.value)}
                              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-y focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                          </div>

                          {/* Nápověda k proměnným */}
                          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded p-2 space-y-1">
                            <p className="font-semibold mb-1">Dostupné proměnné:</p>
                            <p><code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">{'{{companyName}}'}</code> – název klienta</p>
                            <p><code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">{'{{ico}}'}</code> – IČO klienta</p>
                            <p><code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">{'{{ca}}'}</code> – číslo zákazníka (CA)</p>
                            <p><code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">{'{{prm}}'}</code> – původní vlastník</p>
                            <p><code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">{'{{senderName}}'}</code> – jméno odesílatele</p>
                          </div>

                          {/* Příjemci */}
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                            Volitelní příjemci (povinný je vždy email odesílatele):
                          </p>
                          <input
                            type="email"
                            placeholder="Příjemce 2 (volitelný)"
                            value={preblokaceSettings.recipient2}
                            onChange={(e) => setPreblokaceSettings(prev => ({ ...prev, recipient2: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <input
                            type="email"
                            placeholder="Příjemce 3 (volitelný)"
                            value={preblokaceSettings.recipient3}
                            onChange={(e) => setPreblokaceSettings(prev => ({ ...prev, recipient3: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <input
                            type="email"
                            placeholder="Příjemce 4 (volitelný)"
                            value={preblokaceSettings.recipient4}
                            onChange={(e) => setPreblokaceSettings(prev => ({ ...prev, recipient4: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />

                          <button
                            type="button"
                            onClick={handleSavePreblokaceSettings}
                            disabled={preblokaceSettingsSaving}
                            className={`w-full py-2 rounded-lg text-sm font-semibold transition-all ${
                              preblokaceSettingsSaving
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'bg-purple-600 hover:bg-purple-700 text-white'
                            }`}
                          >
                            {preblokaceSettingsSaving ? 'Ukládám...' : '💾 Uložit nastavení pro všechny'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                </div>{/* konec Email mustr */}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 rounded-b-lg flex justify-end border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={() => setShowPreblokovaniModal(false)}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal - žádost o uvolnění klienta */}
      {showUvolnitModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[110] p-4"
          onClick={() => { if (!uvolnitEmailSending) setShowUvolnitModal(false); }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-amber-500 rounded-t-xl">
              <div className="flex items-center gap-2 text-white">
                <span className="text-xl">✉️</span>
                <div>
                  <h3 className="font-semibold text-base leading-tight">Odeslat žádost o uvolnění</h3>
                  <p className="text-green-100 text-xs">{form.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowUvolnitModal(false)}
                disabled={uvolnitEmailSending}
                className="text-white/80 hover:text-white text-xl leading-none disabled:opacity-40"
              >✕</button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 overflow-y-auto max-h-[80vh]">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Zkontrolujte obsah emailu a potvrďte odeslání.
              </p>

              {/* Předmět */}
              <div>
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Předmět</label>
                <input
                  type="text"
                  value={editableSubject}
                  onChange={e => setEditableSubject(e.target.value)}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
              </div>

              {/* Tělo emailu – editovatelné + preview */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Text emailu</label>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => setEmailBodyViewMode('edit')}
                      className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                        emailBodyViewMode === 'edit' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50'
                      }`}>
                      ✏️ Upravit
                    </button>
                    <button type="button" onClick={() => setEmailBodyViewMode('preview')}
                      className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                        emailBodyViewMode === 'preview' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50'
                      }`}>
                      👁 Náhled
                    </button>
                  </div>
                </div>
                {emailBodyViewMode === 'edit' ? (
                  <textarea
                    rows={12}
                    value={editableBody}
                    onChange={e => setEditableBody(e.target.value)}
                    className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-3 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-amber-400 font-mono"
                  />
                ) : (
                  <div
                    className="w-full max-h-96 overflow-y-auto text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: markdownToHtml(editableBody) }}
                  />
                )}
              </div>

              {uvolnitSendMessage && (
                <div className="text-sm rounded-lg px-3 py-2 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border border-green-300 dark:border-green-700">
                  ✅ {uvolnitSendMessage}
                </div>
              )}
              {uvolnitSendError && (
                <div className="text-sm rounded-lg px-3 py-2 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 border border-red-300 dark:border-red-700">
                  ❌ {uvolnitSendError}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-800 rounded-b-xl">
              <button
                type="button"
                onClick={() => setShowUvolnitModal(false)}
                disabled={uvolnitEmailSending}
                className="px-5 py-2 rounded-lg text-sm font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40"
              >
                Zrušit
              </button>
              <button
                type="button"
                onClick={handleSendUvolnitEmail}
                disabled={uvolnitEmailSending}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  uvolnitEmailSending
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {uvolnitEmailSending ? 'Odesílám...' : '✉️ Odeslat žádost'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal – PB test (automatická přeblokace) */}
      {showPbTestModal && (() => {
        const vfLogin = users.find(u => u._id === userId)?.vfLogin || '';
        const ico = form.ico?.padStart(8, '0') || form.ico || '';
        const fields = [
          { num: 1, label: 'IČO',                value: ico,                    hint: 'zadejte max. 8 číslic' },
          { num: 2, label: 'CA',                 value: form.ca || '',          hint: 'musí obsahovat přesně 10 číslic' },
          { num: 3, label: 'Původní Vlastník',   value: form.prm || '',         hint: 'Username původního vlastníka' },
          { num: 4, label: 'Požadovaný Vlastník', value: vfLogin,               hint: 'Username na které přeblokovat' },
          { num: 5, label: 'Přeblokační Scénář', value: 'A (bez platné P)',     hint: 'vybrat z roletky' },
        ];

        const isRunning = pbExtensionActive && !['idle','ready','completed','error'].includes(pbTestStep) && !pbTestStep.startsWith('copied');
        const isDone = pbTestStep === 'completed';
        // Porovnání verze – upozornění při zastaralé verzi rozšíření
        const pbExtVersionOutdated = (() => {
          if (!pbExtensionActive || !pbExtensionVersion) return false;
          const v = pbExtensionVersion.split('.').map(Number);
          const m = [2, 3, 0];
          for (let i = 0; i < 3; i++) {
            if ((v[i]||0) < (m[i]||0)) return true;
            if ((v[i]||0) > (m[i]||0)) return false;
          }
          return false;
        })();

        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[120] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[96vw] h-[92vh] flex flex-col">

              {/* Header */}
              <div className={`text-white px-5 py-3 rounded-t-xl flex items-center justify-between flex-shrink-0 ${pbExtensionActive ? 'bg-cyan-700' : 'bg-gray-600'}`}>
                <div className="flex items-center gap-3">
                  <span className="text-lg">{isDone ? '✅' : '⚡'}</span>
                  <div>
                    <h3 className="font-bold text-base leading-tight">Přeblokace</h3>
                    <p className="text-xs text-cyan-200">
                      {pbExtensionActive
                        ? isDone ? 'Přeblokace dokončena!'
                          : pbDecisionNeeded ? 'Kontrola vlastníka v Siebelu'
                          : isRunning ? 'Extension běží...'
                          : 'Extension aktivní – klikni Spustit'
                        : 'Extension není nainstalována – návod níže'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {pbExtensionActive && !isDone && (
                    <button
                      onClick={pbStartAutomation}
                      disabled={isRunning}
                      className="px-3 py-1.5 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded text-xs font-semibold transition"
                    >
                      {isRunning ? '⏳ Běží...' : '▶ Spustit'}
                    </button>
                  )}
                  <button
                    onClick={() => setShowPbTestModal(false)}
                    className="p-1 hover:bg-white/20 rounded transition"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 flex overflow-hidden min-h-0">

                {/* ── Levý panel ────────────────────────────────────────── */}
                <div className="w-72 flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-900">

                  {/* Extension stav (jen když aktivní – stažení/návod je vpravo) */}
                  {pbExtensionActive && (
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-cyan-50 dark:bg-cyan-900/20">
                      <div className="flex items-center gap-2">
                        <span className="text-green-500 text-lg">●</span>
                        <div>
                          <p className="text-xs font-bold text-cyan-800 dark:text-cyan-300">Extension aktivní {pbExtensionVersion && <span className="font-normal opacity-70">v{pbExtensionVersion}</span>}</p>
                          <p className="text-[11px] text-cyan-600 dark:text-cyan-400">Siebel + PowerApps automaticky</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Upozornění na zastaralou verzi rozšíření */}
                  {pbExtVersionOutdated && (
                    <div className="p-3 border-b border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/30">
                      <p className="text-xs font-bold text-amber-800 dark:text-amber-300">⚠️ Stará verze rozšíření</p>
                      <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">
                        Máte v{pbExtensionVersion}, aktuální je v2.3.0. Pro správnou funkci odstraňte starou verzi a nainstalujte novou.
                      </p>
                      <a
                        href="/pb-extension.zip"
                        download="pb-extension.zip"
                        className="inline-block mt-2 text-[11px] font-bold text-white bg-amber-600 hover:bg-amber-700 px-2 py-1 rounded transition"
                      >
                        ⬇ Stáhnout v2.3.0
                      </a>
                    </div>
                  )}

                  {/* Výběr zákazníka při více výsledcích */}
                  {pbTestMultiple.length > 0 && (
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-bold text-amber-700 dark:text-amber-400 mb-2">
                        Nalezeno {pbTestMultiple.length} zákazníků – vyberte správný:
                      </p>
                      {pbTestMultiple.map(acc => (
                        <button
                          key={acc.index}
                          type="button"
                          onClick={() => {
                            window.postMessage({ type: 'SIEBEL_SELECT', index: acc.index }, '*');
                            setPbTestMultiple([]);
                          }}
                          className="w-full text-left text-xs px-2 py-2 mb-1 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 transition font-medium"
                        >
                          {acc.name}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Data z karty */}
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-1.5">
                    <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {pbExtensionActive ? 'Data která se vyplní' : 'Klikni → zkopíruj → vlož'}
                    </p>
                    {fields.map(f => (
                      <button
                        key={f.num}
                        type="button"
                        disabled={pbExtensionActive}
                        onClick={() => {
                          if (!f.value || pbExtensionActive) return;
                          navigator.clipboard.writeText(f.value).catch(() => {});
                          setPbTestStep(`copied-${f.num}`);
                          setTimeout(() => setPbTestStep('ready'), 1500);
                        }}
                        className={`w-full text-left rounded-xl border-2 px-3 py-2 transition-all group
                          ${pbTestStep === `copied-${f.num}`
                            ? 'border-green-400 bg-green-50 dark:bg-green-900/30'
                            : pbExtensionActive
                              ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 cursor-default'
                              : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 cursor-pointer'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                            {f.num}. {f.label}
                          </span>
                          {!pbExtensionActive && (
                            <span className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity text-cyan-600 font-semibold">
                              {pbTestStep === `copied-${f.num}` ? '✓' : '📋'}
                            </span>
                          )}
                        </div>
                        <p className="font-mono font-bold text-sm truncate text-gray-900 dark:text-white">
                          {f.value || <span className="text-red-400 font-normal italic text-xs">nevyplněno</span>}
                        </p>
                      </button>
                    ))}

                    {!pbExtensionActive && (
                      <button
                        type="button"
                        onClick={() => {
                          const allValues = fields.map(f => `${f.label}: ${f.value}`).join('\n');
                          navigator.clipboard.writeText(allValues).catch(() => {});
                          setPbTestStep('copied-all');
                          setTimeout(() => setPbTestStep('ready'), 2000);
                        }}
                        className={`w-full py-1.5 rounded-lg text-xs font-bold transition mt-1 ${pbTestStep === 'copied-all' ? 'bg-green-500 text-white' : 'bg-cyan-600 hover:bg-cyan-700 text-white'}`}
                      >
                        {pbTestStep === 'copied-all' ? '✓ Vše zkopírováno!' : '📋 Kopírovat vše'}
                      </button>
                    )}
                  </div>

                  {/* Log kroků (extension mode) */}
                  {pbTestLog.length > 0 && (
                    <div className="flex-1 p-3 overflow-y-auto">
                      <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Průběh</p>
                      <div className="space-y-0.5">
                        {pbTestLog.map((line, i) => (
                          <p key={i} className="text-[11px] font-mono text-gray-600 dark:text-gray-400 leading-relaxed">{line}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {isDone && (
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 border-t border-green-300 dark:border-green-700">
                      <p className="text-sm font-bold text-green-700 dark:text-green-400 text-center">✅ Hotovo!</p>
                    </div>
                  )}
                </div>

                {/* ── Pravý panel ───────────────────────────────────────── */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  {pbExtensionActive ? (
                    /* Extension mode – zobraz stav místo iframe */
                    <div className="flex-1 flex flex-col items-center justify-center gap-6 p-8 text-center">
                      {pbDecisionNeeded ? (
                        /* ── Rozhodovací brána – vlastník neodpovídá VF loginu ── */
                        <div className="max-w-md w-full">
                          <div className="text-5xl mb-3">🚧</div>
                          <p className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">
                            Přeblokace není možná po kontrole SBL
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                            Vlastník zjištěný v Siebelu: <strong className="font-mono">{pbCheckedOwner || '—'}</strong>
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                            Tento vlastník neodpovídá žádnému VF loginu ani loginu volnému k obchodování.
                            Vyberte, jak chcete pokračovat:
                          </p>
                          <div className="flex flex-col gap-3">
                            <button
                              type="button"
                              onClick={() => {
                                // Zrušit běžící automatizaci a otevřít okno žádosti o uvolnění
                                window.postMessage({ type: 'PB_DECISION', action: 'cancel' }, '*');
                                setPbDecisionNeeded(false);
                                setShowPbTestModal(false);
                                setShowUvolnitModal(true);
                              }}
                              className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm transition"
                            >
                              ✉️ Poslat žádost o uvolnění
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                // Už mám schválení – vyplnit PowerApps VČETNĚ scénáře, ale NEODESLAT
                                pbAddLog('✅ Pokračuji – vyplním formulář (bez automatického odeslání).');
                                setPbDecisionNeeded(false);
                                setPbTestStep('pa-opening');
                                window.postMessage({ type: 'PB_DECISION', action: 'fill' }, '*');
                              }}
                              className="w-full py-3 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm transition"
                            >
                              ✅ Už mám schválení s uvolněním
                            </button>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                              „Už mám schválení" vyplní celý formulář v PowerApps včetně scénáře, ale <strong>neodešle</strong> ho –
                              přiložíte soubor a odešlete ručně.
                            </p>
                          </div>
                        </div>
                      ) : isDone ? (
                        <>
                          <div className="text-6xl">✅</div>
                          <p className="text-xl font-bold text-green-600 dark:text-green-400">Hotovo!</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {(pbTestLog[pbTestLog.length - 1] || '').includes('ručně')
                              ? 'Formulář vyplněn – přiložte soubor a odešlete ručně v PowerApps.'
                              : 'Přeblokace odeslána. PowerApps záložka se automaticky zavřela.'}
                          </p>
                          {pbCloseCountdown !== null && (
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Okno se zavře za {pbCloseCountdown} s...
                            </p>
                          )}
                        </>
                      ) : isRunning ? (
                        <>
                          <div className="animate-spin rounded-full h-16 w-16 border-4 border-cyan-500 border-t-transparent" />
                          <div>
                            <p className="text-lg font-bold text-gray-800 dark:text-white">Automatizace probíhá...</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Extension otevírá záložky a vyplňuje formuláře automaticky.
                            </p>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-xl px-4 py-2 max-w-xs">
                            {pbTestLog.length > 0 ? pbTestLog[pbTestLog.length - 1] : 'Startuje...'}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-5xl">⚡</div>
                          <div>
                            <p className="text-lg font-bold text-gray-800 dark:text-white">Extension je připravena</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Klikni <strong>▶ Spustit</strong> a extension automaticky:</p>
                          </div>
                          <ol className="text-sm text-left text-gray-600 dark:text-gray-300 space-y-2 max-w-xs">
                            {['Otevře Siebel a vyhledá zákazníka podle IČO','Extrahuje CA a Vlastníka (PRM) ze Sieblu','Aktualizuje data na kartě zákazníka','Otevře PowerApps a vyplní celý formulář','Vybere scénář "A (bez platné P)" a odešle'].map((s, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-400 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                                {s}
                              </li>
                            ))}
                          </ol>
                        </>
                      )}
                    </div>
                  ) : (
                    /* Extension není nainstalována – detailní návod nahoře + ruční formulář dole */
                    <div className="flex-1 flex flex-col overflow-hidden">
                      {/* Detailní návod na instalaci / aktivaci */}
                      <div className="p-5 overflow-y-auto border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/10">
                        <details className="group">
                          <summary className="flex items-start gap-3 cursor-pointer list-none select-none">
                            <span className="text-3xl">🧩</span>
                            <div className="flex-1">
                              <p className="text-base font-bold text-amber-800 dark:text-amber-300 flex items-center gap-2">
                                Nainstalujte rozšíření pro plnou automatizaci
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 transition-transform group-open:rotate-180 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </p>
                              <p className="text-xs text-amber-700 dark:text-amber-400">Rozšíření vyplní Siebel i PowerApps automaticky na jedno kliknutí. <span className="opacity-70">(klikni pro návod)</span></p>
                            </div>
                          </summary>
                          <ol className="text-sm text-amber-900 dark:text-amber-200 space-y-2 list-decimal pl-5 mt-3">
                            <li>
                              Stáhněte si rozšíření:
                              <a
                                href="/pb-extension.zip"
                                download="pb-extension.zip"
                                className="inline-block ml-2 px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition align-middle"
                              >
                                📦 Stáhnout pb-extension.zip
                              </a>
                            </li>
                            <li>Rozbalte stažený ZIP do trvalé složky (např. <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">Dokumenty/pb-extension</code>) – složku nemažte.</li>
                            <li>V Chrome/Edge otevřete stránku <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">chrome://extensions</code>.</li>
                            <li>Vpravo nahoře zapněte <strong>Režim pro vývojáře</strong> (Developer mode).</li>
                            <li>Klikněte na <strong>Načíst rozbalené</strong> (Load unpacked) a vyberte rozbalenou složku.</li>
                            <li>Rozšíření se objeví v seznamu – ujistěte se, že je <strong>zapnuté</strong>.</li>
                            <li><strong>Obnovte tuto stránku CRM</strong> (F5). Po obnovení se nahoře zobrazí „Extension aktivní".</li>
                          </ol>
                          <div className="mt-3 text-xs text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 rounded-lg p-2">
                            💡 Po instalaci a obnovení stránky se toto okno přepne do automatického režimu – přeblokace pak proběhne na jedno kliknutí.
                          </div>
                        </details>
                      </div>

                      {/* Ruční vyplnění – formulář dole */}
                      <div className="flex-1 flex flex-col min-h-0">
                        {pbTestIframeUrl ? (
                          <iframe
                            ref={pbTestIframeRef}
                            key={pbTestIframeUrl}
                            src={pbTestIframeUrl}
                            className="flex-1 w-full border-none"
                            title="Přeblokace – ruční formulář"
                            allow="fullscreen"
                          />
                        ) : (
                          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-6">
                            <p className="text-sm text-gray-600 dark:text-gray-300 max-w-sm">
                              Nechcete instalovat rozšíření? Otevřete přeblokační formulář ručně a údaje z karty zkopírujte vlevo.
                            </p>
                            <button
                              onClick={pbStartAutomation}
                              className="px-6 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-semibold text-sm transition"
                            >
                              ▶ Otevřít formulář ručně
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal - obsazený časový slot */}
      {busySlotModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
          onClick={() => setBusySlotModal(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-sm"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                ⚠️ Obsazeno v {busySlotModal.timeSlot}
              </h3>
              <button
                onClick={() => setBusySlotModal(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
              {busySlotModal.companies.map(c => (
                <div key={c._id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-lg gap-3">
                  <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{c.name}</span>
                  <a
                    href={`#/companies/${c._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs font-medium flex-shrink-0"
                  >
                    ↗️ Otevřít
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 flex flex-col items-center shadow-2xl">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
            <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">Aktualizuji...</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Ukládám změny a načítám data</p>
          </div>
        </div>
      )}

      {/* Signál modal */}
      {showSignalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-3xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
            {/* Hlavička */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-bold text-base leading-tight">Porovnání pokrytí LTE + 5G</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Data z ČTÚ VPortal · Vodafone CZ</p>
                </div>
              </div>
              <button onClick={() => { setShowSignalModal(false); setSignalResult(null); setSignalError(null); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none p-1">✕</button>
            </div>

            {/* Adresa – editovatelná */}
            <div className="mx-5 mt-3 flex-shrink-0">
              <div className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                <span className="text-sm flex-shrink-0">📍</span>
                <input
                  type="text"
                  value={signalAddress}
                  onChange={e => setSignalAddress(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && signalAddress.trim()) { setSignalResult(null); setSignalError(null); checkSignal(signalAddress.trim()); } }}
                  placeholder="Zadejte adresu…"
                  className={`flex-1 text-xs bg-transparent outline-none min-w-0 ${isDarkMode ? 'text-gray-200 placeholder-gray-500' : 'text-gray-700 placeholder-gray-400'}`}
                />
                <button
                  onClick={() => { if (signalAddress.trim()) { setSignalResult(null); setSignalError(null); checkSignal(signalAddress.trim()); } }}
                  disabled={!signalAddress.trim() || signalLoading}
                  className="flex-shrink-0 text-xs px-2.5 py-1 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white rounded-md font-semibold transition"
                >
                  Ověřit
                </button>
              </div>
            </div>

            {/* Obsah */}
            <div className="overflow-y-auto flex-1 px-5 py-3">
              {signalLoading && (
                <div className="flex flex-col items-center py-10 gap-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Dotazuji ČTÚ VPortal…</p>
                </div>
              )}

              {signalError && !signalLoading && (
                <div className="mt-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded-xl p-4 text-sm">
                  ❌ {signalError}
                </div>
              )}

              {signalResult && !signalLoading && (() => {
                const BAR_COLORS = { green: '#16a34a', amber: '#d97706', orange: '#ea580c', red: '#dc2626' };
                const qualityBg = { green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', amber: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400', orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400', red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' };
                const SignalBars = ({ bars, color, size = 1 }) => {
                  const col = BAR_COLORS[color] || '#6b7280';
                  const w = size === 1 ? 5 : 4;
                  const hBase = size === 1 ? 3.5 : 3;
                  return (
                    <div className="flex items-end gap-[2px]" style={{ height: size === 1 ? 20 : 16 }}>
                      {[1,2,3,4,5].map(b => (
                        <div key={b} style={{ width: w, height: 4 + b * hBase, borderRadius: 1.5, background: b <= bars ? col : (isDarkMode ? '#374151' : '#e5e7eb') }} />
                      ))}
                    </div>
                  );
                };

                const CoverageCard = ({ title, data, accent, compact = false }) => {
                  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200';
                  const subBg  = isDarkMode ? 'bg-gray-900/50' : 'bg-white';
                  const emptyBars = <div className="flex items-end gap-[3px]" style={{ height: 20 }}>{[1,2,3,4,5].map(b => <div key={b} style={{ width: 5, height: 4 + b * 3.5, borderRadius: 1.5, background: isDarkMode ? '#374151' : '#e5e7eb' }} />)}</div>;
                  if (!data) return (
                    <div className={`rounded-xl border p-3 ${cardBg} h-full`}>
                      <div className={`text-sm font-bold mb-2 ${accent}`}>{title}</div>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Mimo pokrytí</span>
                      <div className="mt-2">{emptyBars}</div>
                    </div>
                  );
                  const [dlMin, dlMax, ulMin, ulMax] = data.speeds;
                  return (
                    <div className={`rounded-xl border ${cardBg} ${compact ? '' : 'mb-3'}`}>
                      {/* Header */}
                      <div className="flex items-center justify-between px-3 pt-3 pb-2">
                        <span className={`text-sm font-bold ${accent}`}>{title}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${qualityBg[data.outdoor.color]}`}>{data.outdoor.quality}</span>
                      </div>
                      {/* Outdoor / Indoor rows */}
                      <div className={`mx-2 mb-2 rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} overflow-hidden`}>
                        <div className={`flex items-center gap-2 px-3 py-2 ${subBg}`}>
                          <span className={`text-xs flex-shrink-0 w-14 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>📡 Venku</span>
                          <SignalBars bars={data.outdoor.bars} color={data.outdoor.color} />
                          <span className={`text-xs font-mono ml-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{data.dbmOut.toFixed(1)} dBm</span>
                        </div>
                        <div className={`flex items-center gap-2 px-3 py-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          <span className={`text-xs flex-shrink-0 w-14 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>🏠 Uvnitř</span>
                          {data.indoor ? (
                            <><SignalBars bars={data.indoor.bars} color={data.indoor.color} />
                            <span className={`text-xs font-mono ml-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{data.dbmIn.toFixed(1)} dBm</span></>
                          ) : (
                            <><div className="ml-1">{emptyBars}</div><span className={`text-xs ml-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>slabý</span></>
                          )}
                        </div>
                      </div>
                      {/* Speeds — always side by side */}
                      <div className={`mx-2 mb-2 rounded-lg px-3 py-2 ${isDarkMode ? 'bg-gray-900/50 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                        <div className="flex items-center gap-4 text-xs">
                          <span className={`${isDarkMode ? 'text-green-400' : 'text-green-600'} font-semibold`}>↓ {dlMin}–{dlMax} Mbps</span>
                          <span className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'} font-semibold`}>↑ {ulMin}–{ulMax} Mbps</span>
                        </div>
                      </div>
                      {/* Active bands */}
                      <div className="px-3 pb-2">
                        {data.bands.map(band => (
                          <span key={band.id} className={`inline-flex items-center mr-1 mb-0.5 text-xs font-mono px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600 border border-gray-300'}`} title={`${band.freq} · ${band.note}`}>{band.id}</span>
                        ))}
                      </div>
                    </div>
                  );
                };

                const OP_STYLE = {
                  'T-Mobile': { text: 'text-pink-600 dark:text-pink-400', bg: 'bg-pink-50 dark:bg-pink-900/20', accent: 'text-pink-600 dark:text-pink-400' },
                  'O2':       { text: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20', accent: 'text-blue-600 dark:text-blue-400' },
                };
                const curStyle = OP_STYLE[signalResult.currentOp] || { text: 'text-gray-600 dark:text-gray-300', bg: 'bg-gray-100 dark:bg-gray-800', accent: 'text-gray-600 dark:text-gray-300' };

                return (
                  <div className="mt-2">
                    {/* Column headers — only show when comparing */}
                    {!signalResult.isCurrentOpVodafone && signalResult.currentOp && (
                      <div className="flex gap-2 mb-2">
                        <div className="flex-1 text-center text-[11px] font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg py-1">🟥 Vodafone</div>
                        <div className={`flex-1 text-center text-[11px] font-bold ${curStyle.text} ${curStyle.bg} rounded-lg py-1`}>{signalResult.currentOp}</div>
                      </div>
                    )}

                    {/* LTE row */}
                    {!signalResult.isCurrentOpVodafone && signalResult.currentOp ? (
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1"><CoverageCard title="📶 LTE (4G)" data={signalResult.lte} accent="text-red-600 dark:text-red-400" compact /></div>
                        <div className="flex-1"><CoverageCard title="📶 LTE (4G)" data={signalResult.curLte} accent={curStyle.accent} compact /></div>
                      </div>
                    ) : (
                      <CoverageCard title="📶 LTE (4G)" data={signalResult.lte} accent="text-blue-600 dark:text-blue-400" />
                    )}

                    {/* 5G row */}
                    {!signalResult.isCurrentOpVodafone && signalResult.currentOp ? (
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1"><CoverageCard title="🔵 5G NR" data={signalResult.fiveG} accent="text-red-600 dark:text-red-400" compact /></div>
                        <div className="flex-1"><CoverageCard title="🔵 5G NR" data={signalResult.curFiveG} accent={curStyle.accent} compact /></div>
                      </div>
                    ) : (
                      <CoverageCard title="🔵 5G NR" data={signalResult.fiveG} accent="text-purple-600 dark:text-purple-400" />
                    )}

                    <div className={`text-xs mt-1 pt-3 border-t space-y-0.5 ${isDarkMode ? 'text-gray-500 border-gray-700' : 'text-gray-400 border-gray-200'}`}>
                      <div>📌 GPS: {signalResult.lat.toFixed(5)}, {signalResult.lon.toFixed(5)}</div>
                      <div className="truncate">📍 {signalResult.displayName}</div>
                      <div className="italic pt-1">Zdroj: ČTÚ VPortal · Indoor odhad dle 3GPP TR 36.814</div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Patička */}
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 flex gap-2">
              {signalResult && (
                <button
                  onClick={() => {
                    const snap = {
                      savedAt: new Date().toISOString(),
                      address: signalResult.address,
                      lte:      signalResult.lte    ? { dbm: signalResult.lte.dbmOut,    quality: signalResult.lte.outdoor.quality    } : null,
                      fiveG:    signalResult.fiveG  ? { dbm: signalResult.fiveG.dbmOut,  quality: signalResult.fiveG.outdoor.quality  } : null,
                      curOp:    signalResult.currentOp || null,
                      curLte:   signalResult.curLte   ? { dbm: signalResult.curLte.dbmOut,   quality: signalResult.curLte.outdoor.quality   } : null,
                      curFiveG: signalResult.curFiveG ? { dbm: signalResult.curFiveG.dbmOut, quality: signalResult.curFiveG.outdoor.quality } : null,
                    };
                    handleChange('coverageSnapshot', snap);
                    setShowSignalModal(false);
                    setSignalResult(null);
                    setSignalError(null);
                  }}
                  className="flex-1 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition"
                >
                  📌 Uložit na kartu
                </button>
              )}
              <button
                onClick={() => { setShowSignalModal(false); setSignalResult(null); setSignalError(null); }}
                className="flex-1 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-sm font-semibold transition"
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Co nabídnout? drawer */}
      {showOfferDrawer && company && (
        <WhatToOfferDrawer
          company={company}
          isDarkMode={isDarkMode}
          initialData={offerResult}
          onClose={() => { setShowOfferDrawer(false); setOfferResult(null); }}
        />
      )}

      {/* Šablony aktivit - správa (pouze admin) */}
      {showTemplatesModal && (
        <ActivityTemplatesModal
          isDarkMode={isDarkMode}
          availableStatuses={availableStatuses}
          userId={userId}
          onClose={() => { setShowTemplatesModal(false); fetch(`${API_URL}/activity-templates?userId=${userId || ''}`).then(r => r.json()).then(d => { if (Array.isArray(d)) setActivityTemplates(d); }).catch(() => {}); }}
        />
      )}

      {/* Modal: výzva k ohodnocení leadu před uložením */}
      {showLeadRatingPrompt && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className={`w-full max-w-sm rounded-2xl shadow-2xl p-6 flex flex-col gap-4 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
            {/* Hlavička */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">L</span>
              </div>
              <div>
                <h2 className="text-base font-bold">Ohodnoťte lead</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Tato firma pochází z leadu callcentra</p>
              </div>
            </div>

            {/* Popis */}
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Lead zatím není ohodnocen. Ohodnoťte prosím jeho kvalitu — pomůžete tím zlepšit práci celého týmu.
            </p>

            {/* Hvězdičky */}
            <div className="flex flex-col gap-2">
              <p className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Hodnocení (1–5 hvězdiček)</p>
              <div className="flex items-center gap-2 justify-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={`rating-prompt-star-${star}`}
                    type="button"
                    onClick={() => handleLeadRatingAndSave(star)}
                    onMouseEnter={() => setLeadRatingHover(star)}
                    onMouseLeave={() => setLeadRatingHover(0)}
                    disabled={leadRatingSaving}
                    className={`text-4xl leading-none transition-colors disabled:opacity-50 ${star <= leadRatingHover ? 'text-yellow-400' : 'text-gray-300'}`}
                    title={`Ohodnotit ${star}/5`}
                  >
                    ★
                  </button>
                ))}
              </div>
              {/* Vysvětlivky hodnocení */}
              <div className={`mt-1 rounded-lg p-3 text-[11px] leading-snug space-y-1 ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                <div><span className="text-yellow-400">★</span> = Zákazník nemá zájem, byl nepříjemný.</div>
                <div><span className="text-yellow-400">★★</span> = Zákazník řekl, že to nemá cenu, že to říkal telefonistce.</div>
                <div><span className="text-yellow-400">★★★</span> = Dovoláno, nabídka, 2 a více chybných údajů (email, adresy, neověřeno s kým mluví).</div>
                <div><span className="text-yellow-400">★★★★</span> = Dovoláno, nabídka (max 1 chyba – email, adresa, neověřeno s kým mluví).</div>
                <div><span className="text-yellow-400">★★★★★</span> = Dovoláno, nabídka, zákazník o callu ví dopředu.</div>
              </div>
            </div>

            {/* Tlačítka */}
            <div className="flex flex-col gap-2 pt-1">
              <button
                type="button"
                onClick={handleLeadRatingSkip}
                className={`w-full py-2 rounded-lg text-sm font-medium transition border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
              >
                Ohodnotím později
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pro reklamaci leadu (tmavý režim) */}
      {showReklamaceModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl p-6 flex flex-col gap-4 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
            {/* Hlavička */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">🚩</span>
              </div>
              <div>
                <h2 className="text-base font-bold">Reklamovat lead</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Reklamace bude odeslána na callcentrum</p>
              </div>
            </div>

            {/* Textové pole */}
            <div className="flex flex-col gap-1.5">
              <label className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Důvod reklamace (nepovinné)</label>
              <textarea
                value={reklamaceText}
                onChange={(e) => setReklamaceText(e.target.value)}
                rows={4}
                autoFocus
                placeholder="Popište důvod reklamace leadu…"
                className={`w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500 ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
              />
            </div>

            {/* Tlačítka */}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => { setShowReklamaceModal(false); setReklamaceText(''); }}
                disabled={reklamaceSaving}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition border disabled:opacity-50 ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
              >
                Zrušit
              </button>
              <button
                type="button"
                onClick={handleSubmitReklamace}
                disabled={reklamaceSaving}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {reklamaceSaving ? 'Odesílám…' : 'Odeslat reklamaci'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
